/**
 * THE figure parser. F-16 RULING: "single shared money/figure parsing module,
 * gateway-style choke point".
 *
 * The ruling exists because three INDEPENDENT comma bugs shipped past green
 * tests in three different files, each having re-implemented this:
 *   1. the pipeline clause splitter turned "$1,900" into "$1"
 *   2. the verifier read "is $65, takes a week" as "$65," and matched nothing
 *   3. substring grounding accepted "$14" against a KB fact of "$14,000"
 *
 * Each was found by reading output, never by a test, and each was written by
 * someone who had already fixed the other one. That is the signature of a
 * missing choke point, not of carelessness. Nothing in this repo may parse a
 * money value or a figure any other way. B13.5 is the stake: "any
 * customer-facing figure must exact-match a KB source or the verifier blocks
 * the send."
 */

import { extractDeadlines, extractPeriodMentions, type PeriodMentionOptions } from "./deadlines";

// Thousands separators only, never a trailing comma. `[\d,]+` greedily eats
// the comma in "is $65, takes a week" and yields "$65,", which matches
// nothing downstream and reads as "no figure present".
const MONEY = /\$\s?\d+(?:,\d{3})*(?:\.\d{2})?/g;

/**
 * TA-403. A spelled number is the same claim as its digits: a real-model draft
 * said "four to six weeks" and passed the verifier clean because only digits
 * were read (B13.5, zero tolerance on numbers). The grammar is closed on
 * purpose, zero to 999,999 in words, so "four six" is never read as ten.
 *
 * A spelled number counts only where a digit one would: next to a unit. "one
 * sec" and "a day or two" stay prose.
 *
 * TA-408, Kabir 2026-09-26 (typed, "fix bug/issue"): "a week", "a month" and
 * "a couple of days/weeks/months" are turnaround claims, the same as "1 week"
 * and "2 weeks". "a few weeks" and "a day or two" name no number and stay prose.
 */
const UNIT_WORD = "(?:one|two|three|four|five|six|seven|eight|nine)";
const TEEN_WORD = "(?:ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen)";
const TENS_WORD = "(?:twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)";
const SUB100 = `(?:${TENS_WORD}(?:[\\s-]${UNIT_WORD}\\b)?|${TEEN_WORD}|${UNIT_WORD})`;
const SUB1000 = `(?:${SUB100}\\s+hundred(?:\\s+(?:and\\s+)?${SUB100})?|${SUB100})`;
const SPELLED = `(?:${SUB1000}\\s+thousand(?:\\s+(?:and\\s+)?${SUB1000})?|${SUB1000}|zero)\\b`;
const NUM = `(?:\\d+(?:\\.\\d+)?|${SPELLED})`;
const SPELLED_NUMBER = new RegExp(`\\b${SPELLED}`, "gi");

const WORD_VALUE: Readonly<Record<string, number>> = {
  zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9,
  ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16,
  seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50,
  sixty: 60, seventy: 70, eighty: 80, ninety: 90,
};

/**
 * Every word `SPELLED` reads as part of a number. A filter that must hold any
 * line that could carry a figure builds on this list, never on a copy of its
 * own (F-16: the voice-line filter's copy missed "sixty" and nine more).
 */
export const NUMBER_WORDS: readonly string[] = [...Object.keys(WORD_VALUE), "hundred", "thousand"];

/** A phrase `SPELLED` matched, as a number. Deterministic, never a model (section 4). */
function spelledValue(phrase: string): number {
  let total = 0;
  let current = 0;
  for (const word of phrase.toLowerCase().split(/[\s-]+/)) {
    if (word === "and" || word === "") continue;
    if (word === "hundred") current *= 100;
    else if (word === "thousand") {
      total += current * 1000;
      current = 0;
    } else current += WORD_VALUE[word] ?? 0;
  }
  return total + current;
}

const DURATION_UNIT = "(?:day|days|week|weeks|month|months|hour|hours|min|mins|minute|minutes)";
const PERCENT_MARK = "(?:%|per\\s?cent\\b|pct\\b)";

/**
 * A stated range, matched BEFORE the bare turnaround so the more specific
 * figure comes out first.
 *
 * Both come out, and that is the point. A fact reading "3 to 5 days" grounds a
 * reply that says "5 days", because the number is inside the range the fact
 * stated. A fact reading "5 days" does NOT ground a reply that says "3 to 5
 * days": the reply invented the 3, and until this pattern existed both sides
 * reduced to "5 days" and the invention passed.
 */
const DURATION_RANGE = new RegExp(`\\b${NUM}\\s*(?:to|-)\\s*${NUM}\\s*${DURATION_UNIT}\\b`, "gi");
const DURATION = new RegExp(`\\b${NUM}\\s*${DURATION_UNIT}\\b`, "gi");
const ARTICLE_DURATION = /\b(?:a\s+couple\s+of\s+(?:days|weeks|months)|an?\s+(?:week|month))\b/gi;
const PERCENT = new RegExp(`\\b${NUM}\\s*${PERCENT_MARK}`, "gi");
const CARAT = new RegExp(`\\b${NUM}\\s*(?:ct|carat|carats)\\b`, "gi");
/** "85 dollars" and "eighty five dollars" are "$85". `extractMoney` stays the shop's own "$" form. */
const MONEY_WORDS = new RegExp(`\\b${NUM}\\s*(?:dollars?|bucks)\\b`, "gi");

/**
 * Item 125, second review: every dash a keyboard or a phone puts between two
 * words (hyphen, the Unicode hyphens and dashes, minus), escaped for a
 * character class. "half" joined to "off" by an em dash read as no discount.
 */
const DASH = "\\-\\u2010-\\u2015\\u2212";

/**
 * B2 and section 6: a discount is a commitment, which a turnaround is not.
 *
 * Item 125, second review: "50 percent off", "50 pct off", "10-percent-off"
 * and "fifty percent off" are the same claim as "50% off". The first read
 * only "%", so a draft spelling the percent out passed the concession ledger
 * whenever a cited fact grounded the figure.
 *
 * Item 125, third review: joined by any dash (`DASH`), as the fraction reader
 * is, so "50%" and "off" joined by an em dash is the claim too. "a hundred"
 * (and "hundred") is read here alone, as 100 (`readDiscountClaims`): the
 * shared spelled-number grammar takes a hundred only after a unit word, and
 * widening it would change every figure reader and `NUMBER_WORDS`.
 */
const DISCOUNT_HUNDRED = "(?:a\\s+)?hundred\\b";
/** What a price is called, after "in" or "of" ("a 20% reduction in price"). */
const PRICE_WORD = "(?:the\\s+|your\\s+|its\\s+|our\\s+)?(?:prices?|pricing|costs?|totals?|bills?|fees?|charges?|quotes?)\\b";
/**
 * Decision 3: a percent named a discount, a markdown or a saving is the claim
 * "off" is. DISC fix round 1 (review): a reduction, a rebate and a price cut
 * too, the request side's own vocabulary (`CONCESSION_OBJECT`, concessions.ts),
 * so "a 10% reduction" is no longer the way round a 0% cap. DISC fix round 2
 * (its fuzz): a saving "in" or "of" something is one only of a price ("a 20%
 * saving in emissions" is none), as a reduction is (`REDUCTION`); "a 20%
 * discount in store" stays read.
 */
const DISCOUNT_NAMED =
  `(?:discount(?:ed|s)?|markdowns?|savings?(?!\\s+(?:in|of)\\s+(?!${PRICE_WORD}))|rebates?|price[\\s${DASH}]?cuts?)`;
/**
 * Fix round 1: one word may stand between a percent and a named discount ("a
 * 10% military discount"), never a word that makes it a deposit, a rate or a
 * size ("a 20% weight reduction").
 */
const DISCOUNT_ADJECTIVE =
  "(?:(?!(?:off|of|on|in|at|the|a|an|and|or|to|for|no|not|is|was|are|deposit|down|interest|financing|apr|" +
  `weight|size|time|wait|metal|gold|carat)\\b)[a-z]+[\\s${DASH}]+)?`;
/** Fix round 1: "a 20% reduction in size" and "a 10% reduction in the deposit" are no discount. */
const NAMED_TAIL =
  "(?!\\s+(?:in|of|on)\\s+(?:the\\s+|your\\s+|its\\s+)?(?:sizes?|weight|time|wait|waiting|turnaround|deposits?|" +
  "interest|apr|financing|carats?|metal|gold|lead\\s+time)\\b)";
/**
 * DISC fix round 2 (review): a reduction is a discount only of a price. A
 * reduction is anything made smaller ("a 20% reduction in the band width", "a
 * 90% emissions reduction"), so the one word before it may only be a price
 * word, and "in" or "of" after it must name a price. Before "on" it is what a
 * discount applies to ("a 10% reduction on repairs") and stays read.
 */
const REDUCTION = `(?:(?:price|cost|pricing)[\\s${DASH}]+)?reductions?\\b(?!\\s+(?:in|of)\\s+(?!${PRICE_WORD}))`;
/**
 * Fix round 1: "20% less" and "20% cheaper" are money off at a clause end,
 * before a connective or before a price word, never before "than" ("20%
 * cheaper than mined" compares two things) or a material ("20% less gold").
 *
 * DISC fix round 2 (review): before an offer word ("if", "this", "today", a
 * price word, "for you") it is read whatever it compares ("Diamonds are 20%
 * cheaper this week"). At a clause end, or before a connective, "on", "for"
 * or "with", it is read only when nothing in its sentence before it is a
 * metal, a stone or a weight ("14k is about 20% cheaper.", "lab-grown is
 * about 70% less." and "14k weighs about 20% less." compare materials).
 */
const COMPARED =
  "(?:\\d{1,2}\\s?(?:k|kt|karat)|karats?|gold|platinum|silver|palladium|titanium|tungsten|metals?|alloys?|lab|" +
  "mined|natural|moissanite|sapphires?|rub(?:y|ies)|emeralds?|diamonds?|stones?|gems?|gemstones?|weighs?|weight|" +
  "lighter|heavier|denser|" +
  // DISC fix round 3 (review): a carat, a cut, a setting, a version or a size compared ("The oval is about 20% less.").
  "\\d+(?:\\.\\d+)?\\s?(?:ct|cts|carats?)|carats?|cts?|oval|round|cushion|pear|princess|marquise|asscher|radiant|" +
  "baguette|solitaire|halo|bezel|pave|prongs?|settings?|versions?|smaller|larger|bigger|thinner|wider|thicker|narrower)";
const LESS_TAIL =
  "(?!\\s+than\\b)(?:(?=\\s+(?:if|when|this|today|now|until|through|for\\s+(?:you|u|yourself)|prices?|priced|" +
  "pricing|costs?|rates?)\\b)|" +
  `(?<!\\b${COMPARED}\\b[^.!?;]*)(?=\\s*(?:[.,;:!?)]|$)|\\s+(?:for|on(?!\\s+average\\b)|with|and|but|so)\\b))`;
/**
 * Decision 3 of the 10-02b list: "pc" is a percent here too ("50pc off").
 * Fix round 1: only before "off" or "discount", since "saving 3pc" and "a
 * discount of 3pc sets" count pieces. `PERCENT_MARK` is shared with the
 * figure readers, where "3pc" is always a count of pieces.
 */
const DISCOUNT_CLAIM = new RegExp(
  `\\b(${DISCOUNT_HUNDRED}|${NUM})(?:[\\s${DASH}]*${PERCENT_MARK}[\\s${DASH}]*` +
    `(?:off\\b|${DISCOUNT_ADJECTIVE}${DISCOUNT_NAMED}\\b${NAMED_TAIL}|${REDUCTION}${NAMED_TAIL}|` +
    `(?:less|cheaper|lower)\\b${LESS_TAIL})|` +
    `[\\s${DASH}]*(?:pc|perc)[\\s${DASH}]*(?:off|discount(?:ed|s)?)\\b)`,
  "gi",
);

const FIGURE_PATTERNS: readonly RegExp[] = [MONEY, MONEY_WORDS, DURATION_RANGE, DURATION, ARTICLE_DURATION, PERCENT, CARAT];

/**
 * Money-LIKE, deliberately looser than `MONEY`, and it must end in a digit so
 * "is $65, takes a week" yields "$65" rather than "$65,".
 *
 * It exists to find the token that `MONEY_EXACT` then either reads or refuses.
 * Matching loosely and validating strictly is what lets this module say "I
 * cannot read that" instead of reading half of it.
 */
const MONEY_TOKEN = /\$\s?\d(?:[\d,]*\d)?(?:\.\d+)?/;
const MONEY_EXACT = /^\$(\d{1,3}(?:,\d{3})*|\d+)(?:\.(\d{2}))?$/;

/**
 * The first money value in the text, as integer cents, or null.
 *
 * Null means "no readable price", never zero: zero is a price, and a caller
 * that cannot tell the two apart writes $0.00 into a record that should say
 * nothing at all.
 *
 * A money-like token this module cannot read exactly also returns null, rather
 * than the part of it that happens to parse. "$65,5" is not $655 and it is not
 * $65; "$5.5" is probably $5.50 and might be a typo. Both refuse, per section
 * 0: precision beats recall, and when unsure, do not. The caller sees no price
 * instead of a wrong one.
 */
export function parseMoneyCents(text: string): number | null {
  const read = readMoneyCents(text);
  return read === INEXACT_AMOUNT ? null : read;
}

/**
 * TA-1378 (FINDINGS TA-1377 left open 8, B13). An amount `MONEY_EXACT` reads
 * whose cents are not a safe integer: past `Number.MAX_SAFE_INTEGER` two
 * different amounts are one number ("$999,999,999,999,999" read as
 * 99999999999999904 cents), and past about 309 digits the number is
 * Infinity, which `formatCents` refuses with a throw. Such an amount is a
 * figure nobody can read exactly, so it is no price, never a wrong one.
 */
export const INEXACT_AMOUNT = "inexact" as const;
export type MoneyRead = number | typeof INEXACT_AMOUNT | null;

/** One money token, verbatim, as cents: null when `MONEY_EXACT` refuses it, `INEXACT_AMOUNT` past a safe integer. */
function tokenCents(token: string): MoneyRead {
  const m = MONEY_EXACT.exec(token.replace(/\s+/g, ""));
  if (m === null) return null;
  const dollars = Number((m[1] ?? "").replace(/,/g, ""));
  const cents = dollars * 100 + (m[2] === undefined ? 0 : Number(m[2]));
  return Number.isSafeInteger(dollars) && Number.isSafeInteger(cents) ? cents : INEXACT_AMOUNT;
}

/**
 * `parseMoneyCents`, telling an amount past a safe integer of cents
 * (`INEXACT_AMOUNT`) from no price at all (null). The save gate reads it so
 * an unreadable amount doubts a label, never vanishes (TA-1378).
 */
export function readMoneyCents(text: string): MoneyRead {
  const token = MONEY_TOKEN.exec(text)?.[0];
  return token === undefined ? null : tokenCents(token);
}

/** Why a typed amount was not read. The caller words the refusal (cliFlags.ts's rule). */
export type TypedDollarsRead =
  | { readonly ok: true; readonly cents: number }
  | { readonly ok: false; readonly reason: "unreadable" | "negative" | "too-large" };

/**
 * A dollar amount a person typed into a field of its own, as integer cents
 * (L4: `pnpm outcomes record --amount` and the outcome action's amount).
 *
 * `parseMoneyCents` reads the FIRST price in a text and ignores the rest,
 * which is right for a message and wrong for a field: "1900 or so" is not
 * $1,900. So here the WHOLE value, trimmed, must be one amount `MONEY_EXACT`
 * reads, with the "$" optional because a person filling in a dollar field
 * rarely types it. A decimal is two digits of cents or it is unreadable, the
 * same refusal as "$5.5" above.
 *
 * A leading "-" on an otherwise readable amount is reported as negative, so
 * the caller can say so instead of "unreadable". Past `Number.isSafeInteger`
 * two different amounts read as one number of cents, so that is refused as
 * too large: a limit of exactness, not a business cap, which this module
 * does not set. Zero reads as zero; whether a zero is allowed is the caller's.
 */
export function readTypedDollars(raw: string): TypedDollarsRead {
  const text = raw.trim();
  const negative = text.startsWith("-");
  const unsigned = negative ? text.slice(1) : text;
  const m = MONEY_EXACT.exec(unsigned.startsWith("$") ? unsigned : `$${unsigned}`);
  if (m === null) return { ok: false, reason: "unreadable" };
  if (negative) return { ok: false, reason: "negative" };
  const dollars = Number((m[1] ?? "").replace(/,/g, ""));
  const cents = dollars * 100 + (m[2] === undefined ? 0 : Number(m[2]));
  if (!Number.isSafeInteger(dollars) || !Number.isSafeInteger(cents)) return { ok: false, reason: "too-large" };
  return { ok: true, cents };
}

/**
 * Every money-like token in the text that this module cannot read exactly,
 * verbatim and in the order written. Empty means every price here reads whole.
 *
 * TA-426. `extractFigures` reads "$8.5" as "$8" and "$1,90" as "$1", which is
 * right for a reply (nothing grounds against half a price) and wrong for an
 * importer, which would store the half as the fact's figure. An importer that
 * must refuse such a file needs these tokens, and it cannot find them itself:
 * F-16 bans a second money pattern outside this module.
 */
export function unreadableMoney(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(new RegExp(MONEY_TOKEN.source, "g"))) {
    // TA-1378: an amount past a safe integer of cents is no exact read either.
    if (tokenCents(m[0]) === null || tokenCents(m[0]) === INEXACT_AMOUNT) out.push(m[0]);
  }
  return out;
}

/**
 * What follows a money token and makes the token not the whole amount: a scale
 * ("$8k", "$1.2m", "$3 thousand", "$3 grand") or the far end of a range that
 * carries no "$" of its own ("$3-4k", "$65 to 70"). "$20 a month" and "$65 me"
 * do not: a scale word must end at a word boundary.
 */
const MONEY_SCALE = "(?:k|m|mm|bn|thousand|million|billion|grand)\\b";
const MONEY_FAR_END = `\\d(?:[\\d,]*\\d)?(?:\\.\\d+)?(?:\\s?${MONEY_SCALE})?`;
const MONEY_CONTINUES = new RegExp(`^(?:\\s?${MONEY_SCALE}|\\s*[-\u2013]\\s*${MONEY_FAR_END}|\\s+to\\s+${MONEY_FAR_END})`, "i");

/** What `statedAmounts` read. Both lists are verbatim and in the order written. */
export interface StatedAmounts {
  /** Money read whole, as the shop writes it (`extractMoney`'s form). */
  readonly amounts: readonly string[];
  /** A money-like token, with whatever made it not whole, that this module will not read. */
  readonly unreadable: readonly string[];
}

/**
 * The money a text states, each amount read WHOLE or refused (L11 review).
 *
 * `extractMoney` reads the longest "$" plus digits it finds, which is right
 * for matching a reply against a fact and wrong for telling a person what a
 * customer said: it reads "$8k" as "$8" and "$10k-$12k" as "$10" and "$12",
 * bug 1's class (the header above). A caller that prints an amount to a human,
 * who may repeat it, asks here instead. An amount lands in `amounts` only when
 * `MONEY_EXACT` reads the token AND nothing after it scales it or continues it
 * as a range. Anything else lands in `unreadable`, so the caller can say "an
 * amount I cannot read" rather than print half of one (section 0, precision
 * beats recall). Dollars only, like `extractMoney`: "8k" with no "$" is no
 * amount here.
 */
export function statedAmounts(text: string): StatedAmounts {
  const amounts: string[] = [];
  const unreadable: string[] = [];
  for (const m of text.matchAll(new RegExp(MONEY_TOKEN.source, "g"))) {
    const token = m[0];
    const continues = MONEY_CONTINUES.exec(text.slice((m.index ?? 0) + token.length))?.[0];
    // TA-1378: whole means a safe integer of cents too (`tokenCents`), so a caller that reads each whole
    // amount with `parseMoneyCents` (vipPricing's `wholeCents`, which throws on a null) never meets one past it.
    if (continues === undefined && typeof tokenCents(token) === "number") amounts.push(token);
    else unreadable.push(`${token}${continues ?? ""}`);
  }
  return { amounts, unreadable };
}

/**
 * The first money value, verbatim, or null. `parseMoneyCents` reads it as a
 * number; this reads it as the shop wrote it, which is what a reply quotes.
 */
export function firstMoney(text: string): string | null {
  return extractMoney(text)[0] ?? null;
}

/** Every money value in the text, verbatim and in the order written. */
export function extractMoney(text: string): string[] {
  return (text.match(MONEY) ?? []).map((m) => m.trim());
}

/** The first turnaround, range preferred, because "3 to 5 days" is one claim. */
export function firstDuration(text: string): string | null {
  return text.match(DURATION_RANGE)?.[0] ?? text.match(DURATION)?.[0] ?? null;
}

/**
 * Does this text state a figure of any kind?
 *
 * The abstain paths care about this: A3.6 says a reply that cannot answer
 * states no figure at all. "At all" includes percentages and carat weights,
 * which is why this reads the same pattern list as everything else rather than
 * a shorter one written at the call site.
 */
export function hasFigure(text: string): boolean {
  return extractFigures(text).length > 0;
}

/**
 * Prices and discounts only, which is narrower than `extractFigures` on
 * purpose.
 *
 * CLAUDE.md section 6: anything touching price, discount or policy that traces
 * only to contact memory is blocked by the verifier. A turnaround a customer
 * remembers being told is not one of those, so it is not swept in here.
 */
export function extractPriceAndDiscountClaims(text: string, opts: DiscountReadOptions): string[] {
  return [...extractDiscountClaims(text, opts), ...(text.match(MONEY) ?? []).map((m) => m.trim())];
}

/**
 * Discounts alone.
 *
 * A price in a reply is ordinary; a discount is a commitment the shop has to
 * honour, and B2 blocks one that traces only to contact memory. The two need
 * separate readers, and both read the same pattern.
 */
export function extractDiscountClaims(text: string, opts: DiscountReadOptions): string[] {
  return readDiscountClaims(text, opts).map((c) => c.text);
}

/**
 * DISC fix round 5, the structural rule: a customer writing about their budget
 * is never a shop offering a discount. Who wrote the text a discount reader
 * reads, named by every caller and never defaulted.
 *
 * - `shop`: a drafted reply (`verifyDraft`), a campaign or fixed line, an
 *   owner note, the shop's own earlier turns. Read by every reader, those
 *   decision 3 and DISC rounds 1 to 4 added included: at the 0% cap a
 *   discount the shop states is held, the hold direction.
 * - `customer`: an inbound line (the concession ask, the retrieval gate), the
 *   memory screen, contact memory. Read exactly as base e5faa94 read it, by
 *   the readers older than DISC: "Two for $3,000 on the bands is our limit"
 *   is a budget, and reading it as a deal screened a booking ask (verify4).
 *   Pinned line for line by `customerReadBase.test.ts` against
 *   `customerReadBase.fixture.json`, generated at e5faa94.
 */
export type DiscountAuthor = "shop" | "customer";
export interface DiscountReadOptions {
  readonly author: DiscountAuthor;
}

/**
 * Every discount the text states, with its percent (null for money off), in
 * the order written, as the author's side reads it (`DiscountReadOptions`).
 */
export function readDiscountClaims(text: string, opts: DiscountReadOptions): DiscountClaim[] {
  return opts.author === "shop" ? readShopDiscountClaims(text) : readCustomerDiscountClaims(text);
}

/**
 * DISC fix round 5: the customer side, base e5faa94's reader plus Z2's (CUSTOMER_PERCENT_UNDER_LIST),
 * over base's patterns (`BASE_DISCOUNT_CLAIM` and the rest, beside the shop
 * patterns below). A fraction off, a digit fraction, "two for one", BOGO,
 * "buy N get M" and "N for the price of M", each read as it was before DISC, and "N% under list".
 */
function readCustomerDiscountClaims(text: string): DiscountClaim[] {
  const found: { at: number; claim: DiscountClaim }[] = [];
  const push = (m: RegExpMatchArray, percent: number): void => {
    found.push({ at: m.index ?? 0, claim: { text: m[0].trim(), percent } });
  };
  /** n of every d paid back, rounded UP to the hundredth: a total that under-counts lets more through. */
  const share = (n: number, d: number): number => Math.ceil((10000 * n) / d) / 100;
  /** "<fraction> off" is the fraction; "<fraction> price" and "<fraction> the cost" are what is left to pay. */
  const off = (kind: string | undefined, n: number, d: number): number => (/(?:price|cost)$/i.test(kind ?? "") ? d - n : n);
  /** A deal's count: digits, a unit word, or "another" (one more). */
  const count = (w: string | undefined): number =>
    /^another$/i.test(w ?? "") ? 1 : (WORD_VALUE[(w ?? "").toLowerCase()] ?? Number(w));

  for (const m of [...text.matchAll(BASE_DISCOUNT_CLAIM), ...text.matchAll(CUSTOMER_PERCENT_UNDER_LIST)]) {
    const n = m[1] ?? "";
    push(m, /^\d/.test(n) ? Number.parseFloat(n) : /^(?:a\s+)?hundred$/i.test(n) ? 100 : spelledValue(n));
  }
  for (const m of text.matchAll(BASE_DISCOUNT_FRACTION)) {
    const key = (m[1] ?? "").toLowerCase().replace(DISCOUNT_JOIN_ALL, "");
    const [n, d] = (FRACTION_VALUE[key] ?? "").split("/").map(Number);
    // Unreachable while every FRACTION_WORD has a value. Loud, never a skip:
    // a discount read with no size is one the cap cannot count.
    if (n === undefined || d === undefined || !Number.isInteger(n) || !Number.isInteger(d) || d === 0) {
      throw new Error(`readDiscountClaims: no value for the fraction "${m[1] ?? ""}"`);
    }
    push(m, share(off(m[2], n, d), d));
  }
  for (const m of text.matchAll(BASE_DISCOUNT_SLASH)) {
    const n = Number(m[1]);
    const d = Number(m[2]);
    // "3/2 off" is no fraction of a price; the verifier still reads its figure.
    if (n < d) push(m, share(off(m[3], n, d), d));
  }
  // A deal: one of every two paid for is half off.
  for (const m of text.matchAll(TWO_FOR_ONE)) push(m, 50);
  for (const m of text.matchAll(BOGO)) push(m, 50);
  for (const m of text.matchAll(BASE_BUY_GET)) {
    const paid = count(m[1]);
    const free = count(m[2]);
    // The free reading, the largest: "buy one get one half off" is counted twice, never under.
    push(m, share(free, paid + free));
  }
  for (const m of text.matchAll(FOR_PRICE_OF)) {
    const taken = count(m[1]);
    const paid = count(m[2]);
    // "one for the price of two" is no discount.
    if (taken > paid) push(m, share(taken - paid, taken));
  }
  return found.sort((a, b) => a.at - b.at).map((f) => f.claim);
}

/**
 * A discount as written, and the percent off it states. Null for money off
 * ("$50 off"): its size as a percent depends on a price this reader does not
 * have, and a guess would give the cap a number nobody stated (decision 3).
 */
export interface DiscountClaim {
  readonly text: string;
  readonly percent: number | null;
}

/**
 * Every discount a text states, in the order written, with its size: "20%
 * off", and a fraction off ("half off", "a third off", "quarter price",
 * "half-price").
 *
 * Decision list item 125, rec accepted 2026-10-02 (Kabir typed "Yes I want to
 * continue the production" in reply to the accept-the-recs question): "Wire
 * FRACTION_CLAIM in as a concession so 'half off' is caught." Before this the
 * fraction was a verifier-only figure, so the concession ledger, the discount
 * question gate, the memory screen and the owner-note tier all read "half off"
 * as no discount at all.
 *
 * The size is arithmetic, so it is done here (F-16, section 4), never by a
 * caller. "<fraction> off" is that fraction; "<fraction> price" is what is left
 * to pay, so "quarter price" is 75% off. A third is rounded UP to the
 * hundredth (33.34, 66.67): a running total that under-counts is a cap that
 * lets more through than it says.
 *
 * Item 125, second review (independent Opus re-check): a deal and the other
 * spellings of a percent are discounts too, or a draft stating one cleared the
 * cap whenever a cited fact grounded its figure. "two for one", "2-for-1",
 * "buy one get one" and "BOGO" are 50% ("buy two get one" is a third);
 * "half the price" and "half" joined to "off" by any dash are the fraction;
 * "1/2 off" and "1/4 price" are the fraction in digits; "50 percent off",
 * "50 pct off" and "fifty percent off" are 50%. "half down" stays a deposit.
 * KNOWN OVER-BLOCKS, pinned in figures.test.ts and listed for Kabir: "half the
 * price is due up front" and "hold 2 for 1 week" read as 50% off (the hold
 * direction). Third review: so does a comparison, "lab-grown diamonds are
 * about half the price of mined ones" (and "a third of the price of" is
 * 66.67), and "two for one day only". No exception is written: one for "of
 * X" also passes "half the price of the original", a real discount. A clock
 * time ("2 for 1:30") is the one exclusion (`TWO_FOR_ONE`).
 *
 * Third review, residual spellings: a percent joined to "off" by any dash,
 * "1/2off" and "1 / 2 off", "2for1", "B.O.G.O.", "<n> for the price of <m>"
 * (the share not paid), "buy one, get another" (50%), "<fraction> the cost"
 * (sized as "the price") and "a hundred percent off" (100%) are read too.
 * KNOWN OVER-BLOCKS this adds, pinned in figures.test.ts: "half the cost is
 * due up front", "insurance covers half the cost", "labor is about a third of
 * the cost", "buy one, get another opinion" and "a hundred percent off the
 * mark". Fourth review: "cost" is read only after "the" ("half the cost", "a
 * third of the cost"), never bare, so "a third cost" and "three quarters cost
 * me $5" read nothing.
 *
 * Decision 3 of the 10-02b list (Kabir typed "the first messge shuld not be a
 * discount ,were not running a thrift shop"): the automated cap is 0%, so the
 * rest of the ways a reply states money off are read, the hold direction. A
 * percent named a discount, markdown or saving ("50% discount", "a discount
 * of 20%"); "save" or "marked down by" with a figure after it, never bare
 * ("save the date"); money off ("$50 off", "50 dollars off", "a $50
 * discount", "save $50"), whose percent is null; a fifth and a tenth ("a
 * fifth off", never "a fifth of whiskey"); "priced" and the price named
 * usual, regular and the like ("half-priced", "half its regular price"); the
 * half sign and the fraction slash, folded to the digits (the text returned
 * is the folded reading); "B1G1" and "B2G1"; "2-4-1"; "the second ring free"
 * (one of two, never "the second ring size"); and a noun inside "buy one
 * ring, get one". No deposit, financing or payment figure is a discount:
 * "50% deposit", "half down", "0% financing" and "pay half now" read nothing.
 * Fix round 3: "the fifth price on the list" is an ordinal and reads nothing.
 *
 * DISC fix round 1 (two independent reviews): at a 0% cap a reading that is
 * no discount strands the draft, and on the customer side screens a booking
 * ask, so each new reader needs its discount context. A date, a slot or a
 * visit is no ordinal deal ("the 2nd is free at 2pm", "the second appointment
 * is free"); a size, a carat, an hour or a period after a saving makes it
 * none ("reduced by half a size", "save half an hour", "save $10 a month"); a
 * payment, a deposit, what is owed or a wait reduced is none ("the balance is
 * reduced by $65 once your deposit clears"); "2-4-1" in an address or a
 * phone number is none; "off" before a dash is none ("$50 off-white"); "pc"
 * is a percent only before "off" or "discount". Read besides: money spelled
 * with no digit ("a hundred dollars off", "a grand off", "$2k off"); a
 * reduction, a rebate and a price cut, with one word before the noun ("a 10%
 * military discount"); a percent "less", "cheaper" or "lower", never before
 * "than"; "reduced by" anything else; "get a second free" after a "buy"; a
 * zero-width character dropped and the Arabic percent sign folded. Two
 * readers over one statement ("save 20% off") are one discount. KNOWN
 * OVER-BLOCKS this keeps, pinned: "it'll save you $200 versus replacing the
 * band" and "you'd save $65 on sizing by buying the band here".
 *
 * DISC fix round 2 (an independent recheck of fix round 1): what is reduced
 * must be a price or a piece, so a craft or consult question reads nothing
 * ("Is the risk of losing the stone reduced by half?"); "reduced by" is read
 * after a price word, a piece word, "everything", a pronoun with its verb,
 * or at a sentence start (`REDUCED_BY`), and "reduce it by" after "we" or
 * "I". A reduction is read before "in" or "of" only when a price follows ("a
 * 20% reduction in price", never "a 20% reduction in the band width"). "free
 * of" a flaw and "free to" do something are no ordinal deal; "free of charge"
 * and "free to you" still are, as is "free until Sunday", and "the second
 * free" after a "buy". A service free ("the second sizing is free") is no
 * share off: concessions.ts counts it as a freebie (`readServiceFreebies`).
 * "less" and "cheaper" at a clause end after a metal, a stone or a weight
 * compare ("14k is about 20% cheaper."), unless an offer word follows ("20%
 * cheaper this week"). A deposit credited is no money off
 * (`depositCredited`). "2-4-1" before a capitalized word is a deal unless an
 * address word follows ("2-4-1 This Weekend" reads, "2-4-1 Ginza, Chuo-ku"
 * does not). One size said twice back to back ("half-priced, 50% off") is
 * one statement. KNOWN OVER-BLOCKS this adds, pinned: "2-4-1 Ginza" and
 * "it's $500 off what you owe".
 *
 * DISC fix round 3 (two independent reviews of fix round 2). An ordinal free
 * is read only of a named piece ("the second ring is free"), of "one" after a
 * "buy" or as a bare statement, never of a person, a room, a day or a quality
 * ("the second stylist is free", "the second stone is free from
 * inclusions"), and never asked ("Is the second one free?"): the piece list
 * is closed (`PIECE_NOUN`), so an unlisted piece reads nothing. A deal with
 * an article is read ("Buy a band and get one free", "Get one free when you
 * buy a band"), as are "3 for 2" (the share not paid) and "Two for $100"
 * (unsized). A reduction in a dimension, or after a craft step with no price
 * in its clause ("reduced by a third in weight after the recut"), is none.
 * A comparison of a carat, a shape, a setting or a version at a clause end
 * is none ("The oval is about 20% less."); an offer of a piece for a percent
 * less is read ("I can do the diamond for 20% less"). "2-4-1" after an order,
 * an item, a SKU or a style label is none, as before "cho" and "machi". A
 * fifth or a tenth after a determiner is an ordinal. Read besides: the shop
 * lowering, dropping, knocking, bringing or coming down a price by a figure,
 * "knock off" and "take off" a figure, money in USD, euros and quid, "20$",
 * "a hundred off", "100 off the ring" (ten or more) and "do 10 off" at a
 * clause end, "reduced 20%" and "20% reduced", "perc" and "half its usual
 * cost". A deal followed by its size ("BOGO 50% off") is one claim at the
 * size, never two summed. KNOWN OVER-BLOCKS this keeps, pinned: "a tenth of
 * the price of" a comparison, "my band was reduced by a third" and "it is
 * reduced by half" with no craft step, "order 2-4-1" and "the total is
 * reduced by $500 since I removed the band" (round 4 clears the middle three).
 *
 * DISC fix round 4 (verify 3 of round 3, with the corpus in
 * `discountCorpus.fixture.ts`). A reader DISC added reads only on a positive
 * deal context, never by a list of innocent contexts: a named piece or "one"
 * as the thing discounted or free, a sale, offer or deal word in the sentence
 * (`SALE_CONTEXT`), or a price word as the thing reduced. So "3 for 2", "N for
 * $X" and "2-4-1" need a sale word, the piece the deal is on ("on all
 * bands"), or for "2-4-1" a piece beside it or, stated as a deal, when or
 * where it runs ("is it 2-4-1 this week?", "2-4-1 at our Main St store");
 * "N for $X" also reads after "just" or "only" and as a headline on a piece,
 * never with a budget word in its sentence nor as N times a stated unit price.
 * The shop offers a piece for less only when the piece is named before "for"
 * ("I can do the diamond for 20% less", never "I can do lab-grown for about
 * 70% less"); a price is lowered, dropped or cut only by the shop, a "can you"
 * or at a sentence start (never "Going with 14k lowers the price by 20%"); a
 * figure is off a piece only at a sentence start or after an offer verb
 * (never "30 off the shelf rings", "cut 12 off the chain"); the ordinal free
 * after a "buy" names a piece, says "get", or follows "buy one" (never "If you
 * buy this week, the 2nd is free for a fitting"); and a pronoun's reduction
 * reads only when it is money or its sentence names a sale or a period (never
 * "my band was reduced by a third", "it is reduced by half"). Read besides:
 * a gem by name and jewelry as a piece, "purchase" as "buy" and "receive" as
 * "get". KNOWN MISSES this adds, pinned in the corpus: "three for two" bare,
 * "It is 20% reduced", "Order 2-4-1 today.", "It's reduced by 20% now." and
 * "N for $X" beside a budget word, each behind the human approval gate.
 *
 * DISC fix round 5: everything above reads shop-authored text only
 * (`readDiscountClaims` with `author: "shop"`). A customer's line is read by
 * base e5faa94's reader (`readCustomerDiscountClaims`).
 */
function readShopDiscountClaims(written: string): DiscountClaim[] {
  // Decision 3: "½" and "1⁄2" are the digits. Folded only when the text has a
  // character outside ASCII, so every ASCII reading is unchanged. Fix round 1:
  // a zero-width character is dropped and the Arabic percent sign is "%",
  // which NFKC folds neither of. TA-1378: per-cent folds to per cent (`foldPerCent`).
  const text = foldPerCent(/[^\x00-\x7f]/.test(written)
    ? written
        .normalize("NFKC")
        .replace(/\u2044/g, "/")
        .replace(/[\u200b-\u200d\u2060\ufeff]/g, "")
        .replace(/\u066a/g, "%")
    : written);
  const found: { at: number; end: number; claim: DiscountClaim; deal: boolean }[] = [];
  const push = (m: RegExpMatchArray, percent: number | null, deal = false): void => {
    const at = m.index ?? 0;
    found.push({ at, end: at + m[0].length, claim: { text: m[0].trim(), percent }, deal });
  };
  /** n of every d paid back, rounded UP to the hundredth: a total that under-counts lets more through. */
  const share = (n: number, d: number): number => Math.ceil((10000 * n) / d) / 100;
  /** "<fraction> off" is the fraction; "<fraction> price", "priced" and "the cost" are what is left to pay. */
  const off = (kind: string | undefined, n: number, d: number): number =>
    /(?:price|priced|cost)$/i.test(kind ?? "") ? d - n : n;
  /** A deal's count: digits, a unit word, or "another", "a second", "a", "an" and "any" (one). */
  const count = (w: string | undefined): number =>
    /^(?:another|a\s+second|an?|any)$/i.test(w ?? "") ? 1 : (WORD_VALUE[(w ?? "").toLowerCase()] ?? Number(w));
  /** A typed percent: digits, "a hundred", or a spelled number. */
  const percentOf = (n: string): number =>
    /^\d/.test(n) ? Number.parseFloat(n) : /^(?:a\s+)?hundred$/i.test(n) ? 100 : spelledValue(n);
  /** A fraction word's share off, from `FRACTION_VALUE`. Loud, never a skip: a discount with no size is one the cap cannot count. */
  const fraction = (word: string, kind: string | undefined): number => {
    const key = word.toLowerCase().replace(DISCOUNT_JOIN_ALL, "");
    const [n, d] = (FRACTION_VALUE[key] ?? "").split("/").map(Number);
    if (n === undefined || d === undefined || !Number.isInteger(n) || !Number.isInteger(d) || d === 0) {
      throw new Error(`readDiscountClaims: no value for the fraction "${word}"`);
    }
    return share(off(kind, n, d), d);
  };
  /** A sized saving after "save" or "marked down by": a percent, a fraction, or money (null). */
  const saving = (m: RegExpMatchArray): number | null =>
    m[1] !== undefined ? percentOf(m[1]) : m[2] !== undefined ? fraction(m[2], "off") : null;
  /**
   * Fix round 3 (review): a percent or a fraction reduced by a recut, a sizing
   * or wear is the piece made smaller, unless a price is named in the clause.
   */
  const craftReduction = (m: RegExpMatchArray): boolean => {
    if (m[1] === undefined && m[2] === undefined) return false;
    const at = m.index ?? 0;
    if (!CRAFT_EVENT.test(text.slice(at + m[0].length))) return false;
    const before = text.slice(0, at);
    const stops = [...before.matchAll(/[.!?;](?=\s|$)/g)];
    const clause = stops.length > 0 ? before.slice((stops[stops.length - 1]!.index ?? 0) + 1) : before;
    return !PRICE_IN_CLAUSE.test(clause);
  };
  /** Fix round 3: a bare figure off a piece is money off only from ten up ("a size 7 off the ring" is none). */
  const atLeastTen = (n: string): boolean =>
    (/^\d/.test(n) ? Number.parseFloat(n) : /^a\s+(?:hundred|thousand)$/i.test(n) ? 100 : spelledValue(n)) >= 10;
  /** DISC fix round 4: the sentence a match stands in, where a reader added by this item finds its deal context. */
  const SENTENCE_STOP = /[.!?;](?=\s|$)|\n/g;
  const sentenceStart = (at: number): number => {
    const stops = [...text.slice(0, at).matchAll(SENTENCE_STOP)];
    return stops.length > 0 ? (stops[stops.length - 1]!.index ?? 0) + 1 : 0;
  };
  const sentenceOf = (m: RegExpMatchArray): string => {
    const at = m.index ?? 0;
    const end = at + m[0].length;
    const next = text.slice(end).search(/[.!?;](?=\s|$)|\n/);
    return text.slice(sentenceStart(at), next < 0 ? text.length : end + next);
  };
  const after = (m: RegExpMatchArray): string => text.slice((m.index ?? 0) + m[0].length);
  /** A sale, a deal or an offer named in the sentence, or the piece the deal is on after it ("on all bands"). */
  const dealContext = (m: RegExpMatchArray): boolean => SALE_CONTEXT.test(sentenceOf(m)) || DEAL_ON_PIECE.test(after(m));
  /** "N for $X" where a unit price the text states, times N, is $X: arithmetic, no deal. */
  const unitPriceTimes = (m: RegExpMatchArray, n: number): boolean => {
    const total = centsOf(m[0].slice(m[0].search(/for/i) + 3));
    if (total === null) return false;
    for (const u of text.matchAll(UNIT_PRICE)) {
      const unit = centsOf(`${u[1] ?? ""}${u[2] ?? ""}`);
      if (unit !== null && unit * n === total) return true;
    }
    return false;
  };

  for (const m of text.matchAll(DISCOUNT_CLAIM)) push(m, percentOf(m[1] ?? ""));
  for (const m of text.matchAll(OFFERED_LESS)) push(m, percentOf(m[1] ?? ""));
  for (const m of text.matchAll(NOUN_OF_SAVING)) push(m, saving(m));
  for (const m of text.matchAll(SAVE)) push(m, saving(m));
  for (const m of text.matchAll(MARKED_DOWN_BY)) push(m, saving(m));
  for (const m of text.matchAll(KNOCK_OFF)) push(m, saving(m));
  for (const m of text.matchAll(TAKE_OFF_SAVING)) push(m, saving(m));
  for (const m of text.matchAll(REDUCED_BY)) {
    if (!craftReduction(m)) push(m, saving(m));
  }
  // DISC fix round 4: a pronoun's reduction reads when it is money, or its sentence names a sale or a time.
  const pronounReduction = (m: RegExpMatchArray, money: boolean): boolean =>
    money || SALE_CONTEXT.test(sentenceOf(m)) || REDUCED_TIME_CONTEXT.test(sentenceOf(m));
  for (const m of text.matchAll(REDUCED_BY_PRONOUN)) {
    if (!craftReduction(m) && pronounReduction(m, m[1] === undefined && m[2] === undefined)) push(m, saving(m));
  }
  for (const m of text.matchAll(SHOP_REDUCES_BY)) {
    if (!craftReduction(m)) push(m, saving(m));
  }
  for (const m of text.matchAll(REDUCED_PCT)) push(m, percentOf(m[1] ?? ""));
  for (const m of text.matchAll(REDUCED_PCT_PRONOUN)) {
    if (pronounReduction(m, false)) push(m, percentOf(m[1] ?? ""));
  }
  for (const m of text.matchAll(MONEY_OFF)) {
    if (!depositCredited(text, m)) push(m, null);
  }
  for (const m of text.matchAll(BARE_OFF)) {
    if (m[2] !== undefined && !atLeastTen(m[2])) continue;
    if (!depositCredited(text, m)) push(m, null);
  }
  for (const m of text.matchAll(DISCOUNT_FRACTION)) push(m, fraction(m[1] ?? "", m[2]));
  for (const m of text.matchAll(DISCOUNT_SLASH)) {
    const n = Number(m[1]);
    const d = Number(m[2]);
    // "3/2 off" is no fraction of a price; the verifier still reads its figure.
    if (n < d) push(m, share(off(m[3], n, d), d));
  }
  // A deal: one of every two paid for is half off.
  for (const m of text.matchAll(TWO_FOR_ONE)) push(m, 50, true);
  for (const m of text.matchAll(BOGO)) push(m, 50, true);
  for (const m of text.matchAll(TWO_FOUR_ONE)) {
    // DISC fix round 4: on a deal context, a piece named right before or after ("Bands are 2-4-1", "2-4-1 Rings"), or
    // when or where it runs ("2-4-1 this week", "2-4-1 at our Main St store").
    const before = text.slice(0, m.index ?? 0);
    const run = DEAL_STATED_BEFORE.test(before) && DEAL_RUN_AFTER.test(after(m));
    if (dealContext(m) || PIECE_BEFORE_DEAL.test(before) || PIECE_AFTER_DEAL.test(after(m)) || run) {
      push(m, 50, true);
    }
  }
  for (const m of text.matchAll(B_G)) push(m, share(count(m[2]), count(m[1]) + count(m[2])), true);
  for (const m of text.matchAll(ORDINAL_FREE)) {
    // "the second ring free": one of every k free.
    const which = m.slice(1).find((g) => g !== undefined) ?? "";
    const k = ORDINAL_WORD_VALUE[which.toLowerCase()] ?? Number.parseInt(which, 10);
    push(m, share(1, k), true);
  }
  for (const m of text.matchAll(BUY_GET)) {
    const paid = count(m[1]);
    const free = count(m[2]);
    // The free reading, the largest. Fix round 3: a size right after it is the same deal (below).
    push(m, share(free, paid + free), true);
  }
  for (const m of text.matchAll(BUY_ANY_GET)) push(m, 50, true);
  for (const m of text.matchAll(GET_FREE_WHEN_BUY)) {
    const free = count(m[1]);
    const paid = count(m[2]);
    push(m, share(free, paid + free), true);
  }
  for (const m of text.matchAll(FOR_PRICE_OF)) {
    const taken = count(m[1]);
    const paid = count(m[2]);
    // "one for the price of two" is no discount.
    if (taken > paid) push(m, share(taken - paid, taken), true);
  }
  for (const m of text.matchAll(N_FOR_M)) {
    const taken = count(m[1]);
    const paid = count(m[2]);
    // "3 for 2" is a third off; "2 for 3" is no deal. DISC fix round 4: on a deal context only.
    if (taken > paid && dealContext(m)) push(m, share(taken - paid, taken), true);
  }
  for (const m of text.matchAll(N_FOR_MONEY)) {
    // DISC fix round 4: after "just" or "only", as a headline on a piece, or in a sale; never a budget, never arithmetic.
    const head = text.slice(sentenceStart(m.index ?? 0), m.index ?? 0);
    const headline = /^\s*$/.test(head) && DEAL_ON_PIECE.test(after(m));
    const deal = /\b(?:just|only)\s/i.test(m[0]) || headline || SALE_CONTEXT.test(sentenceOf(m));
    if (deal && !BUDGET_WORD.test(sentenceOf(m)) && !unitPriceTimes(m, count(m[1]))) push(m, null, true);
  }
  // Fix round 1: two readers over one statement ("save 20% off") are one
  // discount, or the breach detail states a size nobody offered. Overlapping
  // spans merge into the heavier reading: unsized over sized (no cap holds
  // it), then the larger percent, then the longer text. Two statements that
  // do not overlap ("half-priced, 50% off") stay two: counted twice, never under.
  const weight = (c: DiscountClaim): number => c.percent ?? Number.POSITIVE_INFINITY;
  const merged: typeof found = [];
  for (const f of found.sort((a, b) => a.at - b.at || b.end - a.end)) {
    const last = merged[merged.length - 1];
    if (last === undefined || f.at >= last.end) {
      merged.push(f);
      continue;
    }
    const heavier =
      weight(f.claim) > weight(last.claim) ||
      (weight(f.claim) === weight(last.claim) && f.claim.text.length > last.claim.text.length);
    merged[merged.length - 1] = {
      at: last.at,
      end: Math.max(last.end, f.end),
      claim: heavier ? f.claim : last.claim,
      deal: last.deal || f.deal,
    };
  }
  // Fix round 2 (review): one size said twice back to back, by a comma or
  // "or" alone ("half-priced, 50% off", "50% off or half price"), is one
  // statement, or the breach detail states 100%. Only an equal size merges,
  // and only with no other word between: "20% off rings, 20% off bands" and
  // "50% off, and 50% off engraving" stay two. A space alone is no separator:
  // "buy one get one half off" stays counted twice, never under.
  //
  // Fix round 3 (review): a deal and the size of its free share side by side
  // ("buy one get one half off", "BOGO 50% off") are one deal, or the detail
  // sums them to a 100% nobody wrote. Only a space, a dash, "at" or "for"
  // between; the heavier reading is kept, as an overlap is.
  const joined: typeof merged = [];
  for (const f of merged) {
    const last = joined[joined.length - 1];
    const gap = last === undefined ? "" : text.slice(last.end, f.at);
    if (
      last !== undefined &&
      last.claim.percent !== null &&
      last.claim.percent === f.claim.percent &&
      gap.trim() !== "" &&
      APPOSITION_GAP.test(gap)
    ) {
      joined[joined.length - 1] = {
        at: last.at,
        end: f.end,
        claim: { text: text.slice(last.at, f.end).trim(), percent: last.claim.percent },
        deal: last.deal || f.deal,
      };
      continue;
    }
    if (last !== undefined && last.deal && !f.deal && DEAL_SIZE_GAP.test(gap)) {
      const heavier = weight(f.claim) > weight(last.claim);
      joined[joined.length - 1] = {
        at: last.at,
        end: f.end,
        claim: { text: text.slice(last.at, f.end).trim(), percent: (heavier ? f.claim : last.claim).percent },
        deal: false,
      };
      continue;
    }
    joined.push(f);
  }
  return joined.map((f) => f.claim);
}

/** Fix round 3: what may stand between a deal and the size of its free share ("buy one get one half off"). */
const DEAL_SIZE_GAP = new RegExp(`^[\\s${DASH}]*(?:(?:at|for)\\s+)?$`, "i");

/** Fix round 2: what may stand between one size said twice ("half-priced, 50% off", "50% off or half price"). */
const APPOSITION_GAP = new RegExp(
  `^[\\s,;:()/${DASH}]*(?:(?:or|i\\.?e\\.?|that['’]?s|that\\s+is|aka)[\\s,;:()/${DASH}]*)?$`,
  "i",
);

/**
 * Fix round 2 (review): a deposit or a payment already made, credited against
 * what is owed, is no money off ("your deposit of $500 counts as $500 off the
 * total", "that's $500 off the balance since you paid the deposit"). Skipped
 * only when the sentence names a deposit or payment AND credits it (paid, a
 * credit, counts as, applied, goes toward), AND either names that deposit at
 * the same amount or puts the money off the balance or what is owed. A
 * deposit asked for is no credit ("$200 off the total if you put down a
 * deposit today" is read), nor money off the balance with no payment named
 * ("I'll take $50 off the balance for the wait" is read). KNOWN OVER-BLOCK,
 * pinned: "it's $500 off what you owe", with no payment named. KNOWN miss,
 * listed for Kabir: a goodwill sum off the balance in a sentence that also
 * credits a deposit ("$50 off the balance since you paid the deposit early").
 */
function depositCredited(text: string, m: RegExpMatchArray): boolean {
  const at = m.index ?? 0;
  const end = at + m[0].length;
  const starts = [...text.slice(0, at).matchAll(/[.!?](?=\s|$)/g)];
  const from = starts.length > 0 ? (starts[starts.length - 1]!.index ?? 0) + 1 : 0;
  const stop = text.slice(end).search(/[.!?](?=\s|$)/);
  const sentence = text.slice(from, stop < 0 ? text.length : end + stop);
  if (!DEPOSIT_NAMED.test(sentence) || !DEPOSIT_CREDIT.test(sentence)) return false;
  const amount = (s: string): string | undefined => /\d[\d,]*(?:\.\d+)?/.exec(s)?.[0].replace(/,/g, "");
  const off = amount(m[0]);
  if (off !== undefined) {
    for (const d of sentence.matchAll(DEPOSIT_AMOUNT)) {
      if (amount(d[0]) === off) return true;
    }
  }
  return LEDGER_TAIL.test(text.slice(end));
}
const DEPOSIT_NAMED = /\b(?:deposits?|down\s*payments?|prepayments?|prepaid|paid)\b/i;
const DEPOSIT_CREDIT =
  /\b(?:paid|prepaid|credit(?:s|ed|ing)?|counts?\s+(?:as|toward|towards)|applied|appl(?:y|ies)\s+to|goes\s+(?:toward|towards))\b/i;
const DEPOSIT_AMOUNT =
  /\b(?:deposits?|down\s*payments?|prepayments?|paid)\s+(?:of\s+)?\$\s?\d[\d,]*(?:\.\d+)?|\$\s?\d[\d,]*(?:\.\d+)?\s+(?:deposits?|down\s*payments?|prepayments?)\b/gi;
const LEDGER_TAIL =
  /^\s+(?:(?:the|your)\s+)?(?:balance|remaining|remainder|rest|outstanding|amount\s+(?:due|owed|owing|left)|what\s+(?:you\s+owe|you['’]?ve\s+got\s+left|(?:is|['’]s)\s+(?:due|left|owed|owing)))\b/i;

/**
 * DISC fix round 2 (review). A service a reply gives free by its ordinal ("the
 * second sizing is free"), as written: a freebie for concessions.ts to count
 * against its freebie cap, never a discount with a share the text did not
 * state. Read here, beside `ORDINAL_FREE`, which skips a service.
 *
 * DISC fix round 5: a DISC reader, so shop-authored text only;
 * concessions.ts reads it for `author: "shop"` and never for a customer.
 */
export function readServiceFreebies(text: string): string[] {
  return readServiceFreebieSpans(text).map((f) => f.text);
}

/** DISC fix round 5: a service freebie as written and where it stands, so one span is counted once. */
export interface ServiceFreebie {
  readonly text: string;
  readonly at: number;
  readonly end: number;
}

/** `readServiceFreebies` with where each stands in the text (shop-authored text only). */
export function readServiceFreebieSpans(text: string): ServiceFreebie[] {
  return [...text.matchAll(SERVICE_FREE)].map((m) => {
    const at = (m.index ?? 0) + (m[0].length - m[0].trimStart().length);
    const written = m[0].trim();
    return { text: written, at, end: at + written.length };
  });
}

/**
 * TA-460 (peer review). Number characters this module cannot read: a digit
 * outside ASCII (fullwidth, Arabic-Indic, any other script), a vulgar
 * fraction, a superscript, a circled or a roman numeral. Every pattern here
 * reads ASCII digits only, so "$" followed by fullwidth digits, or a fraction
 * off, stated no figure a caller could see. Returned for refusal, never folded
 * to ASCII and read: the same stance as `unreadableMoney` (section 0).
 *
 * TA-468 (peer review): a currency sign other than the ASCII "$" too. A
 * fullwidth dollar sign before ASCII digits read as no figure at all, and the
 * shop quotes dollars written with "$" alone.
 */
const UNREADABLE_NUMBER = /[^\P{N}0-9]+|[^\P{Sc}$]/gu;

/**
 * Every run of number characters outside ASCII digits, and every currency sign
 * but "$", verbatim. Empty means every figure here is readable.
 */
export function unreadableFigures(text: string): string[] {
  return text.match(UNREADABLE_NUMBER) ?? [];
}

/**
 * Decision list item 85(a). Peer review TA-460: the
 * verifier read prices, turnarounds, percents and carats, and nothing else, so
 * "ready at 4pm", "an 18k band", "a three year warranty", "by the 6th", "size 8"
 * and "a third off" passed against facts that said otherwise (B13.5: price,
 * carat, date, turnaround). These patterns are read by the VERIFIER ALONE,
 * through `extractVerifierFigures` and `verifierFigureSet`, and grounded
 * against the cited facts. The KB build and F-05's substantive-edit check keep
 * `extractFigures`: widening those moves the eval KB and the approved-without-
 * edit rate, which is item 85(b).
 *
 * Closed grammar, like `SPELLED`: a time needs am/pm, a colon or "o'clock"; a
 * karat needs k, kt or karat; a date needs an ordinal, a month name or a
 * slash; a size needs the word "size"; a fraction word needs off, price or
 * down. Anything outside it stays prose.
 *
 * TA-468 (peer review) widened it where a wrong figure still passed: a time
 * in words ("4 in the afternoon", "noon") or clipped ("4p"), a karat
 * hyphenated or flush with "gold", a dotted or spelled date, an ISO or dashed
 * one, a size after a colon, a bare or hyphenated fraction, and dollars
 * written "USD 70" or "70$". It held two equal claims apart, "15:00" against
 * "3pm" and "$65.00" against "$65", which `normalizeVerifierFigure` now reads
 * as one. A resolution ("4k video") is no karat.
 */
const MERIDIEM = "(?:am|pm|a\\.m\\.|p\\.m\\.)";
const CLOCK = "\\d{1,2}(?::\\d{2})?";
const TIME_RANGE = new RegExp(`\\b${CLOCK}\\s*${MERIDIEM}?\\s*(?:to|-)\\s*${CLOCK}\\s*${MERIDIEM}(?![a-z])`, "gi");
const TIME = new RegExp(`\\b${CLOCK}\\s*${MERIDIEM}(?![a-z])`, "gi");
const TIME_24H = new RegExp(`\\b\\d{1,2}:\\d{2}\\b(?!\\s*${MERIDIEM})`, "gi");
const OCLOCK = new RegExp(`\\b${NUM}\\s*o(?:'|\u2019)?clock\\b`, "gi");
// Flush with the digit only, so "3 a day" and "2 a week" stay prose.
const TIME_CLIPPED = /\b\d{1,2}(?::\d{2})?[ap]\b/gi;
const DAY_PART = "(?:(?:in\\s+the|this)\\s+(?:morning|afternoon|evening)|at\\s+night|tonight)";
const TIME_WORDS = new RegExp(`\\b(?:${CLOCK}|${SPELLED})\\s+${DAY_PART}\\b|\\b(?:noon|midday|midnight)\\b`, "gi");
// "$18k" is money shorthand, so a karat never follows "$". Flush with "gold"
// it is still one ("18kgold"); before a screen word it is a resolution.
const KARAT =
  /(?<!\$\s?)(?<![\d.,])\b\d{1,2}\s?(?:kt|k)(?=gold\b|\b)(?!\s*(?:video|videos|footage|resolution|res|display|screen|tv|monitor|camera)\b)/gi;
const KARAT_WORD = new RegExp(`\\b${NUM}[\\s-]*karats?\\b`, "gi");
const YEAR_UNIT = "(?:years?|yrs?)";
const YEAR_RANGE = new RegExp(`\\b${NUM}\\s*(?:to|-)\\s*${NUM}[\\s-]*${YEAR_UNIT}\\b`, "gi");
const YEARS = new RegExp(`\\b${NUM}[\\s-]*${YEAR_UNIT}\\b`, "gi");
/** TA-408's "a week" and "a month", carried to "a year". */
const ARTICLE_YEAR = /\b(?:a\s+couple\s+of\s+years|an?\s+year)\b/gi;
const MONTH =
  "(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)";
const ORDINAL_SUFFIX = "(?:st|nd|rd|th)";
/** A day of the month, 1 to 31: "on 47th since 1974" names a street, not a date. */
const DAY = "(?:3[01]|[12]\\d|[1-9])";
const ORDINAL_UNIT_WORD = "(?:first|second|third|fourth|fifth|sixth|seventh|eighth|ninth)";
/** A day of the month in words, "first" to "thirty-first". */
const ORDINAL_WORD =
  `(?:twenty[\\s-]?${ORDINAL_UNIT_WORD}|thirty[\\s-]?first|tenth|eleventh|twelfth|thirteenth|fourteenth|fifteenth|` +
  `sixteenth|seventeenth|eighteenth|nineteenth|twentieth|thirtieth|${ORDINAL_UNIT_WORD})`;
const MONTH_SEP = "(?:\\.\\s*|\\s+)(?:the\\s+)?";
const MONTH_DAY = new RegExp(`\\b${MONTH}${MONTH_SEP}(?:${DAY}${ORDINAL_SUFFIX}?|${ORDINAL_WORD})\\b`, "gi");
const DAY_MONTH = new RegExp(
  `\\b(?:${DAY}${ORDINAL_SUFFIX}?\\s+(?:of\\s+)?|(?:the\\s+)?${ORDINAL_WORD}\\s+of\\s+)${MONTH}\\b`,
  "gi",
);
/**
 * Decision list item 153, rec accepted 2026-10-02 (Kabir typed "Yes I want to
 * continue the production" in reply to the accept-the-recs question). "May" is
 * the month only when written "May". Lowercase is the verb as often as not
 * ("3 may differ"), and all caps ("MAY 5") carries no case to read, so neither
 * is assumed to be the month. Done in code, not with a case modifier inside
 * the pattern, which the engines this repo supports do not all read.
 */
function mayUnresolved(match: string): boolean {
  return (match.match(/\bmay\b/gi) ?? []).some((word) => word !== "May");
}

interface MonthNamedDate {
  readonly at: number;
  readonly text: string;
  /** Item 153: the month word is a "may" not written "May". */
  readonly unresolved: boolean;
}

/**
 * `MONTH_DAY` or `DAY_MONTH` over a text, every match, each marked whether its
 * month is resolved (item 153). The verb's own shape ("3 may differ") is read
 * too: a pattern cannot tell it from a date, and the first cut's list of words
 * that follow the verb let real dates through ("ready 14 may with the
 * engraving done", FG review). So it is held as "3 may", as "3 May" was held
 * before item 153, and a caller that must see a date sees it.
 */
function monthNamedDates(text: string, pattern: RegExp): MonthNamedDate[] {
  return [...text.matchAll(pattern)].map((m) => ({ at: m.index ?? 0, text: m[0], unresolved: mayUnresolved(m[0]) }));
}
/**
 * The day alone, in words, as `ORDINAL` reads "6th": after a month, before "of"
 * and a month, or after a date word and "the" at the end of its clause. So
 * "through the sixth" is a date and "on the first try" is prose.
 */
const DATE_WORD = "(?:on|by|until|till|til|thru|through|before|after|from|starting|ending)";
const ORDINAL_DAY_WORD = new RegExp(
  `(?<=\\b${MONTH}${MONTH_SEP})${ORDINAL_WORD}\\b|${ORDINAL_WORD}(?=\\s+of\\s+${MONTH}\\b)|` +
    `(?<=\\b${DATE_WORD}\\s+the\\s+)${ORDINAL_WORD}\\b(?=\\s*(?:[.!?,;:)]|$)|\\s+(?:at|of\\s+the\\s+month)\\b)`,
  "gi",
);
// A street, a floor or an occasion is not a date: "5th avenue", "the 3rd floor".
const ORDINAL = new RegExp(
  `\\b${DAY}${ORDINAL_SUFFIX}\\b(?!\\s+(?:st\\b|street|ave\\b|avenue|floor|fl\\b|anniversary|birthday))`,
  "gi",
);
// Month first, as the shop writes it, so "open 24/7" is not a date. Not the
// half in a mixed number: "size 6 1/2" is one size.
const SLASH_DATE = /(?<![\d/]\s?)\b(?:1[0-2]|0?[1-9])\/(?:3[01]|[12]\d|0?[1-9])(?:\/(?:\d{4}|\d{2}))?\b/g;
const ISO_DATE = /\b\d{4}-(?:1[0-2]|0[1-9])-(?:3[01]|[12]\d|0[1-9])\b/g;
/**
 * `SLASH_DATE` with its year required, for `extractDates` (L11 review). The
 * verifier's pattern reads "1/2 carat" as a date, which costs it a refused
 * draft at worst; a brief that tells a person "the card holds a date: 1/2"
 * invents a talking point. A year is what no fraction has.
 */
const SLASH_DATE_YEAR = /(?<![\d/]\s?)\b(?:1[0-2]|0?[1-9])\/(?:3[01]|[12]\d|0?[1-9])\/(?:\d{4}|\d{2})\b/g;
const SIZE_WORD = "(?:sizes?|sz\\.?)\\s*[:#]?";
/**
 * Month first, like `SLASH_DATE`: "10-6". Not a range that carries its unit
 * ("3-5 days", "3-5pm", "sizes 6-7"), which the patterns above read whole, and
 * not "24-7". Read verbatim: "open 10-6" may be hours, so it grounds only
 * against a fact that says "10-6".
 *
 * TA-572: nor a range whose unit only the verifier's wide reader knows ("4-6
 * wks", "2-3 business days", "50-75 points"). It read as a date no fact
 * states, so "4-6 wks" held against "4 to 6 weeks", the same claim.
 */
const WIDE_RANGE_UNIT = "(?:(?:business|working|work)[\\s-]+days?|wks?|mos?|hrs?|cts|ctw|tcw|cttw|points?|pts)";
const DASH_DATE = new RegExp(
  `(?<![\\d/.,$-])(?<!\\b${SIZE_WORD}\\s*)\\b(?:1[0-2]|0?[1-9])-(?:3[01]|[12]\\d|0?[1-9])\\b(?![-/.]?\\d)` +
    `(?!\\s*(?:${MERIDIEM}|[ap]\\b|${DURATION_UNIT}\\b|${YEAR_UNIT}\\b|kt?\\b|ct\\b|[ck]arats?\\b|${PERCENT_MARK}|dollars?\\b|bucks\\b|${WIDE_RANGE_UNIT}\\b))`,
  "gi",
);
const SIZE_NUM = `${NUM}(?:\\s+(?:and\\s+)?a\\s+half\\b|\\s+1\\/2\\b)?`;
const SIZE = new RegExp(`\\b${SIZE_WORD}\\s*${SIZE_NUM}(?:\\s*(?:to|-)\\s*${SIZE_NUM})?`, "gi");
/** What joins the words of a discount: spaces and dashes (`DASH`). */
const DISCOUNT_JOIN = `[\\s${DASH}]+`;
const DISCOUNT_JOIN_ALL = new RegExp(DISCOUNT_JOIN, "g");
/** The fraction words, their two-word forms joined by `join`. */
const fractionWord = (join: string): string =>
  `(?:half|(?:(?:a|one)${join})?third|(?:(?:a|one)${join})?quarter|two${join}thirds|three${join}quarters)`;
const FRACTION_WORD = fractionWord("\\s+");
const FRACTION_CLAIM = new RegExp(`\\b${FRACTION_WORD}[\\s-]+(?:off|price|down)\\b`, "gi");
/**
 * Item 125: `FRACTION_CLAIM` as a discount, for `readDiscountClaims`. Not
 * "down": "half down holds it" is a deposit, which the verifier still grounds
 * as "1/2down", not money off.
 *
 * FG review: a two-word fraction is usually hyphenated ("two-thirds off",
 * "three-quarters off"), and the first cut read that as no discount, so a 66
 * to 75% discount cleared every gate. This reader takes the hyphen. The
 * verifier's `FRACTION_CLAIM` does not (the item widens nothing else): the
 * concession ledger reads the discount, and that alone holds the draft.
 */
/**
 * What a fraction is of: "off" is the fraction off; "price" (bare, "the" or
 * "of the") and, since the item 125 third review, "the cost" or "of the cost"
 * are what is left to pay. Shared by the word and the digit readers.
 *
 * Fourth review (Fable re-check of 946e174, minor): "cost" is never read bare.
 * An ordinal comes before it ("is there a third cost for the setting?"), and
 * read bare that was 66.67% off: a concession ask that dropped the cost fact
 * and screened the size beside it. "half cost" and "three quarters cost me
 * $5" read nothing now too.
 */
const DISCOUNT_OF =
  "(off|priced|(?:(?:of\\s+)?(?:the|its|our|their|your)\\s+(?:(?:usual|regular|normal|original|full|list|retail|sticker)\\s+)?)?price|" +
  "(?:of\\s+)?(?:the|(?:the|its|our|their|your)\\s+(?:usual|regular|normal|original|full|list|retail|sticker))\\s+cost)";
/**
 * Decision 3: a fifth and a tenth too, in a discount alone. The verifier's
 * `FRACTION_CLAIM` keeps `fractionWord` unchanged, so "a fifth off" is no
 * figure there (pinned in figures.test.ts); the ledger is what holds it.
 */
const discountFractionWord = (join: string): string =>
  `(?:${fractionWord(join)}|(?:two|three|four)${join}fifths|(?:(?:a|one)${join})?(?:fifth|tenth))`;
/**
 * DISC fix round 3 (review): "the fifth price on the list" is an ordinal, so a
 * fifth or a tenth after a determiner is no fraction. Bare "fifth price" stays
 * read (80), as does "a fifth off"; "the third price" is unchanged from base.
 */
const ORDINAL_FIFTH = "(?!(?<=\\b(?:the|this|that|each|every|its|our|your|their|my)\\s+)(?:fifth|tenth)s?\\b)";
const DISCOUNT_FRACTION = new RegExp(
  `\\b${ORDINAL_FIFTH}(${discountFractionWord(DISCOUNT_JOIN)})${DISCOUNT_JOIN}${DISCOUNT_OF}\\b`,
  "gi",
);
/**
 * Item 125, second review. The rest of the ways a reply states money off, each
 * sized in `readDiscountClaims`. A fraction in digits ("1/2 off", "3/4 price"),
 * never the half of a size ("6 1/2") or a figure in a longer one; "two for one"
 * and "2-for-1", never "two for one hundred"; "buy one get one" and "buy two
 * get one"; "BOGO".
 *
 * Item 125, third review: the digit fraction flush with its word ("1/2off")
 * or spaced around the slash ("1 / 2 off"); "2for1"; a dotted "B.O.G.O.";
 * "N for the price of M" (`FOR_PRICE_OF`); "buy one, get another".
 */
const DISCOUNT_SLASH = new RegExp(
  `(?<![\\d/.,$])(?<!\\d\\s+)\\b([1-9])\\s*\\/\\s*([2-9]|10)(?![\\d/])[\\s${DASH}]*${DISCOUNT_OF}\\b`,
  "gi",
);
/**
 * Item 125, third review: "2 for 1:30" is a clock time, not a deal. The one
 * exclusion is a digit 1 followed by a colon and exactly two digits. These
 * stay read, the hold direction: "2 for 1: 30 rings left", "two for one day
 * only".
 */
const TWO_FOR_ONE = new RegExp(
  `\\b(?:two${DISCOUNT_JOIN}for${DISCOUNT_JOIN}|2[\\s${DASH}]*for[\\s${DASH}]*)(?:one|1(?!:\\d\\d(?!\\d)))\\b(?![.,]?\\d)(?![\\s${DASH}]*(?:hundred|thousand|dollars?|bucks)\\b)`,
  "gi",
);
const BOGO = /\bb\.?o\.?g\.?o(?:\.?f)?\b/gi;
const DEAL_COUNT = `(${UNIT_WORD}|[1-9])`;
/** The free count in "buy N get M": a number, or "another" (one more). */
const FREE_COUNT = `(${UNIT_WORD}|[1-9]|another)`;
/**
 * DISC fix round 3 (review, major): a piece of jewelry, named. What an ordinal
 * makes free (`ORDINAL_FREE`), what a deal gives, and what a bare figure is
 * off (`BARE_OFF`). A list of what a piece is, never a list of what it is not:
 * a deny-list read a stylist, a viewing room and "the second one" in a booking
 * line as a piece, and held each as a 50% concession.
 *
 * DISC fix round 4: a gem by name and jewelry ("the second sapphire is
 * free"), which a deal on a stone names as often as "stone" does.
 */
const PIECE_NOUN =
  "(?:rings?|bands?|pieces?|pairs?|sets?|items?|necklaces?|pendants?|chains?|chokers?|lockets?|bracelets?|bangles?|" +
  "cuffs?|earrings?|studs?|hoops?|charms?|brooch(?:es)?|anklets?|cufflinks?|watch(?:es)?|stones?|diamonds?|gems?|" +
  "gemstones?|pearls?|strands?|settings?|mount(?:ing)?s?|solitaires?|signets?|tiaras?|accessor(?:y|ies)|purchases?|orders?|" +
  "sapphires?|rub(?:y|ies)|emeralds?|opals?|jewel(?:le)?ry|jewels?)";
/**
 * DISC fix round 4: a piece a deal is on, before or after "2-4-1" ("Bands are
 * 2-4-1", "2-4-1 Rings"). Never an order, an item or a purchase, which label
 * a code ("order 2-4-1").
 */
const DEAL_PIECE_NOUN = `(?!(?:items?|purchases?|orders?)\\b)${PIECE_NOUN}`;
/**
 * Decision 3: a noun may name what is bought, "buy one ring, get one". DISC
 * fix round 3 (review): "and" or "you'll" may come before "get" ("buy one
 * ring and get one").
 */
const GET_LEAD = "(?:(?:and|&)\\s+)?(?:you(?:['\u2019]ll|\\s+can|\\s+will)\\s+)?";
/** DISC fix round 4: "purchase" buys and "receive" gets ("Purchase two, get one free", "Buy a band, receive one free"). */
const BUY_WORD = "(?:buy|purchase)";
const GET_WORD = "(?:get|receive)";
const BUY_GET = new RegExp(
  `\\b${BUY_WORD}${DISCOUNT_JOIN}${DEAL_COUNT}(?:${DISCOUNT_JOIN}[a-z]+)?[\\s,${DASH}]+${GET_LEAD}${GET_WORD}${DISCOUNT_JOIN}${FREE_COUNT}\\b(?![.,]?\\d)`,
  "gi",
);
/** "two for the price of one": N taken, M paid for. */
const FOR_PRICE_OF = new RegExp(
  `\\b${DEAL_COUNT}${DISCOUNT_JOIN}for${DISCOUNT_JOIN}the${DISCOUNT_JOIN}price${DISCOUNT_JOIN}of${DISCOUNT_JOIN}${DEAL_COUNT}\\b(?![.,]?\\d)`,
  "gi",
);
/**
 * DISC fix round 5: base e5faa94's patterns that DISC widened for the shop,
 * kept as they were for the customer side (`readCustomerDiscountClaims`). A
 * percent off only; a fraction off, priced or of the price or the cost; a
 * digit fraction the same; "buy N get M" with buy and get only. Never widened:
 * a customer's reading is base's (the structural rule, round 5).
 */
const BASE_DISCOUNT_CLAIM = new RegExp(`\\b(${DISCOUNT_HUNDRED}|${NUM})[\\s${DASH}]*${PERCENT_MARK}[\\s${DASH}]*off\\b`, "gi");
/**
 * Z2 CLAIMSCREEN (Y6 ASI01, ASI06; CLAUDE.md section 6): a customer's claimed
 * rate said without "off", "my resizes are 15% under list". Customer side only
 * (the memory screen and the concession ask), where a hit is a quarantine of a
 * recalled row or an escalation, never a refusal. Read as that percent, so the
 * ledger sizes it like any other claim. The shop's reading is untouched.
 * AWAITING RATIFICATION: the list words (list, retail, msrp, sticker) and the under, below, beneath verbs.
 */
const CUSTOMER_PERCENT_UNDER_LIST = new RegExp(
  `\\b(${DISCOUNT_HUNDRED}|${NUM})[\\s${DASH}]*${PERCENT_MARK}\\s+(?:under|below|beneath)\\s+(?:the\\s+)?(?:list|retail|msrp|sticker)(?:\\s+price)?\\b`,
  "gi",
);
const BASE_DISCOUNT_OF = "(off|(?:(?:of\\s+)?the\\s+)?price|(?:of\\s+)?the\\s+cost)";
const BASE_DISCOUNT_FRACTION = new RegExp(`\\b(${fractionWord(DISCOUNT_JOIN)})${DISCOUNT_JOIN}${BASE_DISCOUNT_OF}\\b`, "gi");
const BASE_DISCOUNT_SLASH = new RegExp(
  `(?<![\\d/.,$])(?<!\\d\\s+)\\b([1-9])\\s*\\/\\s*([2-9]|10)(?![\\d/])[\\s${DASH}]*${BASE_DISCOUNT_OF}\\b`,
  "gi",
);
const BASE_BUY_GET = new RegExp(`\\bbuy${DISCOUNT_JOIN}${DEAL_COUNT}[\\s,${DASH}]+get${DISCOUNT_JOIN}${FREE_COUNT}\\b(?![.,]?\\d)`, "gi");
/**
 * DISC fix round 3 (review, major): the same deal written with an article,
 * the way a jeweller writes a sale fact ("Buy a band and get one free.",
 * "Buy any band and get another one free."), one of two free. "free" is
 * required: "buy a ring and get another sized" is a bench job. "get a second
 * free" is `ORDINAL_FREE`'s.
 */
const BUY_ANY_GET = new RegExp(
  `\\b${BUY_WORD}\\s+(?:a|an|any|each|every)\\s+(?:[a-z][\\w'-]*\\s+){0,2}?[a-z][\\w'-]*[\\s,]+${GET_LEAD}` +
    `${GET_WORD}\\s+(?:one|another(?:\\s+one)?)(?:\\s+(?:more|${PIECE_NOUN}))?\\s+(?:for\\s+)?free\\b`,
  "gi",
);
/** The deal said the other way round: "Get one free when you buy a band." Sized as "buy N get M". */
const GET_FREE_WHEN_BUY = new RegExp(
  `\\bget\\s+(${UNIT_WORD}|[1-9]|another|a\\s+second)(?:\\s+(?:more|${PIECE_NOUN}))?\\s+(?:for\\s+)?free\\s+(?:when|if|with)\\s+` +
    `(?:(?:you|i|we)\\s+)?(?:buy|purchase)\\s+(an?|any|${UNIT_WORD}|[1-9])\\b(?:\\s+(?:[a-z][\\w'-]*\\s+)?${PIECE_NOUN}\\b)?`,
  "gi",
);
/** What may not stand before a count in "N for M": a size, a time or a place in a list. */
const DEAL_COUNT_NOT_AFTER = "(?<![$#\\d.,:/])(?<!\\b(?:sizes?|sz|at|by|from|to|until|till|no\\.?|number|of)\\s+)";
/**
 * "3 for 2", "three for two": N taken, M paid for (sized in `readDiscountClaims`,
 * read only when N is more). Never a clock time ("3 for 2:30", "4 for 2pm"),
 * a span ("3 for 2 days") or a party ("4 for 2 people").
 */
const N_FOR_M = new RegExp(
  `${DEAL_COUNT_NOT_AFTER}\\b(${UNIT_WORD}|ten|[1-9]|1\\d)${DISCOUNT_JOIN}for${DISCOUNT_JOIN}(${UNIT_WORD}|[1-9])\\b(?![.,:/]?\\d)` +
    `(?![\\s${DASH}]*(?:am|pm|a\\.m|p\\.m|o['\u2019]?clock|hundred|thousand|dollars?|bucks|days?|weeks?|months?|years?|` +
    "hours?|hrs?|minutes?|mins?|nights?|people|persons?|guests?|of|more|or|to|and\\s+a\\s+half)\\b)",
  "gi",
);

/**
 * Decision 3 of the 10-02b list: the rest of the ways a reply states money
 * off, each sized in `readDiscountClaims`. "B1G1" and "B2G1" (one of N+1
 * free); "2-4-1", dashed only, so "241 Main Street" is an address; "the
 * second ring free", one of two, never "the second ring size".
 *
 * DISC fix round 1 (two reviews): at a 0% cap a reading that is no discount
 * strands the draft, so each reader below now needs its discount context.
 * "2-4-1" is no deal inside a longer number ("555-2-4-1"), after a unit word
 * ("Suite 2-4-1"), before a street word within three words ("2-4-1 Main
 * St") or before a capitalized word ("2-4-1 Ginza", checked in
 * `readDiscountClaims`, which reads case). An ordinal is free only as a
 * piece: one or two words name it, none of them a day, a slot, a visit, a
 * size or a month ("the 2nd is free at 2pm", "the second appointment is
 * free" are bookings), and no time follows "free"; "get a second free" reads
 * only after a "buy" in the same sentence ("when you get a second free" is a
 * moment).
 */
const B_G = /\bb([1-9])g([1-9])f?\b/gi;
/**
 * DISC fix round 2 (review): a word between "2-4-1" and an address word that
 * says it is no street name ("2-4-1 at our Main St store" is a deal at the
 * store). The capitalized-word skip of round 1 is gone: "2-4-1 This Weekend"
 * and "Bands are 2-4-1 Fridays" are deals, and only an address word after it
 * makes "2-4-1" a place ("2-4-1 Ginza, Chuo-ku").
 */
const ADDRESS_GAP_STOP =
  "(?:at|our|the|in|on|for|this|that|these|every|all|any|from|near|by|and|or|with|until|through|only|store|shop|" +
  "location|is|are|was|were|if|when)";
/**
 * DISC fix round 3 (review): a label before "2-4-1" makes it a code, never a
 * deal ("My order number is 2-4-1", "SKU 2-4-1", "style 2-4-1"). A bare
 * "order" is no label: "Order 2-4-1 today" is a deal (KNOWN OVER-BLOCK,
 * pinned: "order 2-4-1"). Never "code": a promo code is a discount.
 */
const TWO_FOUR_ONE_LABEL =
  "(?<!\\b(?:(?:my|your|the|this|that|an|our|their)\\s+(?:order|item|ticket|invoice)|sku|ref|reference|style|model|part)" +
  "(?:\\s+(?:number|no\\.?|#|id))?(?:\\s*:|\\s+(?:is|was))?\\s*)";
const TWO_FOUR_ONE = new RegExp(
  `(?<![\\d.])(?<!\\d[${DASH}])(?<!(?:\\b(?:at|suite|ste|unit|apt|apartment|room|no\\.?|number)|#)\\s*)${TWO_FOUR_ONE_LABEL}` +
    `\\b2[${DASH}]4[${DASH}]1\\b(?![${DASH}]?\\d)(?![.,]\\d)` +
    `(?!(?:[\\s,]+(?!${ADDRESS_GAP_STOP}\\b)[a-z.']+){0,3}?[\\s,${DASH}]+(?:st|street|ave|avenue|rd|road|blvd|` +
    `boulevard|lane|ln|dr|drive|way|suite|ste|unit|apt|floor|fl|plaza|chome|ku|cho|machi)\\b)`,
  "gi",
);
const ORDINAL_FREE_WHICH = "(second|third|fourth|2(?=nd)|3(?=rd)|4(?=th))(?:nd|rd|th)?";
/** A word between an ordinal and "free" that makes it a booking, not a piece. */
const NOT_A_PIECE =
  "(?:days?|dates?|weeks?|weekends?|months?|years?|times?|slots?|spots?|openings?|appointments?|appts?|" +
  "fittings?|visits?|consult(?:ation)?s?|sessions?|bookings?|meetings?|calls?|hours?|half|mornings?|" +
  "afternoons?|evenings?|nights?|sizes?|floors?|(?:mon|tues|wednes|thurs|fri|satur|sun)days?|" +
  "mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun|of|at|on|in|is|are|was|for|and|or|to|from|" +
  `${MONTH})`;
/**
 * DISC fix round 2 (review): a service is no piece. "the second sizing is
 * free" is a freebie, which concessions.ts counts against the freebie cap
 * (`readServiceFreebies`), never a share of a price the text never stated.
 */
const ORDINAL_FREE_SERVICE =
  "(?:re-?)?(?:sizings?|cleanings?|clean|polish(?:es|ing)?|engravings?|inspections?|appraisals?|repairs?|" +
  "platings?|rhodium|tippings?|check-?ups?|checks?|adjustments?|services?|refinish(?:es|ing)?|piercings?)";
/** DISC fix round 3: one word may describe the piece ("the second matching band"), never a booking word or a service. */
const PIECE_MODIFIER = `(?:(?!(?:${NOT_A_PIECE}|${ORDINAL_FREE_SERVICE})\\b)[a-z0-9][\\w'-]*\\s+)?`;
/** "ring", "matching band", "pair of studs". */
const NAMED_PIECE = `${PIECE_MODIFIER}${PIECE_NOUN}\\b(?:\\s+of\\s+${PIECE_MODIFIER}${PIECE_NOUN}\\b)?`;
/**
 * "free at 2pm", "free from 10", "free until noon": a time, so a booking.
 * DISC fix round 2 (review): "until" a day is when a deal ends ("the second
 * ring is free until Sunday"), so only "until" a time is a booking; "free of"
 * a flaw ("free of inclusions", never "free of charge") and "free to" do
 * something ("free to resize", never "free to you") are no deal.
 */
const FREE_TAIL =
  "(?!\\s+(?:(?:at|from|after|before|until|till|til)\\s+(?:\\d|noon\\b|midday\\b|midnight\\b)|between\\b|" +
  "of\\s+(?!(?:charge|cost)\\b)|to\\s+(?!(?:you|u|yourself|customers?|clients?)\\b)[a-z]|" +
  // DISC fix round 3 (review, major): "free from" a flaw ("free from inclusions", "free from nickel"), never from a day.
  `from\\s+(?!(?:charge|today|tomorrow|tonight|now|this|next|(?:mon|tues|wednes|thurs|fri|satur|sun)days?|${MONTH})\\b)[a-z]))`;
/**
 * Fix round 2 (review): "Buy one ring, the second free." The ordinal with no
 * piece word is read only after a "buy" in the same sentence, as "get a second
 * free" is, since "I have the 2nd free." is a customer's free day.
 *
 * DISC fix round 3 (review, major): the piece is named (`NAMED_PIECE`), so a
 * stylist, a room or a designer free is a booking line. "one" stands for a
 * piece after a "buy" ("Buy one ring, the second one is free."), or in the
 * bare offer "the third one free" with no "is" before it; "Thursday or
 * Friday? The second one is free." and "is the second one free?" are about a
 * day. Found by the fix round 3 fuzz: after a "buy" the article may be left
 * out ("buy one, second one free", "buy one get second free"), and a named
 * piece at a sentence start is a headline offer ("2nd ring free this week").
 */
const NOT_ASKED = "(?<!\\b(?:is|are|isn['\u2019]?t|aren['\u2019]?t)\\s+)";
/**
 * DISC fix round 4 (verify 3, major): the buy branch read an ordinal with no
 * piece after any "buy" ("If you buy this week, the 2nd is free for a
 * fitting", "I'd love to buy a ring and I have the 2nd free"), so it is split
 * three ways, each on a positive deal context: (a) the ordinal of a named
 * piece or of "one" after a "buy", never asked; (b) "get a second free" after
 * a "buy"; (c) a word ordinal after "buy one" ("Buy one ring, the second
 * free."), never asked, or a digit one with no article and no verb ("buy 1,
 * 2nd free"), since "the 2nd is free" is a day. A day ordinal with no piece
 * after "buy" a time or "buy a" piece reads nothing. `NOT_ASKED_PAST_ARTICLE` looks past an article, so
 * the match cannot start after "is the".
 */
const NOT_ASKED_PAST_ARTICLE = "(?<!\\b(?:is|are|isn['\u2019]?t|aren['\u2019]?t)\\s+(?:(?:the|your|a)\\s+)?)";
const AFTER_BUY = "(?<=\\bbuy\\b[^.?!]{0,60}?)";
const AFTER_BUY_COUNT = "(?<=\\b(?:buy|purchase)\\s+(?:one|two|three|[1-9])\\b[^.?!]{0,60}?)";
const ORDINAL_FREE = new RegExp(
  `(?:\\b(?:the|your)\\s+${ORDINAL_FREE_WHICH}\\s+${NAMED_PIECE}|` +
    `${NOT_ASKED}\\b(?:the|your)\\s+${ORDINAL_FREE_WHICH}\\s+one(?=\\s+free\\b)|` +
    `${AFTER_BUY}${NOT_ASKED_PAST_ARTICLE}\\b(?:get\\s+(?:(?:a|the|your)\\s+)?|(?:the|your)\\s+)?${ORDINAL_FREE_WHICH}` +
    `\\s+(?:${NAMED_PIECE}|${PIECE_MODIFIER}one\\b)|` +
    `${AFTER_BUY}\\bget\\s+(?:(?:a|the|your)\\s+)?${ORDINAL_FREE_WHICH}|` +
    `${AFTER_BUY_COUNT}${NOT_ASKED_PAST_ARTICLE}\\b(?:(?:the|your)\\s+)?(second|third|fourth)|` +
    `${AFTER_BUY_COUNT}${NOT_ASKED_PAST_ARTICLE}(?<!\\b(?:the|your)\\s+)\\b(2(?=nd)|3(?=rd)|4(?=th))(?:nd|rd|th)(?=\\s+free\\b)|` +
    `(?<=(?:^|[.!?:;\\n])\\s*)${ORDINAL_FREE_WHICH}\\s+${NAMED_PIECE})` +
    `\\s+(?:(?:is|are)\\s+)?free\\b${FREE_TAIL}`,
  "gi",
);
/**
 * "the second sizing is free", "your third ring cleaning is free": a service at no charge (`readServiceFreebies`).
 * DISC fix round 3 (review): never asked ("Is the second sizing free?"), which is a customer's question, no freebie.
 */
const SERVICE_FREE = new RegExp(
  `${NOT_ASKED}\\b(?:the|your)\\s+${ORDINAL_FREE_WHICH}\\s+(?:[a-z][\\w'-]*\\s+)?${ORDINAL_FREE_SERVICE}\\b` +
    `\\s+(?:(?:is|are)\\s+)?free\\b${FREE_TAIL}`,
  "gi",
);
/**
 * Money as `extractMoney` reads it, or a figure in dollars: what money off is
 * counted in. Fix round 1: never a shorter reading of a longer figure ("$1"
 * of "$10 a month"); "$2k"; and money spelled with no digit, "a hundred
 * dollars", "a couple hundred bucks", "hundreds of dollars", which `NUM`
 * does not read alone.
 */
const SPELLED_HUNDREDS =
  "(?:(?:a|one)\\s+(?:hundred|thousand)|(?:a\\s+(?:couple|few)(?:\\s+of)?|several)\\s+(?:hundred|thousand)|" +
  "(?:hundreds|thousands)\\s+of|hundred|thousand)";
/** "USD 70", "70 USD" and "70$" are "$70". Never half a price ("USD 70.5"), never a count ("2 $65"). */
const MONEY_AMOUNT = "\\d+(?:,\\d{3})*(?:\\.\\d{2})?(?![.,]?\\d)";
/**
 * DISC fix round 3 (review): money off in a currency code ("USD 20 off", "20
 * USD off", "20$ off", as `MONEY_CODE` reads a price), in euros or in quid.
 * Never pounds: a weight is written the same way.
 */
const MONEY_OFF_AMOUNT =
  `(?:\\$\\s?\\d+(?:\\.\\d+)?k\\b|${MONEY.source}(?![\\d,]|\\.\\d)|` +
  `\\busd\\s?${MONEY_AMOUNT}|(?<![$\\d.,])\\b${MONEY_AMOUNT}\\s?(?:usd\\b|\\$(?!\\s?\\d))|` +
  `\\b(?:${SPELLED_HUNDREDS}|${NUM})\\s*(?:dollars?|bucks|euros?|eur|quid|gbp)\\b)`;
/**
 * Fix round 1: a deposit or a payment is no money off ("the deposit is $50 off
 * the final price"), and "off" is no price word before a dash ("$50
 * off-white", "a $50 off-site appraisal"), as concessions.ts's own `OFF` reads it.
 */
const MONEY_OFF_NOT_PAID = "(?<!\\b(?:deposits?|down\\s*payments?|payments?|instal?l?ments?|balance)\\s+(?:(?:is|was|of|will\\s+be|would\\s+be)\\s+)?)";
/** "$50 off", "50 dollars off", "a $50 discount", "a $50 rebate". The percent is null: no price is known here. */
const MONEY_OFF = new RegExp(
  `${MONEY_OFF_NOT_PAID}(?:${MONEY_OFF_AMOUNT}[\\s${DASH}]*(?:off\\b(?![${DASH}])|` +
    `(?:discount|reduction|rebate|markdown|saving|price[\\s${DASH}]?cut)s?\\b)|` +
    `\\b(?:a|one|two|three|four|five|six|seven|eight|nine|ten|a\\s+couple(?:\\s+of)?|a\\s+few|\\d+)\\s+grand[\\s${DASH}]*off\\b(?![${DASH}]))`,
  "gi",
);
/**
 * "Two for $100": a deal whose size as a percent depends on a price this reader
 * does not have, so unsized. DISC fix round 4: read only on a deal context
 * (`readDiscountClaims`); the count is captured for the price check there.
 */
const N_FOR_MONEY = new RegExp(
  `${DEAL_COUNT_NOT_AFTER}\\b(two|three|four|five|six|seven|eight|nine|ten|[2-9]|1\\d)${DISCOUNT_JOIN}for${DISCOUNT_JOIN}` +
    `(?:(?:just|only)\\s+)?${MONEY_OFF_AMOUNT}` +
    "(?!\\s*(?:deposits?|down|each|apiece|per|a\\s+piece|an?\\s+(?:hour|day|week|month|year))\\b)",
  "gi",
);
/**
 * DISC fix round 4 (verify 3, major): a deal reader this item added ("3 for
 * 2", "Two for $100", "2-4-1") reads only on a positive deal context, never on
 * a list of innocent ones. Round 3 read "3 for 2 bridesmaids", "two for $3,000
 * is our budget" and "order 2-4-1" as deals, each a booking line screened or a
 * reply held at the 0% cap. The context is one of: a sale, a deal, a promotion
 * or an offer named in the same sentence (`SALE_CONTEXT`, never a bare time
 * word: "Book 3 for 2 Oct?"), the piece the deal is on after it ("on all
 * bands", `DEAL_ON_PIECE`), or for "2-4-1" a piece named right before or after
 * it ("Bands are 2-4-1", "2-4-1 Rings"). "N for $X" reads besides after "just"
 * or "only" and as a headline on a piece ("Two for $100 on the studs."), and
 * never with a budget word in its sentence ("Our budget is two for just
 * $3,000", "two for $3,000 on the bands is our budget"), nor where $X is N
 * times a unit price the text states ("$450 each, so two for $900").
 */
const SALE_CONTEXT = new RegExp(
  "\\b(?:sale|deals?|promos?|promotions?|clearance|discount(?:s|ed)?|bargains?|" +
    "(?:on|special|our|this|that|the|an?)\\s+offers?|mix(?:\\s+|-)(?:and|&|n)(?:\\s+|-)match|limited[\\s-]+time|" +
    "while\\s+(?:stocks?|supplies)\\s+lasts?|(?:this\\s+(?:week|weekend|month)|today|tonight)\\s+only|" +
    "only\\s+(?:this|today|tonight|until|till|through))\\b",
  "i",
);
const DEAL_QUANTIFIER = "(?:(?:all|any|every|each|the|our|your|these|those|this|that|selected)\\s+)?";
const DEAL_ON_PIECE = new RegExp(
  `^\\s+(?:on|across)\\s+${DEAL_QUANTIFIER}(?:everything\\b|${PIECE_MODIFIER}${PIECE_NOUN}\\b)`,
  "i",
);
const PIECE_AFTER_DEAL = new RegExp(
  `^\\s+(?:(?:on|across)\\s+${DEAL_QUANTIFIER})?(?:everything\\b|${PIECE_MODIFIER}${DEAL_PIECE_NOUN}\\b)`,
  "i",
);
const PIECE_BEFORE_DEAL = new RegExp(`\\b${DEAL_PIECE_NOUN}\\s+(?:(?:is|are|now)\\s+)?$`, "i");
/**
 * DISC fix round 4: what follows "2-4-1" that makes it a deal and never an
 * address: when it runs ("2-4-1 this week", "2-4-1 until Sunday", "2-4-1
 * Fridays") or the shop it runs in ("2-4-1 at our Main St store"). A street
 * name is never followed by either. Read only where "2-4-1" is stated as a
 * deal (`DEAL_STATED_BEFORE`): at a sentence or clause start, or after "is
 * it", "it's" or "we're doing". A label before it is a code whatever follows
 * ("Can I pick up order 2-4-1 today?").
 */
const DEAL_STATED_BEFORE = new RegExp(
  "(?:^|[.!?:;,(\\n])\\s*$|\\b(?:(?:is|are|was|were)(?:\\s+(?:it|they|there|that|this))?|it['\u2019]s|that['\u2019]s|" +
    "we['\u2019]re|we\\s+are|doing|do|running|runs?|offering|offers?|get|gets|getting)\\s+$",
  "i",
);
const DEAL_RUN_AFTER = new RegExp(
  "^\\s+(?:(?:this|next|all)\\s+(?:week|weekend|month)|today|tonight|until|till|through|" +
    "(?:on\\s+)?(?:mon|tues|wednes|thurs|fri|satur|sun)days?|" +
    "(?:at|in)\\s+(?:our|the|all|every|any)\\s+(?:[\\w'.-]+\\s+){0,3}?(?:stores?|shops?|boutiques?|showrooms?|locations?|branch(?:es)?))\\b",
  "i",
);
/** A customer's budget in the sentence of "N for $X", before or after it: an ask of what to spend, never a deal. */
const BUDGET_WORD = /\b(?:budget\w*|ideally|afford\w*|spend(?:s|ing)?|hoping|hope)\b/i;
/** A unit price the text states: "$450 each". */
const UNIT_PRICE = /\$\s?(\d+(?:,\d{3})*(?:\.\d{1,2})?)(k)?\s*(?:each|apiece|a\s+piece|per\s+(?:piece|ring|band|stud|pair|item|one))\b/gi;
/** Whole cents of "$1,800", "$3k", "900 dollars"; null when spelled. Deterministic code, never model arithmetic (B13). */
const centsOf = (written: string): number | null => {
  const m = /(\d+(?:,\d{3})*(?:\.\d+)?)\s*(k\b)?/i.exec(written);
  if (m === null) return null;
  return Math.round(Number.parseFloat((m[1] ?? "").replace(/,/g, "")) * (m[2] !== undefined ? 1000 : 1) * 100);
};
/** Where a bare figure off ends a clause: the end, a stop, a connective or an offer word. */
const BARE_OFF_END = "(?=\\s*(?:[.,;:!?)]|$)|\\s+(?:for|on|with|and|but|so|if|today|now|this|tonight|right)\\b)";
/**
 * DISC fix round 3 (review): a figure off with no currency named, which the
 * shop says aloud ("I can do 10 off the total", "100 off the ring", "a
 * hundred off"). Its size is unknown (ten dollars or ten percent), so it is
 * unsized, as money off is. Read only where nothing else is off: before a
 * price word, before a piece when ten or more (`readDiscountClaims`), "a
 * hundred" or "a thousand" at a clause end, and after "do", "knock", "give"
 * or "offer" at a clause end. Never right after "$" or inside a longer
 * figure, which `MONEY_OFF` reads; "a size 10 off the shelf" is none.
 *
 * DISC fix round 4 (verify 3): a figure off a piece is read only where the
 * figure is what is offered, at a sentence start ("100 off the ring") or
 * after an offer verb ("Get 100 off any ring", "I'll take 100 off the band"),
 * so a count off a shelf or a length off a chain is none ("We have 30 off the
 * shelf rings", "Can you cut 12 off the chain?").
 */
const BARE_OFF_PIECE_LEAD =
  "(?<=(?:^|[.!?:;(\\n])\\s*|\\b(?:do|does|doing|did|give|gives|giving|gave|take|takes|taking|took|knock(?:s|ed|ing)?|" +
  "offer(?:s|ed|ing)?|get|gets|getting|got|save|saves|saving|that['’]?s|it['’]?s)\\s+(?:you\\s+)?" +
  "(?:an?\\s+(?:extra|additional)\\s+)?)";
const BARE_OFF = new RegExp(
  `${MONEY_OFF_NOT_PAID}(?<![$\\d.,])(?:` +
    `\\b(${NUM}|a\\s+(?:hundred|thousand))\\s+off\\s+(?=(?:the|your|this|that|our|its)\\s+(?:[a-z][\\w'-]*\\s+)?` +
    "(?:prices?|costs?|totals?|bills?|quotes?|invoices?|fees?|balance)\\b)|" +
    `${BARE_OFF_PIECE_LEAD}\\b(${NUM}|a\\s+(?:hundred|thousand))\\s+off\\s+(?=(?:(?:the|your|this|that|our|its|a|any|each|every)\\s+)?` +
    `${PIECE_MODIFIER}${PIECE_NOUN}\\b)|` +
    `\\b(?:a|${UNIT_WORD}|a\\s+few|a\\s+couple(?:\\s+of)?|several)\\s+(?:hundred|thousand)\\s+off\\b(?![${DASH}])${BARE_OFF_END}|` +
    "(?<=\\b(?:do|does|doing|knock(?:s|ed|ing)?|give|gives|giving|gave|offer(?:s|ed|ing)?)\\s+(?:you\\s+)?)" +
    `\\b${NUM}\\s+off\\b(?![${DASH}])${BARE_OFF_END})`,
  "gi",
);
/**
 * Fix round 1: what a saving may not be followed by. A fraction of a size, a
 * carat or an hour ("save half an hour", "reduced by half a size") and money
 * by the period ("save $10 a month") are no discount.
 */
const SAVING_TAIL =
  "(?!\\s*(?:an?\\s+)?(?:sizes?|carats?|cts?|inch(?:es)?|mm|millimet(?:er|re)s?|hours?|hrs?|days?|weeks?|" +
  "minutes?|mins?|months?|years?)\\b)" +
  "(?!\\s+(?:(?:a|an|per|each|every)\\s+(?:day|week|month|year)|monthly|weekly|yearly|annually)\\b)" +
  // DISC fix round 3 (review): a piece made smaller in a dimension ("reduced by a third in weight").
  "(?!\\s+in\\s+(?:weight|size|thickness|width|length|height|diameter|depth|mass|volume)\\b)";
/** What a saving is: a percent, a fraction, or money, in that capture order. Never "pc" (fix round 1). */
const SAVING =
  `(?:(${DISCOUNT_HUNDRED}|${NUM})[\\s${DASH}]*${PERCENT_MARK}|` +
  `(${discountFractionWord(DISCOUNT_JOIN)})\\b|${MONEY_OFF_AMOUNT})${SAVING_TAIL}`;
/** "save 20%", "save half", "save $50", "save up to 20%". Never a bare "save": "save the date". */
const SAVE = new RegExp(`\\bsav(?:e|es|ing)\\s+(?:you\\s+)?(?:up\\s+to\\s+|an?\\s+extra\\s+)?${SAVING}`, "gi");
/**
 * DISC fix round 3 (review): who reduces. "I" or "we" (the shop), with any
 * helper words ("I'm happy to", "we can also"), or "can you" (a customer's ask).
 */
const SHOP_SUBJECT =
  "(?:\\b(?:we|i)(?:['\u2019](?:ve|d|ll|m|re))?(?:\\s+(?:have|had|can|could|will|would|just|already|also|gladly|" +
  "happily|am|are|be|to|able|happy|glad|willing|going|definitely|certainly|really|still|now|try|should|may|might|do|did))*|" +
  "\\b(?:can|could|would|will)\\s+(?:you|u)(?:\\s+(?:please|just|maybe|possibly|also))*)\\s+";
const PRICE_OBJECT =
  "(?:the|its|our|their|your|this|that)\\s+(?:[a-z][\\w'-]*\\s+)?(?:prices?|costs?|totals?|quotes?|bills?|fees?|charges?)";
/**
 * "marked down by half", "marked down by $50", "reduced the price by 20%".
 * DISC fix round 2 (review): "reduce the price by" in any tense.
 * DISC fix round 3 (review): lowered, dropped, cut, brought or knocked down,
 * of a price, a cost, a total, a quote or a fee, by anyone ("could you lower
 * the price by 10%?"). The shop reducing a piece is `SHOP_REDUCES_BY`.
 *
 * DISC fix round 4 (verify 3, major): lowering, dropping, cutting or reducing a
 * price is read only of the shop or a customer's "can you" (`SHOP_SUBJECT`), or
 * at a sentence start ("Cut the price by 20%."). A choice is never the
 * subject: "Going with 14k lowers the price by 20%" and "Switching to
 * lab-grown cuts the cost by 70%" compare. "marked down", "knocked down" and
 * "discounted" by a figure read after any subject.
 */
const PRICE_VERB_LEAD =
  `(?<=${SHOP_SUBJECT}|(?:^|[.!?:;(\\n])\\s*(?:(?:please|just|(?:happy|glad|willing)\\s+to|let\\s+(?:me|us)|let['\u2019]s)\\s+)?)`;
const MARKED_DOWN_BY = new RegExp(
  `(?:\\b(?:marked${DISCOUNT_JOIN}down|knocked${DISCOUNT_JOIN}down|discounted)|${PRICE_VERB_LEAD}(?:` +
    `(?:reduc(?:e|ed|ing)|lower(?:s|ed|ing)?|drop(?:s|ped|ping)?|cut(?:s|ting)?)\\s+${PRICE_OBJECT}|` +
    `(?:bring(?:s|ing)?|brought|knock(?:s|ed|ing)?)\\s+${PRICE_OBJECT}\\s+down))\\s+by\\s+${SAVING}`,
  "gi",
);
const SHOP_PRONOUN = "(?:it|them|this|that|these|those)";
/**
 * DISC fix round 2 (review): the shop reducing a piece ("we reduced it by
 * $50", "I can reduce it by 20%"), where "we" or "I" is what makes it the
 * price. DISC fix round 3 (review): lowering, dropping, knocking or bringing
 * it down, discounting it or a piece ("we can discount the ring by 20%"), and
 * coming down ("I'll come down $50"). A recut or a sizing after a percent or a
 * fraction is the piece made smaller (`craftReduction`).
 */
const SHOP_REDUCES_BY = new RegExp(
  `(?<=${SHOP_SUBJECT})(?:(?:` +
    `(?:reduc(?:e|ed|ing)|lower(?:ed|ing)?|drop(?:ped|ping)?|knock(?:ed|ing)?|discount(?:ed|ing)?)\\s+${SHOP_PRONOUN}(?:\\s+down)?|` +
    `(?:bring|bringing|brought)\\s+${SHOP_PRONOUN}\\s+down|` +
    `discount(?:ed|ing)?\\s+(?:the|this|that|these|those|your|our)\\s+${PIECE_MODIFIER}${PIECE_NOUN})\\s+by|` +
    `come\\s+down(?:\\s+by)?)\\s+(?:up\\s+to\\s+)?${SAVING}`,
  "gi",
);
/** DISC fix round 3 (review): "knock off 20%", "knock off $50", by anyone. */
const KNOCK_OFF = new RegExp(
  `\\bknock(?:s|ed|ing)?\\s+off\\s+(?:an?\\s+(?:extra|additional)\\s+)?${SAVING}`,
  "gi",
);
/** DISC fix round 3 (review): "take off 10%", "take off $50". Never a fraction: "take off half the band". */
const TAKE_OFF_SAVING = new RegExp(
  `\\b(?:take|takes|taking|took)\\s+off\\s+(?:an?\\s+(?:extra|additional)\\s+)?` +
    `(?:(${DISCOUNT_HUNDRED}|${NUM})[\\s${DASH}]*${PERCENT_MARK}|${MONEY_OFF_AMOUNT})${SAVING_TAIL}`,
  "gi",
);
/** A verb or an adverb between what is reduced and "reduced": "the price has now been reduced". */
const REDUCED_AUX_WORD =
  "(?:is|was|are|were|has|have|had|been|being|be|will|would|can|could|gets?|got|now|all|already|just|also|since)";
/**
 * "reduced by 20%", "reduced by half", "reduced by $50". Fix round 1: never
 * before a size or a period ("reduced by half a size", `SAVING_TAIL`).
 *
 * DISC fix round 2 (review, major): read only when what is reduced is a price
 * or a piece (`REDUCED_LEAD`, "the band is reduced by 20%", "prices have
 * been reduced by a quarter"), a pronoun with its verb ("it was reduced by a
 * third", "it's reduced by 20%"), or nothing, at a sentence start ("Reduced
 * by 20% this week."). Round 1 read any subject but a listed few, so a craft
 * question ("is the risk of losing the stone reduced by half with six
 * prongs?", "my grandmother's diamond was reduced by a third when it was
 * recut") was a concession ask and an answer ("the glare is reduced by half")
 * was held. A pronoun with no verb is an object ("losing it reduced by half").
 */
/**
 * DISC fix round 4 (verify 3): the subject is split. A price word,
 * "everything", a piece the shop names ("the band", "all rings", "selected
 * styles", a bare piece at a sentence start) or nothing at a sentence start
 * reads as before (`REDUCED_LEAD`). A pronoun reads only when the saving is
 * money ("it is reduced by $50") or its sentence names a sale or a time
 * ("it's reduced by 20% today", checked in `readDiscountClaims`), and a
 * customer's own piece ("my band was reduced by a third") is no subject: a
 * piece made smaller and a price cut read the same, and only the deal context
 * tells them apart.
 */
const REDUCED_PRICE_WORD = "(?:prices?|pricing|costs?|totals?|fees?|charges?|quotes?|bills?|invoices?|tags?|stickers?)";
const REDUCED_PIECE =
  "(?:rings?|bands?|pieces?|items?|necklaces?|bracelets?|earrings?|pendants?|studs?|sets?|chains?|watch(?:es)?|styles?|" +
  "collections?|orders?|purchases?)";
const REDUCED_AUXES = `(?:${REDUCED_AUX_WORD}\\s+){0,3}`;
const REDUCED_LEAD =
  `(?<=\\b(?:${REDUCED_PRICE_WORD}|everything(?:['\u2019]s)?)\\s+${REDUCED_AUXES}|` +
  `\\b(?:the|this|that|these|those|your|our|all|every|any|selected)\\s+(?:[a-z0-9][\\w'-]*\\s+)?${REDUCED_PIECE}\\s+${REDUCED_AUXES}|` +
  `(?:^|[.!?:;(\\n])\\s*${REDUCED_PIECE}\\s+${REDUCED_AUXES}|^\\s*|[.!?:;(]\\s*)`;
const REDUCED_PRONOUN_LEAD =
  `(?<=\\b(?:it|this|that|these|those|they)(?:['\u2019](?:s|re)\\s+|\\s+${REDUCED_AUX_WORD}\\s+)` +
  `(?:${REDUCED_AUX_WORD}\\s+){0,2})`;
const REDUCED_BY_TAIL = `\\breduced\\s+(?:by\\s+)?(?:up\\s+to\\s+)?${SAVING}`;
const REDUCED_PCT_TAIL = `\\b(${DISCOUNT_HUNDRED}|${NUM})[\\s${DASH}]*${PERCENT_MARK}[\\s${DASH}]*reduced\\b`;
/** DISC fix round 3 (review): "by" may be left out ("prices reduced 20% this week"). */
const REDUCED_BY = new RegExp(`${REDUCED_LEAD}${REDUCED_BY_TAIL}`, "gi");
const REDUCED_BY_PRONOUN = new RegExp(`${REDUCED_PRONOUN_LEAD}${REDUCED_BY_TAIL}`, "gi");
/** DISC fix round 3 (review): the percent first, "It is 20% reduced.", of the same subjects. */
const REDUCED_PCT = new RegExp(`${REDUCED_LEAD}${REDUCED_PCT_TAIL}`, "gi");
const REDUCED_PCT_PRONOUN = new RegExp(`${REDUCED_PRONOUN_LEAD}${REDUCED_PCT_TAIL}`, "gi");
/**
 * DISC fix round 4: what lets a pronoun's reduction read, in its sentence: a
 * sale (`SALE_CONTEXT`) or the period a price holds for ("this week",
 * "today", "until Sunday"). Never "now" or a bare "until": "it was reduced by
 * a third and now it spins" is a piece.
 */
const REDUCED_TIME_CONTEXT = new RegExp(
  "\\b(?:this\\s+(?:week|weekend|month)|today|tonight|(?:until|till|through)\\s+(?:(?:mon|tues|wednes|thurs|fri|satur|sun)day|" +
    `the\\s+end|end|close|midnight|${MONTH})\\b)`,
  "i",
);
/**
 * DISC fix round 3 (review): a percent or a fraction reduced when, after or
 * since a piece was recut, sized down, polished or worn is the piece made
 * smaller ("the band was reduced by a quarter when it was sized down"). Money
 * reduced stays read whatever follows, and so does a price reduced
 * (`craftReduction`).
 */
const CRAFT_EVENT =
  /^\s+(?:when|after|once|from|with|during|through|as|since)\s+(?:[a-z'\u2019]+\s+){0,3}?(?:re-?cut\w*|re-?siz\w*|siz(?:ed|ing)\s+down|polish\w*|re-?shap\w*|re-?set(?:ting)?|re-?plat\w*|fil(?:ed|ing)|thinn\w*|wear|worn|stretch\w*|melt\w*|re-?cast\w*|shorten\w*)\b/i;
const PRICE_IN_CLAUSE = /\b(?:prices?|pricing|costs?|totals?|fees?|charges?|quotes?|bills?|invoices?|tags?|stickers?)\b[^.!?;]*$/i;
/**
 * DISC fix round 3 (review): "I can do the diamond for 20% less." A percent
 * less that the shop offers to do is read whatever it compares, which
 * `LESS_TAIL` skips after a stone or a metal. The offer needs a helper
 * ("can", "will", "'ll", "happy to"), so "We do lab-grown for about 70% less."
 * stays a comparison. "than" compares, unless what follows is the usual,
 * list or retail price ("20% less than the list price").
 */
const OFFER_SUBJECT =
  "\\b(?:we|i)(?:['\u2019](?:ll|d)|(?:['\u2019](?:m|re|ve))?(?:\\s+(?:am|are|be|really|definitely|certainly|happy|glad|" +
  "willing|able|going|also|just))*\\s+(?:can|could|will|would|to))" +
  "(?:\\s+(?:just|also|definitely|certainly|happily|gladly|really|easily|absolutely|probably|still|be|able|happy|glad|" +
  "willing|going|to))*\\s+";
/**
 * DISC fix round 4 (verify 3, major): what the shop offers for less is named
 * right before "for" or "at": a piece or "one" after "the", "this" or "your"
 * ("I can do the diamond for 20% less", "I'll give you this one for 10%
 * less"), or "it" or "them". Round 3 read any 40 characters, so an
 * alternative was an offer ("I can do lab-grown for about 70% less", "We can
 * do a smaller stone for 30% less", "I can do it in silver for 80% less"):
 * each compares a choice, which the shop does not discount.
 */
const OFFERED_OBJECT =
  "(?:(?:you|u)\\s+)?(?:(?:the|this|that|these|those|your)\\s+(?:[a-z0-9][\\w'-]*\\s+){0,2}?" +
  `(?:${PIECE_NOUN}|ones?)\\b|(?:it|them|this|that|these|those)\\b)`;
const OFFERED_LESS = new RegExp(
  `(?<=${OFFER_SUBJECT}(?:(?:do|give|let\\s+you\\s+have|sell|offer)\\s+${OFFERED_OBJECT}` +
    "\\s+(?:for|at)|(?:make|price|do)(?:\\s+(?:it|them|this|that|these|those))?)" +
    "\\s+(?:(?:about|around|roughly|just|only|an\\s+extra)\\s+)?)" +
    `(${DISCOUNT_HUNDRED}|${NUM})[\\s${DASH}]*${PERCENT_MARK}[\\s${DASH}]*(?:less|cheaper|lower)\\b` +
    "(?!\\s+than\\b(?!\\s+(?:the|its|our|your|my)\\s+(?:usual|regular|normal|original|full|list|retail|sticker|asking)\\s+price\\b))",
  "gi",
);
/** "a discount of 20%", "a saving of $50", "a rebate of up to 10%": the noun first. */
const NOUN_OF_SAVING = new RegExp(
  `\\b(?:discounts?|reductions?|markdowns?|rebates?|savings?)\\s+of\\s+(?:up\\s+to\\s+)?${SAVING}`,
  "gi",
);
const MONEY_CODE = new RegExp(`\\busd\\s?${MONEY_AMOUNT}|(?<![$\\d.,])\\b${MONEY_AMOUNT}\\s?(?:usd\\b|\\$(?!\\s?\\d))`, "gi");

const VERIFIER_ONLY_PATTERNS: readonly RegExp[] = [
  TIME_RANGE, TIME, TIME_24H, OCLOCK, TIME_CLIPPED, TIME_WORDS, KARAT, KARAT_WORD, YEAR_RANGE, YEARS, ARTICLE_YEAR,
  MONTH_DAY, DAY_MONTH, ORDINAL, ORDINAL_DAY_WORD, SLASH_DATE, ISO_DATE, DASH_DATE, SIZE, FRACTION_CLAIM, MONEY_CODE,
];

const FRACTION_VALUE: Readonly<Record<string, string>> = {
  half: "1/2", third: "1/3", athird: "1/3", onethird: "1/3", quarter: "1/4", aquarter: "1/4", onequarter: "1/4",
  twothirds: "2/3", threequarters: "3/4",
  // Decision 3, read in a discount alone (`discountFractionWord`).
  fifth: "1/5", afifth: "1/5", onefifth: "1/5", twofifths: "2/5", threefifths: "3/5", fourfifths: "4/5",
  tenth: "1/10", atenth: "1/10", onetenth: "1/10",
};
const MONTH_ONLY = new RegExp(`^${MONTH}$`);
const ORDINAL_WORD_VALUE: Readonly<Record<string, number>> = {
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8, ninth: 9, tenth: 10,
  eleventh: 11, twelfth: 12, thirteenth: 13, fourteenth: 14, fifteenth: 15, sixteenth: 16, seventeenth: 17,
  eighteenth: 18, nineteenth: 19, twentieth: 20, thirtieth: 30,
};

/** "twenty-first" as "21st", the way `ORDINAL` reads the digits. */
function ordinalWordAsDigits(phrase: string): string {
  let n = 0;
  for (const word of phrase.split(/[\s-]+/)) n += ORDINAL_WORD_VALUE[word] ?? WORD_VALUE[word] ?? 0;
  const teen = n % 100 >= 11 && n % 100 <= 13;
  return `${n}${teen ? "th" : (["th", "st", "nd", "rd"][n % 10] ?? "th")}`;
}

/**
 * TA-566 READER (ULTRA accuracy hunt, surface figform). Forms of a figure the
 * base grammar misses or reads by its tail, read here by the VERIFIER ALONE,
 * so the KB build and F-05's edit metric keep `extractFigures` (item 85(b)).
 *
 * One grammar, no second parser: every reader below is built from the same
 * `SPELLED`, `DASH` and `PERCENT_MARK` pieces the base patterns use, and the
 * lists are closed (AWAITING RATIFICATION: first cuts, not calibrated on real traffic). A form it reads is read to ONE canonical string
 * (`normalizeVerifierFigure` leaves it as it is), so "3-week", "3 wks" and "3w"
 * are the same claim as "3 weeks" and none of them is the claim "weeks" with
 * a number the facts happen to state.
 *
 * Why tails matter. The base patterns match anywhere, so "1/2 ct" read as
 * "2ct", ".5 ct" as "5ct", "1 1/2 weeks" as "2 weeks" and "1,650 dollars" as
 * "650 dollars": a part of the number, which a cited fact then grounded. A base
 * read that sits inside a wider read here is dropped (`supersededBy`).
 */
const DIGITS = "(?:\\d{1,3}(?:,\\d{3})+|\\d+)(?:\\.\\d+)?";
const WHOLE = `(?:${DIGITS}|${SPELLED})`;
const PART_WORD = "(?:half|quarter|three[\\s-]quarters?)";
/** A quantity: a number, a mixed number or fraction, ".5", or "half a", "three quarter". */
const QTY =
  `(?:${WHOLE}\\s+and\\s+(?:an?\\s+)?${PART_WORD}|\\d+\\s+\\d+\\/\\d+|\\d+\\/\\d+|\\.\\d+|` +
  "(?:(?:an?|one)\\s+)?(?:half|quarter)(?:\\s+of)?(?:\\s+an?)?|three[\\s-]quarters?(?:\\s+of)?(?:\\s+an?)?|" +
  `${WHOLE})`;
/** Never starts a read inside a longer number or word. */
const QTY_START = "(?<![\\w.,/])";

/** "business days" is a different claim from "days". Flush "3w" and "2h" read; flush "3d" is 3D (a model), never days. */
const WIDE_UNIT =
  "(?:(?:(?:business|working|work)[\\s-]+)?(?:days?|weeks?|wks?|months?|mos?|hours?|hrs?|minutes?|mins?)\\b|" +
  "(?<=\\d)(?:w\\b|h\\b))";
const OTHER_UNIT_AFTER =
  "(?:ct|cts|carats?|days?|weeks?|wks?|months?|mos?|hours?|hrs?|mins?|minutes?|years?|yrs?|dollars?|bucks|percent|per\\s?cent|pct|am|pm|a\\.m|p\\.m|o'?clock|st|nd|rd|th|mm|cm|g|grams?)";

const RANGE_JOIN_WIDE = "(?:\\s+(?:to|or)\\s+|\\s*-\\s*)";
const WIDE_DURATION = new RegExp(
  `${QTY_START}(${QTY})(?:${RANGE_JOIN_WIDE}(${QTY}))?[\\s-]*(${WIDE_UNIT})`,
  "gi",
);
const WIDE_ARTICLE_HALF = /\ban?\s+(week|month|day|hour)\s+and\s+a\s+half\b/gi;
/**
 * TA-1374, draft side only (round 3): "a year and a half" and "a carat and a
 * half" too. A KB fact keeps `WIDE_ARTICLE_HALF`: "a year and a half price
 * resize" is a year and a half-price resize, and a fact must never ground a
 * term it does not state.
 */
const WIDE_ARTICLE_HALF_DRAFT = /\ban?\s+(week|month|day|hour|year|carat)\s+and\s+a\s+half\b/gi;
/**
 * TA-1374, draft side only (round 3): a year quantity `YEARS` cannot read
 * whole ("one and a half years", "1 1/2 years", "half a year"), read as
 * "1.5y". A plain number ("3 years", "1.5 year", "one to two years") stays
 * with `YEARS` and `YEAR_RANGE`.
 */
const WIDE_YEARS = new RegExp(`${QTY_START}(${QTY})(?:${RANGE_JOIN_WIDE}(${QTY}))?[\\s-]*(?:years?|yrs?)\\b`, "gi");
/**
 * TA-1374 round 2, draft side only (round 3): "1 year and a half", "one year
 * and a half", "a year and half": N + 0.5 years. On a fact, "1 year and half
 * the labour cost" is a 1 year term and a half-cost repair, so a fact keeps
 * 373becde's read.
 */
const WIDE_YEAR_AND_HALF = new RegExp(`${QTY_START}(an?|${WHOLE})\\s+(?:years?|yrs?)\\s+and\\s+(?:a\\s+)?half\\b`, "gi");
const PLAIN_NUM = new RegExp(`^${NUM}$`, "i");
/** "a week or two", "a week, maybe two": a range, never the "a week" it starts with. */
const WIDE_ARTICLE_RANGE = new RegExp(
  "\\ban?\\s+(week|month)(?:,\\s*(?:or\\s+)?(?:maybe|perhaps|possibly|even)\\s+|\\s+or\\s+(?:maybe\\s+|perhaps\\s+|even\\s+)?)" +
    `(${WHOLE})(?!\\d|\\.\\d)(?!\\s*(?:${OTHER_UNIT_AFTER}|%))`,
  "gi",
);
const WIDE_COUPLE = /\ba\s+couple\s+(?!of\b)(days|weeks|months)\b/gi;
/** TA-572: "1-1.5 ct" and "10 to 15%" are ranges, read whole, as `WIDE_DURATION` reads "4-6 weeks". */
const WIDE_CARAT = new RegExp(
  `${QTY_START}(${QTY})(?:${RANGE_JOIN_WIDE}(${QTY}))?[\\s-]*(cts?|carats?|ctw|tcw|cttw|points?|pts)\\b`,
  "gi",
);
const WIDE_PERCENT = new RegExp(`${QTY_START}(${QTY})(?:${RANGE_JOIN_WIDE}(${QTY}))?\\s*${PERCENT_MARK}`, "gi");
const WIDE_CENTS = new RegExp(`${QTY_START}(?<!\\$)(${DIGITS}|${SPELLED})\\s*cents?\\b`, "gi");

/**
 * Another currency's word or code after an amount. A price in euros or CAD is
 * a model's own conversion (section 4: math is deterministic, never a model),
 * so it is read as a figure no cited fact states. `cad` is the shop's design
 * work unless it carries money context (a decimal or thousands figure, "1,200
 * CAD"): "2 CAD changes" and "one CAD" are not a price.
 */
const FOREIGN_CURRENCY =
  "(?:cad|aud|nzd|eur|euros?|gbp|pounds?|yen|jpy|cny|rmb|yuan|inr|rupees?|mxn|pesos?|chf|francs?|sek|hkd|sgd|aed|dirhams?)";
const CAD_NOUN = "(?:render\\w*|revision\\w*|design\\w*|file\\w*|model\\w*|drawing\\w*|draft\\w*|work|software|image\\w*|sketch\\w*|mock\\w*|proof\\w*|step|stage|phase|session\\w*|round\\w*|preview\\w*)";
const WIDE_FOREIGN = new RegExp(
  `${QTY_START}(${DIGITS}|${SPELLED})\\s*(${FOREIGN_CURRENCY})\\b(?!\\s+${CAD_NOUN}\\b)`,
  "gi",
);
const WIDE_FOREIGN_CODE = /\b(eur|gbp|jpy|cny|rmb|inr|mxn|chf|sek|hkd|sgd|aed|aud|nzd)\s?(\d+(?:,\d{3})*(?:\.\d+)?)(?![\d,]|\.\d)/gi;
const FOREIGN_UNIT: Readonly<Record<string, string>> = {
  eur: "euro", gbp: "pound", jpy: "yen", cny: "yuan", rmb: "yuan", inr: "rupee", mxn: "peso", chf: "franc",
  aed: "dirham",
};

/** "$5k", "$1.2m", "$3 thousand": a price with its scale, not the "$5" a part of it reads as. */
const MONEY_SCALE_VALUE: Readonly<Record<string, number>> = {
  k: 1e3, thousand: 1e3, grand: 1e3, m: 1e6, mm: 1e6, million: 1e6, bn: 1e9, billion: 1e9,
};
const WIDE_MONEY_SCALED = new RegExp(`\\$\\s?(${DIGITS})\\s?(k|m|mm|bn|thousand|million|billion|grand)\\b`, "gi");
/** "1 500 dollars", "1,650 dollars": a grouped number the base NUM reads by its last group. */
const WIDE_MONEY_GROUPED = /(?<![\d.,])(\d{1,3}(?:[, ]\d{3})+(?:\.\d+)?)\s*(?:dollars?|bucks)\b/gi;
/** TA-1378, cost: where `WIDE_MONEY_GROUPED` can start, and its run of groups (`matchAllPastGroups`). */
const WIDE_MONEY_GROUPED_SCAN: GroupedScan = {
  sticky: new RegExp(WIDE_MONEY_GROUPED.source, "iy"),
  start: /(?<![\d.,])\d/g,
  run: /\d{1,3}(?:[, ]\d{3})+/y,
};

/**
 * TA-1378 (FINDINGS TA-1377 left open 8), cost. A pattern that opens on a
 * number written in groups of three ("1 000 000") was tried by `matchAll` at
 * every group of a long run, and each try read the run to its end before it
 * failed, so "$1" and 20,000 " 000" groups cost time quadratic in the length.
 *
 * `matchAllPastGroups` gives exactly the matches `text.matchAll(pattern)`
 * gives, for a pattern whose run of groups is `run` and that can start only
 * where `start` matches (its own lookbehind, then a digit). It tries the
 * pattern where `matchAll` would, but when a try fails at the start of a run,
 * it does not try the groups inside the run: each would read the same run to
 * the same end and fail on the same text after it, since a shorter run fails
 * before a group the run goes on with. The run's last group is still tried,
 * as `matchAll` would try it, because the pattern can read a number from
 * there that the run does not hold ("1 000,000 HK$" reads "000,000").
 * Every read is as 70ecf4a1 made it; only the time changes.
 */
interface GroupedScan {
  readonly sticky: RegExp;
  readonly start: RegExp;
  readonly run: RegExp;
}
function matchAllPastGroups(text: string, scan: GroupedScan): RegExpExecArray[] {
  const out: RegExpExecArray[] = [];
  const { sticky, start, run } = scan;
  start.lastIndex = 0;
  for (let s = start.exec(text); s !== null; s = start.exec(text)) {
    const at = s.index;
    sticky.lastIndex = at;
    const m = sticky.exec(text);
    if (m !== null) {
      out.push(m);
      start.lastIndex = at + Math.max(1, m[0].length);
      continue;
    }
    run.lastIndex = at;
    // The last group is four characters, a separator and three digits: its digits are tried next.
    if (run.test(text)) start.lastIndex = run.lastIndex - 3;
  }
  return out;
}
/** "a thousand dollars", "5 thousand dollars", "two grand", "a million dollars", "a couple grand" (TA-572). */
const WIDE_MONEY_MAGNITUDE = new RegExp(
  `(?<![\\w.,])(?:(${DIGITS}|${SPELLED})\\s*|(an?)\\s+|(a\\s+couple(?:\\s+of)?)\\s+)(thousand|million|grand)\\b`,
  "gi",
);
/**
 * TA-1374: "3.5k", "1.9K", "2.4 thousand", "3.5m" with no "$": a decimal in
 * shorthand that no reader above saw ("$" is missing for `WIDE_MONEY_SCALED`,
 * and `KARAT` and `WIDE_MONEY_MAGNITUDE` skip a digit after a dot), so a wrong
 * price passed. Read as scaled money, as "$3.5k" and "3.5 grand" are. "mm" is
 * left out: "2.5mm" is a width, not $2.5 million. The integer forms stay where
 * they were ("3k" a karat, FINDINGS: the conservative side).
 *
 * Draft side only (round 3): on a fact, "2.5k five-star reviews" is a count
 * and "2.5m long" a length, and neither may ground a price.
 */
const WIDE_MONEY_SCALED_BARE = /(?<![\w.,$])(?<!\$\s)(\d*\.\d+)\s?(k|m|bn|thousand|million|billion)\b/gi;
/** "3-3.5k": the near end has no scale of its own, so the pair is refused whole, as "$3-4k" is. */
const MONEY_NEAR_BARE = /\d[\d,.]*\s*(?:-|to)\s*$/i;
/**
 * TA-1374 round 2: "EUR 3.5k", a euro or pound sign before, or "3.5k euros" is another currency,
 * never dollars. A code or word reads as that currency, in the form
 * `WIDE_FOREIGN_CODE` and `WIDE_FOREIGN` give; a symbol this table does not
 * name is refused.
 */
const BARE_FOREIGN_CODE_BEFORE = /\b(eur|gbp|jpy|cny|rmb|inr|mxn|chf|sek|hkd|sgd|aed|aud|nzd|cad)\s?$/i;
const BARE_SYMBOL_BEFORE = /(\p{Sc})\s?$/u;
const BARE_FOREIGN_AFTER = new RegExp(`^\\s*(${FOREIGN_CURRENCY})\\b(?!\\s+${CAD_NOUN}\\b)`, "i");
const SYMBOL_UNIT: Readonly<Record<string, string>> = { "\u20ac": "euro", "\u00a3": "pound", "\u00a5": "yen", "\u20b9": "rupee" };
/** TA-572: "15 hundred dollars", the digit form of "fifteen hundred dollars" that the base grammar reads. */
const WIDE_MONEY_HUNDREDS = new RegExp(`(?<![\\w.,])(${DIGITS})\\s*hundred\\s*(?:dollars?|bucks)\\b`, "gi");
/**
 * TA-572: "65-80 dollars", "sixty five to eighty dollars". The base reader reads
 * the far end, which carries the unit; the near end is a price too, read here.
 * Fix round 2: a digit far end may carry its scale ("4 to 9 hundred dollars").
 */
const WIDE_MONEY_WORDS_RANGE = new RegExp(
  `(?<![\\w.,$])(${DIGITS}|${SPELLED})${RANGE_JOIN_WIDE}(${DIGITS}(?:\\s*(?:hundred|thousand)\\b)?|${SPELLED})\\s*(?:dollars?|bucks)\\b`,
  "gi",
);
/**
 * TA-572 fix round 1: "a couple grand tours", "two grand pianos": "grand" before
 * one of these nouns names the noun, not $1,000. Anything else after it keeps
 * it money ("or so", "for", "on", "total", a clause end). AWAITING
 * RATIFICATION (TA-572): a closed first cut, not calibrated on real traffic.
 * "total" is left out: "two grand total" is money, and "a grand total" is
 * already no price ("a grand" reads only before A_GRAND_ENDS).
 */
const GRAND_AFTER_NOUN =
  /^\s+(?:tours?|pianos?|prizes?|finale|slam|jury|opening|canyon|central|rapids)\b/i;
const A_GRAND_ENDS = /^(?:\s*(?:$|[.,;:!?)])|\s+(?:for|and|or|to|more|less|plus|each|per|extra|off|down|deposit|upfront)\b)/i;
const WIDE_HUNDRED = new RegExp(`\\ban?\\s+hundred(?:\\s+(?:and\\s+)?(${SUB100}))?\\s*(?:dollars?|bucks)\\b`, "gi");
/**
 * "$65-80", "$65 to 80", "$65 or 80": the far end has no "$" of its own and is
 * a price. TA-572 fix round 1: "or" stays a range join, so an invented far end
 * is read and must be grounded.
 */
const WIDE_MONEY_UPPER = new RegExp(
  "\\$\\s?\\d[\\d,]*(?:\\.\\d+)?(?:-|\\s+(?:to|through|thru|or)\\s+)" +
    `(${DIGITS})(?!\\d)(?![,.]\\d)(?!\\s*%)(?!\\s*${OTHER_UNIT_AFTER}\\b)(?![:/])`,
  "gi",
);
const MONEY_FAR_SCALE = /^\s?(?:k|m|mm|bn|thousand|million|billion|grand)\b/i;
const MONEY_FAR_BARE = /^\s*(?:-|to)\s*\d/i;

/*
 * TA-1375 round 2, DRAFT SIDE ONLY. Each reader below runs in `draftExtras`,
 * never in `scanWide`: its reads and refusals are added to 5f4596d8's draft
 * read and draft refusals, never in their place, so a draft reads every
 * figure it read at 5f4596d8 and is refused for every token it was refused
 * for there. A fact reads exactly what it read at 5f4596d8 (round 1 changed
 * fact reads and was rejected for it). Every one holds more, never less.
 */
const EXTRA_SCALE = "(?:k|m|bn|thousand|million|billion|grand|hundred)";
/**
 * An amount after a mark; space-grouped digits ("C$ 1 500") are one amount,
 * never its first group. It ends where no digit, no comma group and no
 * decimal follows: a prose comma ("C$3,500, set in platinum") or a cents
 * amount with text after it ("C$3,500.00, set") still ends it (fix round: a
 * lookahead that refused any comma left those reading only "$3,500").
 */
const EXTRA_AMOUNT = `(\\d{1,3}(?: \\d{3})+(?:\\.\\d+)?|${DIGITS})(?:\\s?(${EXTRA_SCALE})\\b)?(?!\\d|,\\d|\\.\\d)`;
/** "1 week and a half", "2 days and half", "one month and a half": N + 0.5. "a week and a half" is `WIDE_ARTICLE_HALF`'s. */
const DRAFT_DURATION_AND_HALF = new RegExp(
  `${QTY_START}(an?|${WHOLE})\\s+(days?|weeks?|months?|hours?|years?|yrs?)\\s+and\\s+(?:a\\s+)?half\\b`,
  "gi",
);
/**
 * "a carat": 1ct, as "a week" is 1 week. Never a rate ("$800 a carat", "2k a
 * carat", "900 euros a carat") and never part of a quantity "half a carat",
 * "three quarters of a carat" and "a carat and a half" already read whole.
 */
const DRAFT_A_CARAT = new RegExp(
  `(?<!(?:\\d|\\d\\s?[km]|[^\\P{Sc}]|\\b(?:dollars?|bucks|grand|thousand|hundred|million|${FOREIGN_CURRENCY}|half|quarters?|thirds?|of|per)\\)?)\\s*)` +
    "\\ban?\\s+carat\\b(?!\\s+and\\s+(?:an?\\s+)?(?:half|quarter)\\b)(?!\\s+weights?\\b)(?![-\\w])",
  "giu",
);
/** A letter prefix on "$" ("C$3,500", "A$3.5k", "HK$ 900", "R$3,500"): 5f4596d8 read its "$3,500" as US dollars. */
const DRAFT_PREFIXED_DOLLAR = new RegExp(`(?<![A-Za-z0-9])([A-Za-z]{1,3})\\$\\s?${EXTRA_AMOUNT}`, "g");
/** A currency code before "$" ("CAD $3,500"): 5f4596d8 read "$3,500" as US dollars. */
const DRAFT_CODE_DOLLAR = new RegExp(`\\b(cad|aud|nzd|hkd|sgd|mxn)\\s+\\$\\s?${EXTRA_AMOUNT}`, "gi");
/** The dollar a prefix names; "peso" as `FOREIGN_UNIT` names "mxn". "US" is US dollars. */
const DRAFT_DOLLAR_CODE: Readonly<Record<string, string>> = {
  cad: "cad", ca: "cad", c: "cad", aud: "aud", au: "aud", a: "aud", nzd: "nzd", nz: "nzd",
  hkd: "hkd", hk: "hkd", sgd: "sgd", sg: "sgd", s: "sgd", mxn: "peso", mx: "peso",
};
/** A range after a marked amount ("C$3,500-4,000"): its far end has no mark of its own, so the pair is refused whole. */
const DRAFT_RANGE_AFTER = /^\s*(?:-|to|or|through|thru)\s*\$?\s?\d[\d,.]*\s?[a-z]*/i;
/** A currency sign other than "$" before an amount ("\u20ac3,500", "\u00a3950") or after one ("3.5k\u20ac", "3,500 \u20ac"). */
const DRAFT_SIGN_BEFORE = new RegExp(`(?<![\\w.,])([^\\P{Sc}$])\\s?${EXTRA_AMOUNT}`, "giu");
const DRAFT_SIGN_AFTER = new RegExp(`(?<![\\w.,$])(${DIGITS})(?:\\s?(${EXTRA_SCALE})\\b)?\\s?([^\\P{Sc}$])`, "giu");
/** Rupees written "Rs" before an amount ("Rs 3,500", "Rs. 3.5k"). */
const DRAFT_RUPEE_BEFORE = new RegExp(`\\brs\\.?\\s?${EXTRA_AMOUNT}`, "gi");
/** A nation before a currency word ("Canadian dollars", "swiss francs"), or alone after a scaled amount ("3.5k Canadian"). */
const DRAFT_NATION =
  "(?:us|u\\.s\\.|american|swiss|canadian|australian|aussie|new\\s+zealand|hong\\s+kong|singapore(?:an)?|mexican|indian|british|japanese|chinese|euro?pean|uk)";
const DRAFT_NATION_DOLLAR: Readonly<Record<string, string>> = {
  us: "usd", "u.s.": "usd", american: "usd", canadian: "cad", australian: "aud", aussie: "aud", "new zealand": "nzd",
  "hong kong": "hkd", singapore: "sgd", singaporean: "sgd", mexican: "peso",
};
/**
 * A currency named after an amount in a way `WIDE_FOREIGN` does not read:
 * with "in" or a bracket ("3,500 in euros", "3.5k (EUR)"), with a nation
 * ("3,500 Canadian dollars", "3.5k swiss francs"), with a scale ("3k
 * euros"), or "CAD" after a plain amount of 100 or more ("3500 CAD").
 */
const DRAFT_NAMED_CURRENCY = new RegExp(
  `${QTY_START}(${DIGITS}|${SPELLED})(?:\\s?(${EXTRA_SCALE})\\b)?\\s*(?:,\\s*)?(?:(in)\\s+|(\\()\\s*(?:in\\s+)?)?` +
    `(?:(${DRAFT_NATION})\\s+)?(${FOREIGN_CURRENCY}|dollars?|bucks|quid)\\b(?!\\s+${CAD_NOUN}\\b)`,
  "gi",
);
/** "3.5k CAD renders": a price for renders or a count of them. 5f4596d8 read "$3,500"; refused. */
const DRAFT_SCALED_CAD_NOUN = new RegExp(
  `${QTY_START}${DIGITS}\\s?${EXTRA_SCALE}\\b\\s*(?:in\\s+|\\(\\s*)?cad\\s+${CAD_NOUN}\\b`,
  "gi",
);
/**
 * "3,500 Swiss", "1200 Canadian.": a nation alone after a plain amount of 100
 * or more that ends the clause. A smaller count ("18 Canadian") and a nation
 * before a noun ("2023 Canadian buyers") are left alone.
 */
const DRAFT_PLAIN_NATION = new RegExp(
  `${QTY_START}(\\d{1,3}(?:,\\d{3})+(?:\\.\\d+)?|\\d{3,}(?:\\.\\d+)?)\\s*(?:\\(\\s*)?(?:in\\s+)?(${DRAFT_NATION})\\b` +
    "(?=\\s*\\)?\\s*(?:$|[.,;:!?]))",
  "gi",
);
const DRAFT_SCALED_NATION = new RegExp(
  `${QTY_START}(${DIGITS}|${SPELLED})\\s?(${EXTRA_SCALE})\\b\\s*(?:,\\s*)?(?:\\(\\s*)?(?:in\\s+)?(${DRAFT_NATION})\\b` +
    `(?!\\s+(?:${FOREIGN_CURRENCY}|dollars?|bucks)\\b)`,
  "gi",
);
/**
 * "the bracelet is 4 thousand": a magnitude with no currency. As dollars it
 * may be a price no fact states, and it may be a count, so it is refused,
 * never read. "a million" is left out ("thanks a million"); "4 thousand
 * dollars", "$4 thousand" and "2.4 thousand" are read elsewhere.
 */
const DRAFT_BARE_MAGNITUDE = new RegExp(
  `(?<![\\w.,$])(?<!\\$\\s)(?:(?:\\d+[\\s-]*|(?:${SUB1000}|a|hundred)[\\s-]+)thousand|(?:\\d+[\\s-]*|(?:${SUB1000}|hundred)[\\s-]+)(?:million|billion))` +
    "\\b(?!\\s*(?:dollars?|bucks)\\b)",
  "gi",
);
const DRAFT_VAGUE_MAGNITUDE = /\b(?:a\s+(?:couple(?:\s+of)?|few)|several|few)\s+(?:thousand|million)\b(?!\s*(?:dollars?|bucks)\b)/gi;
/** Cheap tests that skip a reader when its text cannot occur ("10%, " 4,000 times reads none of them). */
const DRAFT_HAS_HALF = /half/i;
const DRAFT_HAS_CARAT = /carat/i;
const DRAFT_HAS_PREFIX = /[a-z]\s?\$/i;
const DRAFT_HAS_SIGN = /[^\P{Sc}$]/u;
const DRAFT_HAS_RS = /\brs/i;
const DRAFT_HAS_CURRENCY_WORD = new RegExp(`(?:${FOREIGN_CURRENCY}|dollars?|bucks|quid|${DRAFT_NATION})\\b`, "i");
const DRAFT_HAS_MAGNITUDE = /thousand|million|billion/i;

/*
 * TA-1376, DRAFT SIDE ONLY, as the readers above: TA-1375's "Left open" item
 * 1. Each runs in `draftExtras` after every TA-1375 reader and adds a read or
 * a refusal, never one in place of another, so a draft keeps every read and
 * refusal 9f293ea2 gave it.
 */
/** "C $3,500", "Cdn $3,500": a prefix, a space, then "$". The prefix is matched by exact case (`DRAFT_SPACED_CODE`). */
const DRAFT_SPACED_PREFIX = new RegExp(`(?<![A-Za-z0-9])([A-Za-z]{1,3}) \\$\\s?${EXTRA_AMOUNT}`, "gi");
/**
 * Single letters only in capitals ("C $3,500"): "a $3,500 ring" and "it's
 * $3,500" are prose, which is also why "A" and "S" are left out. "CAD $" and
 * the other codes are `DRAFT_CODE_DOLLAR`'s.
 */
const DRAFT_SPACED_CODE: Readonly<Record<string, string>> = {
  C: "cad", CA: "cad", CDN: "cad", Cdn: "cad", cdn: "cad", AU: "aud", Aus: "aud", AUS: "aud", NZ: "nzd", HK: "hkd", SG: "sgd", MX: "peso",
};
/** A dollar this module does not name, before a space and "$": refused, as "Can$" is. */
const DRAFT_SPACED_UNNAMED: ReadonlySet<string> = new Set(["Can", "CAN", "NT"]);
/** `DRAFT_PREFIXED_DOLLAR` with its scale read in any case: "C$3.5K" is 3500cad, where that reader read 3.5cad. */
const DRAFT_PREFIXED_DOLLAR_ANY_SCALE = new RegExp(DRAFT_PREFIXED_DOLLAR.source, "gi");
/** A dollar named after the dollar word ("3,500 dollars Canadian", "3,500 bucks CAD", "3,500 dollars (CAD)"). */
const DRAFT_DOLLARS_NATION_AFTER = new RegExp(
  `${QTY_START}(${DIGITS}|${SPELLED})(?:\\s?(${EXTRA_SCALE})\\b)?\\s*(?:dollars?|bucks)\\s*,?\\s*(?:\\(\\s*)?(?:in\\s+)?` +
    `(${DRAFT_NATION}|cad|aud|nzd|hkd|sgd|mxn|usd|cdn|aus|hk|nz|au|sg)\\b(?!\\s+${CAD_NOUN}\\b)`,
  "gi",
);
/** A dollar's short name or code before the dollar word ("3,500 HK dollars", "3,500 CAD dollars"). */
const DRAFT_CODE_BEFORE_DOLLARS = new RegExp(
  `${QTY_START}(${DIGITS}|${SPELLED})(?:\\s?(${EXTRA_SCALE})\\b)?\\s*(?:in\\s+|\\(\\s*(?:in\\s+)?)?` +
    "(hk|nz|au|aus|cdn|can|sg|cad|aud|nzd|hkd|sgd|mxn)\\s?(?:dollars?|bucks)\\b",
  "gi",
);
const DRAFT_COMMA_BEFORE_NATION = /(?:dollars?|bucks)\s*,/i;
const DRAFT_CLAUSE_END = /^\s*\)?\s*(?:$|[.,;:!?])/;
/** A dollar named after "dollars" that `DRAFT_AND_HALF_SCALE` read ("three and a half thousand dollars, CAD"). */
const DRAFT_NATION_TAIL = new RegExp(
  `^\\s*,?\\s*(?:\\(\\s*)?(?:in\\s+)?(${DRAFT_NATION}|cad|aud|nzd|hkd|sgd|mxn|usd|cdn|aus|hk|nz|au|sg)\\b(?!\\s+${CAD_NOUN}\\b)`,
  "i",
);
const DRAFT_DOLLAR_NAME: Readonly<Record<string, string>> = {
  cad: "cad", cdn: "cad", aud: "aud", aus: "aud", au: "aud", nzd: "nzd", nz: "nzd", hkd: "hkd", hk: "hkd", sgd: "sgd",
  sg: "sgd", mxn: "peso", usd: "usd",
};
/**
 * "3,500 sterling": pounds. Never a silver fineness ("925 sterling", "950
 * sterling silver") and never "sterling" naming a piece ("1,000 sterling
 * rings"): read only for a grouped, scaled or four-digit amount, and only
 * where "sterling" ends the clause or a connective follows it.
 */
const DRAFT_STERLING = new RegExp(
  `${QTY_START}(${DIGITS})(?:\\s?(${EXTRA_SCALE})\\b)?\\s*(?:(?:pounds?|quid)\\s+)?(?:in\\s+)?sterling\\b` +
    "(?=\\s*(?:$|[.,;:!?)])|\\s+(?:for|each|per|total|and|or|plus|only)\\b)",
  "gi",
);
/** Swiss francs written "Fr." or "SFr." before an amount. Lower-case "fr" is "from", so the prefix is matched by exact case. */
const DRAFT_FRANC_BEFORE = new RegExp(`(?<![A-Za-z0-9])(s?fr)\\.?\\s?${EXTRA_AMOUNT}`, "gi");
const DRAFT_FRANC_PREFIX: ReadonlySet<string> = new Set(["Fr", "FR", "SFr", "sFr", "SFR", "Sfr"]);
/** Korean won after a scaled or grouped amount ("3.5k won", "3,500 won", "3,500 Korean won"); "2019 won awards" is a verb. */
const DRAFT_WON = new RegExp(`${QTY_START}(${DIGITS})(?:\\s?(${EXTRA_SCALE})\\b)?\\s*((?:south\\s+)?korean\\s+)?won\\b(?!['\u2019]t\\b)`, "gi");
/** "three and a half thousand euros": (N + 0.5) times the scale, in the currency after it, refused where none follows. */
const DRAFT_AND_HALF_SCALE = new RegExp(`${QTY_START}(${WHOLE})[\\s-]+and[\\s-]+a[\\s-]+half[\\s-]+(thousand|million|billion|grand)\\b`, "gi");
const DRAFT_CURRENCY_AFTER = new RegExp(
  `^\\s*(?:in\\s+|\\(\\s*(?:in\\s+)?)?(?:(${DRAFT_NATION})\\s+)?(${FOREIGN_CURRENCY}|dollars?|bucks|quid|sterling)\\b(?!\\s+${CAD_NOUN}\\b)`,
  "i",
);
/** "35 hundred", "4 hundred": a magnitude with no currency, refused as "4 thousand" is. "35 hundred dollars" is read elsewhere. */
const DRAFT_BARE_HUNDRED = /(?<![\w.,$])(?<!\$\s)\d+[\s-]*hundred\b(?!\s*(?:dollars?|bucks)\b)/gi;
/** "4 mil": a million, or a plating thickness. Read as dollars after "$" or before "dollars", refused otherwise. */
const DRAFT_MIL = new RegExp(`(?<![\\w.,])(\\$\\s?)?(${DIGITS}|${SPELLED})[\\s-]?mils?\\b(\\s*(?:dollars?|bucks)\\b)?`, "gi");
/** "4 lakh", "2 crore": refused, whatever follows. */
const DRAFT_LAKH = new RegExp(`(?<![\\w.,])(?:${DIGITS}|${SPELLED})[\\s-]?(?:lakhs?|crores?)\\b`, "gi");
/** "tens of thousands", "a few grand": an amount with no number, refused as "a few thousand" is. "a couple grand" is $2,000. */
const DRAFT_VAGUE_MANY = /\b(?:tens|hundreds|dozens)\s+of\s+(?:thousands|millions|billions)\b|\b(?:a\s+few|several|few)\s+grand\b/gi;
/** "3 days, and a half", "3 days & a half", "3 business days and a half", "3 minutes and a half": N + 0.5 of the unit. */
const DRAFT_DURATION_HALF_JOINED = new RegExp(
  `${QTY_START}(an?|${WHOLE})\\s+((?:business|working|work)[\\s-]+)?(days?|weeks?|wks?|months?|mos?|hours?|hrs?|minutes?|mins?|years?|yrs?)\\b` +
    "\\s*(?:,\\s*)?(?:and|&)\\s+(?:a\\s+)?half\\b",
  "gi",
);
/**
 * "two & a half years", "two-and-a-half-years", "2 & half year": the whole and
 * the half together. 9f293ea2's draft read only the "a half year" in them, so
 * a fact that states a half year alone (0.5y, "6 months") must not ground them.
 */
const DRAFT_HALF_JOINED_BEFORE_UNIT = new RegExp(
  `(?:(?<![\\w.,/])(\\d+)|(?<![A-Za-z])(${SPELLED}))(?:[\\s-]*[&+][\\s-]*|[\\s-]+(?:and|plus|n)[\\s-]+)` +
    "(?:(?:an?|one)[\\s-]+)?half[\\s-]+(?:an?[\\s-]+)?" +
    "((?:business|working|work)[\\s-]+)?(days?|weeks?|wks?|months?|mos?|hours?|hrs?|minutes?|mins?|years?|yrs?)\\b",
  "gi",
);
/**
 * TA-1376 fix round: a half after a whole this module does not read. At
 * 9f293ea2 "a dozen and a half years", "several & a half years" and "3 half
 * years" read only their "a half year" (0.5y), so a fact that states a half
 * year alone (0.5y) would ground them. Each is refused, and read as
 * written, a figure no fact reads, so the send-time recheck (which reads
 * figures, not refusals) holds it too. Never a guess at the whole.
 */
const DRAFT_HALF_TAIL =
  "half[\\s-]+(?:an?[\\s-]+)?(?:(?:business|working|work)[\\s-]+)?(?:days?|weeks?|wks?|months?|mos?|hours?|hrs?|minutes?|mins?|years?|yrs?)\\b";
/** A whole that names no number: "a dozen", "a couple", "several", "a hundred", then an optional joiner. */
const DRAFT_HALF_VAGUE_WHOLE = new RegExp(
  "\\b(?:(?:an?[\\s-]+)?(?:dozens?|couple|pair|score|decades?|century|centuries|few|several|many|handful|bunch|gross|some|umpteen)|" +
    "an?[\\s-]+(?:hundred|thousand|million))\\b[\\s-]*(?:(?:and|plus|n)\\b[\\s-]*|[&+][\\s-]*)?(?:(?:an?|one)[\\s-]+)?" +
    DRAFT_HALF_TAIL,
  "gi",
);
/** A number straight before "half" with no joiner ("3 half years", "two a half years"); "one half year" is half a year and is left alone. */
const DRAFT_HALF_BARE_WHOLE = new RegExp(
  `(?:(?<![\\w.,/])(\\d+(?:\\.\\d+)?)|(?<![A-Za-z])(${SPELLED}))[\\s-]+(an?[\\s-]+)?${DRAFT_HALF_TAIL}`,
  "gi",
);
/**
 * "between 1 and 0.5 year", "one and 0.5 years", "one and 1.5 year": the whole
 * number before a decimal is read too. "between 1 and 1.5 years" and "from 2
 * and 2.5 weeks" are ranges, left as 9f293ea2 read them (group 1).
 */
const DRAFT_WHOLE_AND_DECIMAL = new RegExp(
  `(?:\\b(between|from)[\\s-]+)?(?:(?<![\\w.,/])(\\d+)(?![.,]\\d)|(?<![A-Za-z])(${SPELLED}))[\\s-]*(?:&|and)[\\s-]*` +
    "(\\d*\\.\\d+)[\\s-]*((?:business|working|work)[\\s-]+)?(days?|weeks?|wks?|months?|mos?|hours?|hrs?|minutes?|mins?|years?|yrs?)\\b",
  "gi",
);
const DRAFT_HAS_SPACED_DOLLAR = / \$/;
const DRAFT_HAS_TAIL_CURRENCY = /dollars?|bucks|sterling|won\b/i;
const DRAFT_HAS_FR = /fr/i;
const DRAFT_HAS_AND_HALF = /and[\s-]+a[\s-]+half/i;
const DRAFT_HAS_HUNDRED = /hundred/i;
const DRAFT_HAS_MIL = /mil/i;
const DRAFT_HAS_LAKH = /lakh|crore/i;
const DRAFT_HAS_VAGUE = /thousands|millions|billions|grand/i;
const DRAFT_HAS_AND_DECIMAL = /(?:&|and)[\s-]*\d*\./i;
/*
 * TA-1376 round 3, draft side only: a whole joined to a half through any run
 * of punctuation, symbols and joiner words, not only the clean "and" the
 * readers above take ("two and, a half years", "a dozen and... a half years",
 * "3 days (and a half)"). A run of at most 16 characters or words, each a
 * white space, a punctuation mark, a symbol or a joiner word, so the pattern
 * stays linear. A joiner word may be glued to what is around it ("1plus a
 * half year", "2 timeshalf year"): the probe found those read as a bare half.
 * Fix round 2: "or" and the apostrophe too ("3 days and/or a half", "3 days
 * 'n' a half").
 */
const DRAFT_JOIN_RUN =
  `(?:[\\s,;:()\\[\\]&+*/.'${DASH}${String.fromCharCode(0xd7)}${String.fromCharCode(0x2026)}]|and|or|plus|n|x|times|twice){1,16}`;
const DRAFT_HALF_UNIT = "(days?|weeks?|wks?|months?|mos?|hours?|hrs?|minutes?|mins?|years?|yrs?)";
/** A whole this module does not read as a number: "a dozen", "a couple", "several". */
const DRAFT_VAGUE_WHOLE_WORDS =
  "(?:an?[\\s-]+)?(?:dozens?|couple|pair|score|decades?|century|centuries|few|several|many|handful|bunch|gross|some|umpteen)|" +
  "an?[\\s-]+(?:hundred|thousand|million)";
/** A whole, a joining run, then "half a <unit>", "a half <unit>" or "one half <unit>". */
const DRAFT_WHOLE_JOIN_HALF = new RegExp(
  `(?:(?<![\\w.,/])(\\d+(?:\\.\\d+)?)|(?<![A-Za-z])(${SPELLED})|\\b(${DRAFT_VAGUE_WHOLE_WORDS})\\b)(${DRAFT_JOIN_RUN})` +
    `(?:(?:an?|one)[\\s-]+)?half[\\s-]+(?:an?[\\s-]+)?((?:business|working|work)[\\s-]+)?${DRAFT_HALF_UNIT}\\b`,
  "gi",
);
/** The joins the readers above already read whole: " and ", " & ", " + ", " plus ", " n ", "-and-". */
const DRAFT_CLEAN_JOIN = /^(?:\s+(?:and|plus|n)\s+|\s*[&+]\s*|-(?:and|n)-)$/i;
/**
 * A unit, a joining run, then a half: "3 days &a half", "3 days (and a half)", "3 days and one half".
 * TA-1376 round 4: the half ends a clause (the end of the text, a line break, a punctuation mark or a
 * dash), so "3 days - half the usual time" is a half of something else and reads as at 9f293ea2.
 */
const DRAFT_UNIT_THEN_HALF = new RegExp(
  `${QTY_START}(an?|${WHOLE})\\s+((?:business|working|work)[\\s-]+)?${DRAFT_HALF_UNIT}\\b(${DRAFT_JOIN_RUN})(?:(?:an?|one)[\\s-]+)?half` +
    `(?=[ \\t]*(?:$|[\\n\\r.,;:!?)\\]"${DASH}]))`,
  "gi",
);
/** A run that joins: a joiner word, "&", "+" or a dash, not only spaces and commas. */
const DRAFT_JOINS = new RegExp(`\\b(?:and|plus|n|x|times|twice)\\b|[&+${DASH}]`, "i");
/** A prefixed dollar after its amount ("3,500 HK$", "3.5k C$"): 9f293ea2 read nothing in it. */
const DRAFT_AMOUNT_THEN_PREFIX = new RegExp(`(?<![\\w.,$])${EXTRA_AMOUNT}\\s?([A-Za-z]{1,3})\\$`, "g");
/** TA-1378, cost: where `DRAFT_AMOUNT_THEN_PREFIX` can start, and its run of space groups (`matchAllPastGroups`). */
const DRAFT_AMOUNT_THEN_PREFIX_SCAN: GroupedScan = {
  sticky: new RegExp(DRAFT_AMOUNT_THEN_PREFIX.source, "y"),
  start: /(?<![\w.,$])\d/g,
  run: /\d{1,3}(?: \d{3})+/y,
};
const DRAFT_HAS_DOLLAR_SIGN = /\$/;

/*
 * TA-1377, DRAFT SIDE ONLY, as the readers above: three misreads f1743de4
 * let through. Each runs in `draftExtras1377`, after every TA-1376 reader,
 * and adds a read or a refusal, never one in place of another, so a draft
 * keeps every read and refusal f1743de4 gave it. A fact reads exactly what it
 * read at f1743de4.
 */
/**
 * A "$" amount with a currency named after it ("$65 CAD", "$65 Cdn", "$65
 * (Canadian)", "$1,200 Aussie", "$65-80 CAD"). f1743de4 read "$65" alone where
 * the amount was under 100 with no comma, or the name was "Cdn" or a nation
 * word, so a US $65 fact grounded it. The "$" makes it money, so no count
 * guard applies. Group 7 is a currency word or code read anywhere else in this
 * module; group 8 a nation (`DRAFT_NATION`) or a short dollar name
 * (`DRAFT_DOLLAR_NAME`), read only after "in", in a bracket, or where it ends
 * the clause, a connective or the dollar word follows it ("$65 Canadian for
 * the band"). Anywhere else ("$65 Canadian with tax", a line break the fold
 * made a space, "the $650 Australian opal ring") a nation with a dollar of
 * its own may name the currency or the piece, so the amount is refused (B13,
 * review round 1). The range join takes any dash, spaced or not ("$65 - 80
 * CAD"), and the CAD noun guard ("$65 CAD renders") is the code's only.
 */
const DRAFT_DOLLAR_THEN_NAMED = new RegExp(
  `\\$\\s?${EXTRA_AMOUNT}(?:(?:\\s*[${DASH}]\\s*|\\s+(?:to|through|thru|or)\\s+)\\$?\\s?(${DIGITS})(?:\\s?(${EXTRA_SCALE})\\b)?)?` +
    "\\s*(?:,\\s*)?(?:(in)\\s+|(\\()\\s*(?:in\\s+)?)?" +
    `(?:(${FOREIGN_CURRENCY}|cdn)\\b(?!\\s+${CAD_NOUN}\\b)|(${DRAFT_NATION}|aus|au|nz|hk|sg|mx|ca|can)\\b)`,
  "gi",
);
/**
 * Review round 2: "CAN" after a "$" amount ("$65 CAN") names a dollar this
 * module does not price (`DRAFT_SPACED_UNNAMED`), so the amount is refused,
 * and read as Canadian dollars for the send-time recheck. Matched by exact
 * case: "$65 can be paid" is a verb.
 */
const DRAFT_UNNAMED_AFTER: ReadonlySet<string> = new Set(["CAN"]);
/**
 * What may follow a nation or a short dollar name for it to name the
 * currency: a clause end (`DRAFT_CLAUSE_END`) or a connective
 * (`DRAFT_STERLING`'s). Both sticky, tried where the match ends, so no tail
 * of the text is copied per match; the clause end takes one white-space run
 * before the bracket, so a long run is scanned once.
 */
const DRAFT_NAMED_CONNECTIVE = /\s+(?:for|each|per|total|and|or|plus|only)\b/iy;
const DRAFT_CLAUSE_END_AT = /\s*(?:\)\s*)?(?:$|[.,;:!?])/y;
/** The dollar word after a nation ("$65 Canadian dollars"): the nation names the currency. */
const DRAFT_NAMED_DOLLARS_AT = /\s*(?:dollars?|bucks)\b/iy;
/**
 * A percent joined to its number, or its mark split, by a hyphen or any dash
 * (`DASH`): "20-percent", "twenty-percent", "20-per-cent", "20-pct", "20"
 * U+2011 "percent", "20 per-cent". f1743de4 read nothing in them (`PERCENT` and
 * `WIDE_PERCENT` join with white space only), so a wrong deposit percent
 * passed. Read as `WIDE_PERCENT` reads "20 percent"; a match with no dash and
 * no plural mark is `WIDE_PERCENT`'s and is skipped here. Review round 1: a
 * range joined by a dashed word ("20-to-30-percent") is read whole, as
 * "20 to 30 percent" is, never as its "30-percent" tail, and a plural mark
 * ("20-percents") is read.
 */
/**
 * Review round 2: `DASH` and the fullwidth and small hyphens (U+FF0D, U+FE63)
 * and the small em dash (U+FE58), for the TA-1377 readers only. `DASH` itself
 * is shared with the fact side and stays as it is.
 */
const DRAFT_WIDE_DASH = `${DASH}${String.fromCharCode(0xfe58, 0xfe63, 0xff0d)}`;
/**
 * Review round 2: the join of a percent list or range is a word ("to", "or",
 * "and", "through", "thru", "&") between white space or dashes, or a dash.
 * Group 2 of `DRAFT_JOINED_PERCENT` is the word. "20 and 30 percent" and
 * "20-and-30-percent" are two percents; "20-through-30-percent" is a range,
 * read whole as "20 to 30 percent" is. f1743de4 read the "30 percent" alone.
 */
const DRAFT_PERCENT_RANGE_JOIN =
  `(?:[\\s${DRAFT_WIDE_DASH}]+(to|or|and|through|thru|&)[\\s${DRAFT_WIDE_DASH}]+|\\s*[${DRAFT_WIDE_DASH}]\\s*)`;
const DRAFT_JOINED_PERCENT = new RegExp(
  `${QTY_START}(${QTY})(?:${DRAFT_PERCENT_RANGE_JOIN}(${QTY}))?([\\s${DRAFT_WIDE_DASH}]*)` +
    `(%|per[\\s${DRAFT_WIDE_DASH}]{0,3}cents?\\b|pct\\b)`,
  "gi",
);
const DRAFT_PLURAL_PERCENT = /s$/i;
/** A join `WIDE_PERCENT` does not read, so a spaced match is read here too. */
const DRAFT_PERCENT_LIST_WORD: ReadonlySet<string> = new Set(["and", "through", "thru", "&"]);
const DRAFT_PERCENT_SPLIT_WORD: ReadonlySet<string> = new Set(["and", "&"]);
/** A dash in a run of white space and dashes that a percent mark ends. Tried at the start of a run only, so a long run is scanned once. */
const DRAFT_HAS_JOINED_PERCENT = new RegExp(
  `(?<![\\s${DRAFT_WIDE_DASH}])\\s*[${DRAFT_WIDE_DASH}][\\s${DRAFT_WIDE_DASH}]*(?:%|per|pct)|per[\\s${DRAFT_WIDE_DASH}]{0,3}cent`,
  "i",
);
/** A list word and a "%" anywhere: "20 and 30%" has no dash, so `DRAFT_HAS_JOINED_PERCENT` skips it. Two tests, each one scan. */
const DRAFT_HAS_LIST_WORD = /\b(?:and|through|thru)\b|&/i;
const DRAFT_HAS_PERCENT_SIGN = /%/;
const DRAFT_HAS_DASH = new RegExp(`[${DRAFT_WIDE_DASH}]`);
/**
 * Review round 2: a spelled hundred joined to its percent by a dash ("one-
 * hundred-percent", "a hundred-percent", "one-hundred percent"). f1743de4
 * read "one hundred percent" as 100% and none of these. A match with no dash
 * is skipped where it is read. Group 1 is the multiplier.
 */
const DRAFT_HUNDRED_PERCENT = new RegExp(
  `(?<![\\w.,/${DRAFT_WIDE_DASH}])(a|${UNIT_WORD})[\\s${DRAFT_WIDE_DASH}]+hundred[\\s${DRAFT_WIDE_DASH}]*` +
    `(?:%|per[\\s${DRAFT_WIDE_DASH}]{0,3}cents?\\b|pct\\b)`,
  "gi",
);
/**
 * One more size in a size list after a `SIZE` read ("size 6 or 7", "sizes 6,
 * 7 and 8", "size 6/7", "size 6 through 9"). f1743de4 read the first size
 * alone, so a fact stating size 6 grounded sizes 7, 8 and 9. Matched sticky,
 * item after item. Group 1 is the join, group 2 the size. Never a number that
 * runs on ("7,500") or that a unit, a percent or a karat follows ("size 7 and
 * 3 days", "size 7 and 14k gold"): those are other figures, read by their own
 * readers.
 *
 * Review round 1: the join is a class, not a list of phrases. It is a mark
 * (comma, slash, "&", "+" or any dash, `DASH`) and up to four join words, or
 * one to four join words (`DRAFT_SIZE_JOIN_WORD`), so "and also", "or even a",
 * "vs", "and up to", "through to" and a U+2010 range all read. A join with a
 * range word or a dash in it (`DRAFT_SIZE_RANGE_WORD`) is a range from the
 * size before it; any other join is one more size.
 */
const DRAFT_SIZE_JOIN_WORD =
  "(?:and|or|nor|either|else|n|plus|vs\\.?|versus|also|even|maybe|perhaps|possibly|then|up\\s+to|until|till|through|thru|to|an?|as|well|but)";
/**
 * Review round 2: the join is a run of one to eight tokens, each a mark or a
 * join word, so "and/or", "(or", "as well as" and "but also" read, and a mark
 * may sit flush against the size ("sizes 6,7,8"). A word follows white space
 * or a mark, never a digit. The mark class takes a bracket and "~" too. An
 * item stops only at a real decimal or thousands group ("6.5", "7,500"),
 * never at the next item of a bare comma list ("6,7,8"), which round 2 did.
 * A "#" may lead the size ("#6 or #7"). An opening bracket joins only where
 * a join word or a lone bracketed number follows it ("size 6 (or 7)", "size 6
 * (7)"), so "size 7 (2 in stock)" reads no size 2.
 *
 * Fix round 2: the list separators a size list is written with, not only the
 * comma: a semicolon, a bar, a square bracket, a middle dot, a bullet and an
 * ellipsis ("sizes 6;7;8", "size 6 | 7", "size 6 [7]", "size 6...7"), and
 * the join words "n", "nor", "either" and "else". A colon, a full stop, "!"
 * and "?" end a clause and stay out, so "size 6: 2 left" reads no size 2.
 * An ellipsis is one mark however long it runs (`(?!\.)`), so a run of dots
 * is never split between the tokens of a join and the item stays linear.
 */
const DRAFT_SIZE_MARK =
  `(?:[,/&+~);|\\]${String.fromCharCode(0xb7, 0x2022, 0x2026)}${DRAFT_WIDE_DASH}]|\\.{2,}(?!\\.))`;
const DRAFT_SIZE_OPEN = `[([](?=\\s*(?:${DRAFT_SIZE_JOIN_WORD}(?![A-Za-z])|#?\\s?\\d+(?:\\.\\d+)?\\s*[)\\]]))`;
const DRAFT_SIZE_LIST_ITEM = new RegExp(
  `((?:\\s*(?:${DRAFT_SIZE_MARK}|${DRAFT_SIZE_OPEN})|(?:\\s+|(?<=[([]|${DRAFT_SIZE_MARK}))${DRAFT_SIZE_JOIN_WORD}(?![A-Za-z])){1,8}\\s*)` +
    "(?:#\\s?)?" +
    `(${SIZE_NUM})(?!\\d|\\.\\d|,\\d{3}(?!\\d))(?!\\s*%)` +
    `(?!\\s*(?:${OTHER_UNIT_AFTER}|k|kt|karats?)\\b)`,
  "iy",
);
const DRAFT_SIZE_RANGE_WORD = new RegExp(`\\b(?:to|until|till|through|thru)\\b|[~${DRAFT_WIDE_DASH}]`, "i");
const DRAFT_HAS_SIZE = /\bs(?:ize|z)/i;
/**
 * A fraction other than the half after a size ("size 6 3/4", "size 6" then
 * U+00BE): `SIZE_NUM` reads the whole number alone, which a size 6 fact
 * grounds. The size is read as written instead, a figure no fact reads.
 */
const DRAFT_SIZE_FRACTION_TAIL = new RegExp(
  `^\\s*(\\d+\\/\\d+|[${String.fromCharCode(0xbc)}-${String.fromCharCode(0xbe)}${String.fromCharCode(0x2150)}-${String.fromCharCode(0x215e)}])`,
);
const DRAFT_SIZE_WORD_LEAD = new RegExp(`^${SIZE_WORD}\\s*`, "i");
/** The join inside a `SIZE` range. Tried at the start of a white-space run only, so a long run is scanned once. */
const DRAFT_SIZE_RANGE_JOIN = /(?<!\s)\s*(?:\bto\b|-)\s*/i;

/*
 * TA-1376 Part B, round 3: QUARANTINE, on the fact side only. At 9f293ea2 a
 * fact "1 1/2 years" read "2 years" (the "1/2 years" in it) and "half a year"
 * read "a year", so the fact grounded a draft "2 years" or "1 year". Rounds 1
 * and 2 read such a fact at its true value instead, and each review found a
 * draft that the true value released. A fact now never gains a read: each
 * year read 9f293ea2 makes from inside one of the listed forms is replaced, in
 * place, by a quarantine key (`quarantineKey`) that no draft read can equal.
 * It starts with U+2063, a format character `foldFigureText` strips from every
 * text it reads, so no text can spell it. The fact grounds neither the rounded
 * figure nor any true value, and a draft that states one is held for a person
 * (B13). Every other read, a money, size or date read reaching into the form
 * among them, is 9f293ea2's, byte for byte.
 *
 * The listed forms, exactly: "1 1/2", "1-1/2" or "1 1/2" with a vulgar half,
 * before "years", "year", "yrs" or "yr"; and "half a year", "a half year" or
 * "half year" in those spellings; each with any separator of the classes
 * below (`QUARANTINE_FORM`), plus round 4's further forms listed there.
 * "11/2 years" and "1 1/20 years" are other numbers, not listed, and read as
 * at 9f293ea2. The draft side reads every form exactly as 9f293ea2 did.
 */
const QUARANTINE_PREFIX = `${String.fromCharCode(0x2063)}misread:`;
const VULGAR_HALF = String.fromCharCode(0xbd);
/*
 * The round 3 reviews: the form is the same form whatever its separators, and
 * each separator is a class, never a list of spellings. The text is folded
 * first (`foldFigureText`), so a run of whitespace is one space and a zero
 * width or other format character is gone.
 *   - The slash in the half is "/" or a look-alike that reads the same (U+2044
 *     FRACTION SLASH, U+2215 DIVISION SLASH, U+FF0F FULLWIDTH SOLIDUS), with
 *     or without a space on either side ("1 1 / 2 years").
 *   - The whole and the half are joined by a space, or by any dash the module
 *     reads as one (`DASH`: hyphen, en, em, minus and the rest), spaced or not.
 *   - "half a" and "a half" are joined the same way ("half-a year").
 *   - The unit follows with nothing, a space, or a dash spaced or not
 *     ("1 1/2years", "a 1 1/2-year warranty", "1 1/2 - years").
 *   - Any left context is the form's except one that makes another number:
 *     a digit before the whole ("11 1/2" is read as the whole 11), a digit
 *     then a slash, period or comma ("3/1 1/2", "2.1 1/2", "1,1 1/2"), or a
 *     letter or digit before a word ("xhalf a year"). A slash after a word
 *     ("labor/1 1/2 years") or an underscore ("_half a year") is not a number,
 *     so the form there is the form (round 4).
 * Nothing on the right of the "2" but the unit, so "1 1/20 years" and
 * "1 1 / 22 years" are other numbers and read as at 9f293ea2.
 *
 * TA-1376 round 4 widens the list to the further year misreads round 3
 * found, each read at 9f293ea2 as a rounded or partial year inside the form:
 *   - any whole before the half, a number or a spelled one, joined by a
 *     space, a dash, "and", "&" or "+" ("2 1/2 years" read 2y, "one 1/2
 *     years" and "1 and 1/2 years" read 2y);
 *   - "half of a year", "half an year", "1/2 a year", a vulgar half then "a
 *     year" (each read "a year", 1y);
 *   - "1 year and a half", "one year and a half", "an year and a half", with
 *     "&" or "and" and "a" or "one" before the half (each read 1y from its
 *     head). "a year and a half" and "2 years and a half" are NOT listed:
 *     9f293ea2's own tests pin their 1y and 2y fact reads
 *     (figures.sides.test.ts, figures.drafts1375.test.ts). A half followed
 *     by a word, even across a dash ("a half-price resize", "half the labour
 *     cost", "half - a year"), is another claim, or another form, never this
 *     one;
 *   - a range whose low end is a whole and a half ("1 1/2 - 3 years" read
 *     2-3y, "1 1/2 to 2 years" 2-2y). In a range form only the range read is
 *     quarantined: its high end ("3 years") is what the fact states.
 * Months, weeks and days: TA-1377's `UNIT_HALF_FORM`, at the end of this file.
 */
const QUARANTINE_SLASH = `/${String.fromCharCode(0x2044)}${String.fromCharCode(0x2215)}${String.fromCharCode(0xff0f)}`;
/** Between two words or numbers of the form: a space, or a dash with an optional space on either side. */
const QUARANTINE_JOIN = `(?: ?[${DASH}] ?| )`;
const QUARANTINE_HALF_NUMBER = `1 ?[${QUARANTINE_SLASH}] ?2`;
const QUARANTINE_UNIT = `(?: ?[${DASH}] ?| ?)(?:years?|yrs?)(?!\\w)`;
/** Not inside another number: no digit before, and no digit then a slash, period or comma. */
const QUARANTINE_LEFT_NUMBER = `(?<!\\d)(?<!\\d[${QUARANTINE_SLASH}.,])`;
/** Not inside another word or number. */
const QUARANTINE_LEFT_WORD = "(?<![A-Za-z0-9])";
const QUARANTINE_WHOLE = `(?:${QUARANTINE_LEFT_NUMBER}\\d+|${QUARANTINE_LEFT_WORD}(?:${UNIT_WORD}|${TEEN_WORD}|${TENS_WORD}))`;
const QUARANTINE_WHOLE_JOIN = `(?:${QUARANTINE_JOIN}| and | ?[&+] ?)`;
const QUARANTINE_RANGE_JOIN = `(?: ?[${DASH}] ?| to )`;
const QUARANTINE_FORM = new RegExp(
  `${QUARANTINE_WHOLE}(?:${QUARANTINE_WHOLE_JOIN}${QUARANTINE_HALF_NUMBER}| ?${VULGAR_HALF})` +
    `(?:${QUARANTINE_UNIT}|(?<range>${QUARANTINE_RANGE_JOIN}\\d+(?:\\.\\d+)?${QUARANTINE_UNIT}))` +
    `|${QUARANTINE_LEFT_WORD}(?:half${QUARANTINE_JOIN}a|a${QUARANTINE_JOIN}half|half)${QUARANTINE_UNIT}` +
    `|(?:${QUARANTINE_LEFT_WORD}half${QUARANTINE_JOIN}(?:of${QUARANTINE_JOIN}(?:an?|one|1)|an)` +
    `|(?:${QUARANTINE_LEFT_NUMBER}${QUARANTINE_HALF_NUMBER}|${VULGAR_HALF})${QUARANTINE_JOIN}(?:of${QUARANTINE_JOIN})?(?:an?|one|1))${QUARANTINE_UNIT}` +
    `|(?:${QUARANTINE_LEFT_NUMBER}1|${QUARANTINE_LEFT_WORD}(?:one|an))${QUARANTINE_JOIN}(?:years?|yrs?)` +
    `(?:,? and |,? ?& ?)(?:(?:a|one)${QUARANTINE_JOIN})?half(?!(?:${QUARANTINE_JOIN})?[A-Za-z0-9])`,
  "gi",
);
const QUARANTINE_GATE = new RegExp(`half|${QUARANTINE_HALF_NUMBER}|${VULGAR_HALF}`, "i");
/** A year read as `normalizeVerifierFigure` writes it ("2y", "1-2y"): the only reads a form puts in quarantine. */
const QUARANTINE_YEAR_READ = /^\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?y$/;
/** A year range read ("2-3y"): the only read a range form puts in quarantine. */
const QUARANTINE_RANGE_READ = /^\d+(?:\.\d+)?-\d+(?:\.\d+)?y$/;

/** The key a misread year takes inside a form: the misread and the form, so two forms never share one. */
function quarantineKey(baseKey: string, form: string): string {
  return `${QUARANTINE_PREFIX}${baseKey}:${form.toLowerCase().replace(/ /g, "_")}`;
}

/** Is this normalized figure a quarantine key? Only a fact read makes one, and it grounds nothing. */
export function isUnclearFigure(figure: string): boolean {
  return figure.startsWith(QUARANTINE_PREFIX);
}

/** The read 9f293ea2 made where this key stands ("2y"), or the figure itself where it is not a key. */
function quarantinedRead(figure: string): string {
  if (!isUnclearFigure(figure)) return figure;
  const rest = figure.slice(QUARANTINE_PREFIX.length);
  return rest.slice(0, rest.indexOf(":"));
}

/**
 * A figure for a person to read: a quarantine key as "unclear figure: 1 1/2
 * years", the form as written, never the misread number. Any other figure is
 * returned as it is.
 */
export function describeVerifierFigure(figure: string): string {
  if (!isUnclearFigure(figure)) return figure;
  const rest = figure.slice(QUARANTINE_PREFIX.length);
  return `unclear figure: ${rest.slice(rest.indexOf(":") + 1).replace(/_/g, " ")}`;
}

/**
 * TA-1376 round 3, F-08: do two KB facts differ on a quarantine key? True
 * where one fact's figure set holds a key the other's does not. A contention
 * that touches one cannot be settled by date: which figure the fact states
 * is unclear, so it is held for a person (`needsReview`).
 */
export function unclearFiguresDiffer(a: string, b: string): boolean {
  const ka = [...verifierFigureSet(a)].filter(isUnclearFigure);
  const kb = [...verifierFigureSet(b)].filter(isUnclearFigure);
  const sa = new Set(ka);
  const sb = new Set(kb);
  return ka.some((k) => !sb.has(k)) || kb.some((k) => !sa.has(k));
}

/**
 * The fact read with each misread year inside a listed form replaced by its
 * quarantine key, in place: same order, same length. `read` is
 * `readVerifierFigures`'s fact read; each entry's span is retraced in the
 * order that function wrote it, and a retrace that does not give the same
 * count throws, loudly, never a guess.
 */
function quarantined(
  shared: SharedVerifierReads,
  read: string[],
  preRange: number,
  reads: readonly WideRead[],
  owner: Int32Array,
  norm: (f: string) => string,
): string[] {
  const { folded, base, baseSpans } = shared;
  if (!QUARANTINE_GATE.test(folded)) return read;
  const forms = [...folded.matchAll(QUARANTINE_FORM)].map((m) => ({
    at: m.index ?? 0,
    end: (m.index ?? 0) + m[0].length,
    text: m[0],
    range: m.groups?.range !== undefined,
  }));
  const unitAt = unitHalfLookup(folded, norm); // TA-1377: month, week and day halves, matched apart from the years.
  if (forms.length === 0 && unitAt === null) return read;
  const spans: (readonly [number, number] | null)[] = [];
  for (const b of base) {
    const w = reads[owner[b.at] ?? -1];
    if (w === undefined || !supersededBy(b, w, folded)) spans.push([b.at, b.end]);
  }
  let only = 0;
  for (const pattern of VERIFIER_ONLY_PATTERNS) {
    for (const m of folded.matchAll(pattern)) {
      spans.push([m.index ?? 0, (m.index ?? 0) + m[0].length]);
      only++;
    }
  }
  if (only !== shared.verifierOnly.length) throw new Error("TA-1376 quarantine: verifier-only reads did not retrace");
  for (const w of reads) if (!baseSpans.has(`${w.at}:${w.end}`)) spans.push([w.at, w.end]);
  for (let i = 0; i < shared.deadlines.length; i++) spans.push(null);
  if (spans.length !== preRange) throw new Error("TA-1376 quarantine: the fact read did not retrace");
  const seen = new Set(read.slice(0, preRange).map(norm));
  for (const w of reads) {
    for (const e of w.ends) {
      if (seen.has(e)) continue;
      seen.add(e);
      spans.push([w.at, w.end]);
    }
  }
  if (spans.length !== read.length) throw new Error("TA-1376 quarantine: the range ends did not retrace");
  // Each folded character's form, so each test is O(1) and a long text stays linear (forms never overlap).
  const formAt = new Int32Array(folded.length + 1).fill(-1);
  forms.forEach((f, i) => formAt.fill(i, f.at, f.end));
  return read.map((figure, i) => {
    const span = spans[i];
    if (span === null || span === undefined || span[1] <= span[0]) return figure;
    const first = formAt[span[0]] ?? -1;
    const form = forms[first];
    const key = form !== undefined && formAt[span[1] - 1] === first ? norm(figure) : "";
    if (form !== undefined && QUARANTINE_YEAR_READ.test(key) && !(form.range && !QUARANTINE_RANGE_READ.test(key))) return quarantineKey(key, form.text);
    return unitAt === null ? figure : unitAt(figure, span); // TA-1377: a read the year forms leave.
  });
}

/** A quantity as a number: "two and a half" 2.5, "1 1/2" 1.5, "half a" 0.5, ".5" 0.5. NaN when it has no value. */
function qtyValue(raw: string): number {
  const s = raw.toLowerCase().replace(/\s+/g, " ").trim();
  const whole = (w: string): number => (/^\d/.test(w) ? Number(w.replace(/,/g, "")) : spelledValue(w));
  const part = (w: string): number => (w.startsWith("half") ? 0.5 : w.startsWith("quarter") ? 0.25 : 0.75);
  const and = /^(.+?) and (?:an? )?(half|quarter|three[ -]quarters?)$/.exec(s);
  if (and) return whole(and[1] ?? "") + part(and[2] ?? "");
  const mixed = /^(\d+) (\d+)\/(\d+)$/.exec(s);
  if (mixed) return Number(mixed[3]) === 0 ? NaN : Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
  const fraction = /^(\d+)\/(\d+)$/.exec(s);
  if (fraction) return Number(fraction[2]) === 0 ? NaN : Number(fraction[1]) / Number(fraction[2]);
  if (/^\.\d+$/.test(s)) return Number(`0${s}`);
  if (/^(?:an? |one )?half\b/.test(s)) return 0.5;
  if (/^(?:an? |one )?quarter\b/.test(s)) return 0.25;
  if (/^three[ -]quarters?\b/.test(s)) return 0.75;
  return whole(s);
}

/** A computed quantity as text, to six places (a third is "0.333333"). */
const fmtNum = (n: number): string => String(Number(n.toFixed(6)));
/** A quantity the text wrote in digits, as it wrote it: no trailing zeros, no leading ones, never rounded. */
function textNum(s: string): string {
  let start = 0;
  while (start < s.length - 1 && s[start] === "0" && /\d/.test(s[start + 1] ?? "")) start++;
  let end = s.length;
  if (s.includes(".")) {
    while (end > 0 && s[end - 1] === "0") end--;
    if (s[end - 1] === ".") end--;
  }
  return s.slice(start, end);
}
const quantity = (n: number | string): string => (typeof n === "string" ? textNum(n) : fmtNum(n));

/** Whole dollars as "$1,500", cents as "$0.65": the shop's own form. */
function dollarsFigure(n: number): string {
  const [whole = "0", cents = "00"] = (Math.round(n * 100) / 100).toFixed(2).split(".");
  return `$${groupThousands(whole)}${cents === "00" ? "" : `.${cents}`}`;
}

/** The one spelling of a turnaround: singular only for exactly one, always plural for a range or a part. */
function durationFigure(a: number | string, b: number | string | null, word: string): string {
  const w = word.toLowerCase();
  const business = /^(?:business|working|work)[\s-]/.test(w);
  const base = w.replace(/^(?:business|working|work)[\s-]+/, "");
  const unit = base.startsWith("d")
    ? "day"
    : base.startsWith("w")
      ? "week"
      : base.startsWith("mo")
        ? "month"
        : base.startsWith("h")
          ? "hour"
          : "minute";
  const plural = b !== null || quantity(a) !== "1";
  return `${quantity(a)}${b === null ? "" : `-${quantity(b)}`}${business ? "business" : ""}${unit}${plural ? "s" : ""}`;
}

/** What `normalizeVerifierFigure` does to a turnaround the base reader already read ("30 min", "4to6weeks"). */
const NORMALIZED_DURATION =
  /^(\d+(?:\.\d+)?)(?:(?:to|-)(\d+(?:\.\d+)?))?(business)?(days?|weeks?|months?|hours?|mins?|minutes?)$/;

/**
 * Text as the verifier reads it: format characters removed (a zero-width space
 * inside "$9999" or "dollars" hid the figure), every run of whitespace one
 * space ("$  9999", "sixty  five"), and a tens word and a unit word joined by
 * any dash or hyphen one hyphen (a non-breaking hyphen inside "sixty-five" read
 * as "five", the part a cited $5 fact grounds). Dashes elsewhere are left: a
 * pattern that reads one ("half" and "off") already says which.
 */
export function foldFigureText(text: string): string {
  return text
    .replace(/\p{Cf}/gu, "")
    .replace(/\s+/g, " ")
    .replace(new RegExp(`\\b(${TENS_WORD})[${DASH}]+(${UNIT_WORD})\\b`, "gi"), "$1-$2");
}

interface WideRead {
  readonly at: number;
  readonly end: number;
  readonly figure: string;
  /** A range keeps the base read of its upper end ("4-6 weeks" and "6 weeks"). */
  readonly range: boolean;
  /** TA-572: the range's upper end in canonical form, a figure of its own wherever no base read states it. */
  readonly ends: readonly string[];
}

interface WideScan {
  readonly reads: readonly WideRead[];
  /** For each character, the index in `reads` of the read that covers it, or -1. Reads never overlap. */
  readonly owner: Int32Array;
  /** Verbatim: a form this module will not read whole ("$3-4k"). */
  readonly refused: readonly string[];
}

/**
 * Every wide read in folded text, earlier readers winning an overlap.
 * `draft` adds TA-1374's readers; without it the scan is 373becde's.
 */
function scanWide(text: string, draft: boolean): WideScan {
  const reads: WideRead[] = [];
  const refused: string[] = [];
  const owner = new Int32Array(text.length).fill(-1);
  const add = (at: number, end: number, figure: string, range = false, ends: readonly string[] = []): void => {
    for (let i = at; i < end; i++) if (owner[i] !== -1) return;
    owner.fill(reads.length, at, end);
    reads.push({ at, end, figure, range, ends });
  };
  const moneyValue = (w: string): number => (/^\d/.test(w) ? Number(w.replace(/,/g, "")) : spelledValue(w));
  const span = (m: RegExpMatchArray): [number, number] => [m.index ?? 0, (m.index ?? 0) + m[0].length];
  const tailOf = (end: number): string => text.slice(end);

  for (const m of text.matchAll(WIDE_MONEY_SCALED)) {
    const [at, end] = span(m);
    if (MONEY_FAR_BARE.test(tailOf(end))) {
      refused.push(`${m[0]}${/^\s*(?:-|to)\s*\d[\d,.]*\s?[a-z]*/i.exec(tailOf(end))?.[0] ?? ""}`);
      continue;
    }
    add(at, end, dollarsFigure(Number((m[1] ?? "0").replace(/,/g, "")) * (MONEY_SCALE_VALUE[(m[2] ?? "").toLowerCase()] ?? 1)));
  }
  for (const m of text.matchAll(WIDE_MONEY_UPPER)) {
    const digits = m[1] ?? "";
    const [, end] = span(m);
    const at = end - digits.length;
    const frac = digits.split(".")[1];
    if (MONEY_FAR_SCALE.test(tailOf(end)) || (frac !== undefined && frac.length !== 2)) {
      refused.push(`${text.slice(Math.max(0, at - 12), end)}${MONEY_FAR_SCALE.exec(tailOf(end))?.[0] ?? ""}`.trim());
      continue;
    }
    add(at, end, dollarsFigure(Number(digits.replace(/,/g, ""))));
  }
  for (const m of text.matchAll(WIDE_MONEY_WORDS_RANGE)) {
    const [at, end] = span(m);
    const scale = /\bthousand\b/i.test(m[2] ?? "") ? 1e3 : /\bhundred\b/i.test(m[2] ?? "") ? 100 : 1;
    // Fix round 2: a digit far end carries its scale as a word ("9 hundred").
    const farDigits = /^[\d,.]+/.exec(m[2] ?? "")?.[0];
    const far = farDigits === undefined ? moneyValue(m[2] ?? "") : Number(farDigits.replace(/,/g, "")) * scale;
    let near = moneyValue(m[1] ?? "");
    // TA-572 fix round 1: "four to nine hundred dollars" shares the far end's
    // scale, so the bare near end is $400, not $4 (fix round 2: a digit near
    // end too, "4 to 9 hundred dollars"). Kept as written where the scaled
    // value would pass the far end ("ninety to nine hundred dollars").
    if (scale > 1 && !/\b(?:hundred|thousand)\b/i.test(m[1] ?? "") && near * scale <= far) near *= scale;
    add(at, end, dollarsFigure(near), true, [dollarsFigure(far)]);
  }
  for (const m of text.matchAll(WIDE_MONEY_HUNDREDS)) {
    const [at, end] = span(m);
    add(at, end, dollarsFigure(Number((m[1] ?? "").replace(/,/g, "")) * 100));
  }
  for (const m of matchAllPastGroups(text, WIDE_MONEY_GROUPED_SCAN)) {
    const [at, end] = span(m);
    add(at, end, `$${groupThousands((m[1] ?? "").replace(/[, ]/g, ""))}`);
  }
  for (const m of text.matchAll(WIDE_MONEY_MAGNITUDE)) {
    const [at, end0] = span(m);
    const unit = (m[4] ?? "").toLowerCase();
    const tail = tailOf(end0);
    const dollarWord = /^\s*(?:dollars?|bucks)\b/i.exec(tail)?.[0] ?? "";
    const n = m[2] !== undefined ? 1 : m[3] !== undefined ? 2 : moneyValue(m[1] ?? "");
    if (unit === "grand") {
      if (GRAND_AFTER_NOUN.test(tail)) continue;
      if (m[2] !== undefined && dollarWord === "" && !A_GRAND_ENDS.test(tail)) continue;
    } else if (dollarWord === "") continue;
    add(at, end0 + dollarWord.length, dollarsFigure(n * (unit === "million" ? 1e6 : 1e3)));
  }
  for (const m of draft ? text.matchAll(WIDE_MONEY_SCALED_BARE) : []) {
    const [at, end] = span(m);
    const near = MONEY_NEAR_BARE.exec(text.slice(0, at))?.[0];
    if (near !== undefined || MONEY_FAR_BARE.test(tailOf(end))) {
      const far = /^\s*(?:-|to)\s*\d[\d,.]*\s?[a-z]*/i.exec(tailOf(end))?.[0] ?? "";
      refused.push(`${near ?? ""}${m[0]}${far}`);
      continue;
    }
    const n = Number(m[1] ?? "0") * (MONEY_SCALE_VALUE[(m[2] ?? "").toLowerCase()] ?? 1);
    const before = text.slice(0, at);
    const code = BARE_FOREIGN_CODE_BEFORE.exec(before);
    const symbol = BARE_SYMBOL_BEFORE.exec(before);
    const word = BARE_FOREIGN_AFTER.exec(tailOf(end));
    if (code !== null) {
      const c = (code[1] ?? "").toLowerCase();
      add(at - code[0].length, end, `${fmtNum(n)}${FOREIGN_UNIT[c] ?? c}`);
    } else if (symbol !== null) {
      const unit = SYMBOL_UNIT[symbol[1] ?? ""];
      if (unit === undefined) refused.push(`${symbol[0]}${m[0]}`);
      else add(at - symbol[0].length, end, `${fmtNum(n)}${unit}`);
    } else if (word !== null) {
      const w = (word[1] ?? "").toLowerCase();
      add(at, end + word[0].length, `${fmtNum(n)}${FOREIGN_UNIT[w] ?? w.replace(/s$/, "")}`);
    } else add(at, end, dollarsFigure(n));
  }
  for (const m of text.matchAll(WIDE_HUNDRED)) {
    const [at, end] = span(m);
    add(at, end, dollarsFigure(100 + (m[1] === undefined ? 0 : spelledValue(m[1]))));
  }
  for (const m of text.matchAll(WIDE_FOREIGN)) {
    const [at, end] = span(m);
    const n = /^\d/.test(m[1] ?? "") ? Number((m[1] ?? "").replace(/,/g, "")) : spelledValue(m[1] ?? "");
    const code = (m[2] ?? "").toLowerCase();
    if (code === "cad" && !/[.,]/.test(m[1] ?? "")) continue;
    add(at, end, `${fmtNum(n)}${FOREIGN_UNIT[code] ?? code.replace(/s$/, "")}`);
  }
  for (const m of text.matchAll(WIDE_FOREIGN_CODE)) {
    const [at, end] = span(m);
    const code = (m[1] ?? "").toLowerCase();
    add(at, end, `${fmtNum(Number((m[2] ?? "").replace(/,/g, "")))}${FOREIGN_UNIT[code] ?? code}`);
  }
  for (const m of text.matchAll(WIDE_CENTS)) {
    const [at, end] = span(m);
    const n = /^\d/.test(m[1] ?? "") ? Number((m[1] ?? "").replace(/,/g, "")) : spelledValue(m[1] ?? "");
    add(at, end, dollarsFigure(n / 100));
  }
  for (const m of text.matchAll(WIDE_PERCENT)) {
    const [at, end] = span(m);
    const n = qtyValue(m[1] ?? "");
    const b = m[2] === undefined ? null : qtyValue(m[2]);
    if (!Number.isFinite(n) || (b !== null && !Number.isFinite(b))) continue;
    if (b === null) add(at, end, `${fmtNum(n)}%`);
    else add(at, end, `${fmtNum(n)}-${fmtNum(b)}%`, true, [`${fmtNum(b)}%`]);
  }
  for (const m of text.matchAll(WIDE_CARAT)) {
    const [at, end] = span(m);
    const scale = /^(?:points?|pts)$/i.test(m[3] ?? "") ? 100 : 1;
    const n = qtyValue(m[1] ?? "") / scale;
    const b = m[2] === undefined ? null : qtyValue(m[2]) / scale;
    if (!Number.isFinite(n) || (b !== null && !Number.isFinite(b))) continue;
    if (b === null) add(at, end, `${fmtNum(n)}ct`);
    else add(at, end, `${fmtNum(n)}-${fmtNum(b)}ct`, true, [`${fmtNum(b)}ct`]);
  }
  for (const m of text.matchAll(draft ? WIDE_ARTICLE_HALF_DRAFT : WIDE_ARTICLE_HALF)) {
    const [at, end] = span(m);
    const unit = (m[1] ?? "").toLowerCase();
    add(at, end, unit === "year" ? "1.5y" : unit === "carat" ? "1.5ct" : durationFigure(1.5, null, unit));
  }
  for (const m of draft ? text.matchAll(WIDE_YEAR_AND_HALF) : []) {
    const head = (m[1] ?? "").toLowerCase();
    const n = /^an?$/.test(head) ? 1 : moneyValue(head);
    if (!Number.isFinite(n)) continue;
    const [at, end] = span(m);
    add(at, end, `${fmtNum(n + 0.5)}y`);
  }
  for (const m of draft ? text.matchAll(WIDE_YEARS) : []) {
    const a = (m[1] ?? "").trim();
    const bRaw = m[2]?.trim();
    if (PLAIN_NUM.test(a) && (bRaw === undefined || PLAIN_NUM.test(bRaw))) continue;
    const [at, end] = span(m);
    const n = qtyValue(a);
    const b = bRaw === undefined ? null : qtyValue(bRaw);
    if (!Number.isFinite(n) || (b !== null && !Number.isFinite(b))) continue;
    if (b === null) add(at, end, `${fmtNum(n)}y`);
    else add(at, end, `${fmtNum(n)}-${fmtNum(b)}y`, true, [`${fmtNum(b)}y`]);
  }
  for (const m of text.matchAll(WIDE_ARTICLE_RANGE)) {
    const [at, end] = span(m);
    const upper = /^\d/.test(m[2] ?? "") ? Number(m[2]) : spelledValue(m[2] ?? "");
    add(at, end, durationFigure(1, upper, m[1] ?? ""), true, [durationFigure(upper, null, m[1] ?? "")]);
  }
  for (const m of text.matchAll(WIDE_COUPLE)) {
    const [at, end] = span(m);
    add(at, end, durationFigure(2, null, m[1] ?? ""));
  }
  for (const m of text.matchAll(WIDE_DURATION)) {
    const [at, end] = span(m);
    const a = qtyValue(m[1] ?? "");
    const b = m[2] === undefined ? null : qtyValue(m[2]);
    if (!Number.isFinite(a) || (b !== null && !Number.isFinite(b))) continue;
    add(at, end, durationFigure(a, b, m[3] ?? ""), b !== null, b === null ? [] : [durationFigure(b, null, m[3] ?? "")]);
  }
  return { reads, refused, owner };
}

/**
 * TA-1375 round 2, draft side only: what the readers above `EXTRA_SCALE` read
 * and refuse in folded text. Added to 5f4596d8's draft read and draft
 * refusals, never in place of them, and never run on a fact.
 */
function draftExtras(text: string): { figures: string[]; refused: string[]; before1376: number; before1377: number } {
  const figures: string[] = [];
  const refused: string[] = [];
  const value = (w: string): number => (/^\d/.test(w) ? Number(w.replace(/[ ,]/g, "")) : spelledValue(w));
  const scaled = (n: string, scale: string | undefined): number => {
    const s = (scale ?? "").toLowerCase();
    return value(n) * (s === "hundred" ? 100 : (MONEY_SCALE_VALUE[s] ?? 1));
  };
  /** A marked amount: read in its currency, or refused whole where a range follows it. */
  const marked = (m: RegExpMatchArray, amount: string, scale: string | undefined, unit: string): void => {
    const tail = DRAFT_RANGE_AFTER.exec(text.slice((m.index ?? 0) + m[0].length));
    if (tail !== null) refused.push(`${m[0]}${tail[0]}`);
    else figures.push(`${fmtNum(scaled(amount, scale))}${unit}`);
  };

  if (DRAFT_HAS_HALF.test(text)) {
    for (const m of text.matchAll(DRAFT_DURATION_AND_HALF)) {
      const head = (m[1] ?? "").toLowerCase();
      const n = /^an?$/.test(head) ? 1 : value(head);
      if (!Number.isFinite(n)) continue;
      const unit = (m[2] ?? "").toLowerCase();
      figures.push(/^y/.test(unit) ? `${fmtNum(n + 0.5)}y` : durationFigure(n + 0.5, null, unit));
    }
  }
  if (DRAFT_HAS_CARAT.test(text) && text.search(DRAFT_A_CARAT) !== -1) figures.push("1ct");
  if (DRAFT_HAS_PREFIX.test(text)) {
    for (const m of text.matchAll(DRAFT_PREFIXED_DOLLAR)) {
      const prefix = m[1] ?? "";
      const lower = prefix.toLowerCase();
      if (lower === "us" || lower === "usd") continue;
      const code = DRAFT_DOLLAR_CODE[lower];
      // "C$", "CA$", "ca$": that dollar. "c$" alone may be "a $" mistyped, and
      // "R$", "NT$" or "Can$" is a dollar this module does not name: refused.
      // Any other prefix, whatever its case ("Can$", "Mex$"), is refused.
      if (code !== undefined && (prefix.length > 1 || prefix === prefix.toUpperCase())) marked(m, m[2] ?? "", m[3], code);
      else refused.push(m[0]);
    }
    for (const m of text.matchAll(DRAFT_CODE_DOLLAR)) marked(m, m[2] ?? "", m[3], DRAFT_DOLLAR_CODE[(m[1] ?? "").toLowerCase()] ?? "");
  }
  if (DRAFT_HAS_SIGN.test(text)) {
    for (const m of text.matchAll(DRAFT_SIGN_BEFORE)) {
      const unit = SYMBOL_UNIT[m[1] ?? ""];
      if (unit === undefined) refused.push(m[0]);
      else marked(m, m[2] ?? "", m[3], unit);
    }
    for (const m of text.matchAll(DRAFT_SIGN_AFTER)) {
      const unit = SYMBOL_UNIT[m[3] ?? ""];
      if (unit === undefined) refused.push(m[0]);
      else figures.push(`${fmtNum(scaled(m[1] ?? "", m[2]))}${unit}`);
    }
  }
  if (DRAFT_HAS_RS.test(text)) for (const m of text.matchAll(DRAFT_RUPEE_BEFORE)) marked(m, m[1] ?? "", m[2], "rupee");
  if (DRAFT_HAS_CURRENCY_WORD.test(text)) {
    for (const m of text.matchAll(DRAFT_NAMED_CURRENCY)) {
      const amount = m[1] ?? "";
      const scale = m[2];
      const marker = m[3] !== undefined || m[4] !== undefined;
      const nation = (m[5] ?? "").toLowerCase().replace(/\s+/g, " ");
      const word = (m[6] ?? "").toLowerCase();
      if (/^(?:dollars?|bucks)$/.test(word)) {
        if (nation === "") continue; // US dollars, read elsewhere
        const code = DRAFT_NATION_DOLLAR[nation];
        if (code === undefined) refused.push(m[0]);
        // "3,500 US dollars": 5f4596d8 read nothing, so any fact grounded it.
        else if (code === "usd") figures.push(dollarsFigure(scaled(amount, scale)));
        else figures.push(`${fmtNum(scaled(amount, scale))}${code}`);
        continue;
      }
      if (word === "cad" && !marker && nation === "" && scale === undefined && !/[.,]/.test(amount)) {
        // `WIDE_FOREIGN` reads "1,200 CAD" and leaves "2 CAD" (a count of
        // designs). A plain amount of 100 or more is no count: refused.
        if (/^\d{3,}$/.test(amount)) refused.push(m[0]);
        continue;
      }
      const unit = word === "quid" ? "pound" : (FOREIGN_UNIT[word] ?? word.replace(/s$/, ""));
      figures.push(`${fmtNum(scaled(amount, scale))}${unit}`);
    }
    for (const m of text.matchAll(DRAFT_PLAIN_NATION)) {
      const code = DRAFT_NATION_DOLLAR[(m[2] ?? "").toLowerCase().replace(/\s+/g, " ")];
      if (code === undefined) refused.push(m[0]);
      else if (code !== "usd") figures.push(`${fmtNum(value(m[1] ?? ""))}${code}`);
    }
    for (const m of text.matchAll(DRAFT_SCALED_NATION)) {
      const code = DRAFT_NATION_DOLLAR[(m[3] ?? "").toLowerCase().replace(/\s+/g, " ")];
      if (code === undefined) refused.push(m[0]);
      else if (code !== "usd") figures.push(`${fmtNum(scaled(m[1] ?? "", m[2]))}${code}`);
    }
    for (const m of text.matchAll(DRAFT_SCALED_CAD_NOUN)) refused.push(m[0]);
  }
  if (DRAFT_HAS_MAGNITUDE.test(text)) {
    for (const m of text.matchAll(DRAFT_VAGUE_MAGNITUDE)) refused.push(m[0]);
    // A magnitude inside a price the base reader reads ("three thousand five
    // hundred dollars", `MONEY_WORDS`) is that price's, not a bare one.
    let words: (readonly [number, number])[] | null = null;
    for (const m of text.matchAll(DRAFT_BARE_MAGNITUDE)) {
      const at = m.index ?? 0;
      const end = at + m[0].length;
      words ??= [...text.matchAll(MONEY_WORDS)].map((w) => [w.index ?? 0, (w.index ?? 0) + w[0].length] as const);
      if (!words.some(([a, b]) => at < b && end > a)) refused.push(m[0]);
    }
  }
  // `figures` up to here is 9f293ea2's draft read; `figuresDiffer` compares it too.
  const before1376 = figures.length;
  draftExtras1376(text, figures, refused, value, scaled, marked);
  // TA-1377: `figures` up to here is f1743de4's draft read; `figuresDiffer` compares it too.
  const before1377 = figures.length;
  draftExtras1377(text, figures, refused, scaled);
  return { figures, refused, before1376, before1377 };
}

/**
 * TA-1377, draft side only: a currency named after a "$" amount, a percent
 * joined by a dash, and every size of a size list, each a read or a refusal
 * added after every TA-1376 one (`draftExtras1376`).
 */
function draftExtras1377(
  text: string,
  figures: string[],
  refused: string[],
  scaled: (n: string, scale: string | undefined) => number,
): void {
  if (DRAFT_HAS_DOLLAR_SIGN.test(text)) {
    for (const m of text.matchAll(DRAFT_DOLLAR_THEN_NAMED)) {
      const marker = m[5] !== undefined || m[6] !== undefined;
      const word = (m[7] ?? "").toLowerCase();
      const name = (m[8] ?? "").toLowerCase().replace(/\s+/g, " ");
      let unit: string | undefined;
      if (word !== "") unit = FOREIGN_UNIT[word] ?? DRAFT_DOLLAR_NAME[word] ?? word.replace(/s$/, "");
      else if (name === "can") {
        // Review round 2: "$65 CAN" is refused, and also read as Canadian
        // dollars beside the "$65" read, so the send-time recheck (which
        // reads figures, never refusals) holds it against a US fact too. Any
        // other case is the verb, left as f1743de4 read it.
        if (!DRAFT_UNNAMED_AFTER.has(m[8] ?? "")) continue;
        refused.push(m[0]);
        unit = "cad";
      } else {
        const end = (m.index ?? 0) + m[0].length;
        DRAFT_CLAUSE_END_AT.lastIndex = end;
        DRAFT_NAMED_CONNECTIVE.lastIndex = end;
        DRAFT_NAMED_DOLLARS_AT.lastIndex = end;
        // Review round 2: "MX" and "CA" after the amount, as `DRAFT_DOLLAR_CODE` reads them before a "$".
        unit = DRAFT_DOLLAR_NAME[name] ?? DRAFT_NATION_DOLLAR[name] ?? (name === "mx" || name === "ca" ? DRAFT_DOLLAR_CODE[name] : undefined);
        if (
          !marker && !DRAFT_CLAUSE_END_AT.test(text) && !DRAFT_NAMED_CONNECTIVE.test(text) && !DRAFT_NAMED_DOLLARS_AT.test(text)
        ) {
          // Review round 1: a nation with a dollar of its own in any other
          // position may name the currency ("$65 Canadian with tax", or a line
          // break the fold made a space). Unsure, so refused, and also read in
          // that currency below, beside the "$65" read, so the send-time
          // recheck (which reads figures, never refusals) holds it too. Never
          // read as US dollars alone (B13). A nation with no dollar ("$650
          // Swiss watch") is not a dollar, so the "$" stays the read.
          if (unit === undefined || unit === "usd") continue;
          refused.push(m[0]);
        } else if (unit === undefined) {
          refused.push(m[0]);
          continue;
        }
      }
      if (unit === "usd") continue; // US dollars, read as "$" already
      figures.push(`${fmtNum(scaled(m[1] ?? "", m[2]))}${unit}`);
      if (m[3] !== undefined) figures.push(`${fmtNum(scaled(m[3], m[4]))}${unit}`);
    }
  }
  if (DRAFT_HAS_JOINED_PERCENT.test(text) || (DRAFT_HAS_PERCENT_SIGN.test(text) && DRAFT_HAS_LIST_WORD.test(text))) {
    for (const m of text.matchAll(DRAFT_JOINED_PERCENT)) {
      const join = (m[2] ?? "").toLowerCase();
      if (!DRAFT_HAS_DASH.test(m[0]) && !DRAFT_PLURAL_PERCENT.test(m[5] ?? "") && !DRAFT_PERCENT_LIST_WORD.has(join)) continue;
      const n = qtyValue(m[1] ?? "");
      const b = m[3] === undefined ? null : qtyValue(m[3]);
      if (!Number.isFinite(n) || (b !== null && !Number.isFinite(b))) continue;
      if (b === null) figures.push(`${fmtNum(n)}%`);
      else if (DRAFT_PERCENT_SPLIT_WORD.has(join)) figures.push(`${fmtNum(n)}%`, `${fmtNum(b)}%`);
      else figures.push(`${fmtNum(n)}-${fmtNum(b)}%`);
    }
  }
  if (DRAFT_HAS_HUNDRED.test(text)) {
    for (const m of text.matchAll(DRAFT_HUNDRED_PERCENT)) {
      // Joined by a dash only. The spaced "a hundred percent off" is held as a
      // concession over the cap, and the item 125 pin in
      // verifier.widerFigures.test.ts fixes its violations to that alone.
      if (!DRAFT_HAS_DASH.test(m[0])) continue;
      const w = (m[1] ?? "").toLowerCase();
      figures.push(`${fmtNum((w === "a" ? 1 : spelledValue(w)) * 100)}%`);
    }
  }
  if (DRAFT_HAS_SIZE.test(text)) {
    /** The figure for a size that ends at `end`: as written with its fraction where one follows (`DRAFT_SIZE_FRACTION_TAIL`). */
    const withFraction = (figure: string, end: number): string => {
      const frac = DRAFT_SIZE_FRACTION_TAIL.exec(text.slice(end, end + 16));
      return frac === null ? figure : `${figure} ${frac[1] ?? ""}`;
    };
    for (const m of text.matchAll(SIZE)) {
      const end = (m.index ?? 0) + m[0].length;
      const rest = m[0].replace(DRAFT_SIZE_WORD_LEAD, "");
      const first = withFraction(`size ${rest}`, end);
      if (first !== `size ${rest}`) figures.push(first);
      const sizes = rest.split(DRAFT_SIZE_RANGE_JOIN);
      let prev = sizes[sizes.length - 1] ?? "";
      DRAFT_SIZE_LIST_ITEM.lastIndex = end;
      for (let item = DRAFT_SIZE_LIST_ITEM.exec(text); item !== null; item = DRAFT_SIZE_LIST_ITEM.exec(text)) {
        const size = item[2] ?? "";
        const range = DRAFT_SIZE_RANGE_WORD.test(item[1] ?? "");
        figures.push(withFraction(range ? `size ${prev} to ${size}` : `size ${size}`, item.index + item[0].length));
        prev = size;
      }
    }
  }
}

/**
 * TA-1376, draft side only: the forms TA-1375 left open, each a read or a
 * refusal added after every TA-1375 one (`draftExtras`).
 */
function draftExtras1376(
  text: string,
  figures: string[],
  refused: string[],
  value: (w: string) => number,
  scaled: (n: string, scale: string | undefined) => number,
  marked: (m: RegExpMatchArray, amount: string, scale: string | undefined, unit: string) => void,
): void {
  /** A dollar named by `DRAFT_DOLLAR_NAME` or a nation: read in it, US dollars left to the readers that read them, any other refused. */
  const namedDollar = (m: RegExpMatchArray, amount: string, scale: string | undefined, name: string): void => {
    const key = name.toLowerCase().replace(/\s+/g, " ");
    const code = DRAFT_DOLLAR_NAME[key] ?? DRAFT_NATION_DOLLAR[key];
    if (code === undefined) refused.push(m[0]);
    else if (code !== "usd") figures.push(`${fmtNum(scaled(amount, scale))}${code}`);
  };
  if (DRAFT_HAS_SPACED_DOLLAR.test(text)) {
    for (const m of text.matchAll(DRAFT_SPACED_PREFIX)) {
      const prefix = m[1] ?? "";
      const code = DRAFT_SPACED_CODE[prefix];
      if (code !== undefined) marked(m, m[2] ?? "", m[3], code);
      else if (DRAFT_SPACED_UNNAMED.has(prefix)) refused.push(m[0]);
    }
  }
  if (DRAFT_HAS_PREFIX.test(text)) {
    for (const m of text.matchAll(DRAFT_PREFIXED_DOLLAR_ANY_SCALE)) {
      const prefix = m[1] ?? "";
      const lower = prefix.toLowerCase();
      const scale = m[3];
      // "CDN$3,500": `draftExtras` refused it, which stays; it is also read.
      if (lower === "cdn") marked(m, m[2] ?? "", scale, "cad");
      else if (scale !== undefined && scale !== scale.toLowerCase()) {
        const code = DRAFT_DOLLAR_CODE[lower];
        if (code !== undefined && (prefix.length > 1 || prefix === prefix.toUpperCase())) marked(m, m[2] ?? "", scale, code);
      }
    }
  }
  if (DRAFT_HAS_TAIL_CURRENCY.test(text)) {
    for (const m of text.matchAll(DRAFT_DOLLARS_NATION_AFTER)) {
      // Fix round: after a comma the nation is read only where it ends the clause ("3,500 dollars, Canadian." not "..., Canadian buyers").
      if (DRAFT_COMMA_BEFORE_NATION.test(m[0]) && !DRAFT_CLAUSE_END.test(text.slice((m.index ?? 0) + m[0].length))) continue;
      namedDollar(m, m[1] ?? "", m[2], m[3] ?? "");
    }
    for (const m of text.matchAll(DRAFT_CODE_BEFORE_DOLLARS)) namedDollar(m, m[1] ?? "", m[2], m[3] ?? "");
    for (const m of text.matchAll(DRAFT_STERLING)) {
      const amount = m[1] ?? "";
      if (m[2] !== undefined || amount.includes(",") || /^\d{4,}/.test(amount)) figures.push(`${fmtNum(scaled(amount, m[2]))}pound`);
    }
    for (const m of text.matchAll(DRAFT_WON)) {
      const amount = m[1] ?? "";
      if (m[2] !== undefined || m[3] !== undefined || amount.includes(",")) figures.push(`${fmtNum(scaled(amount, m[2]))}won`);
    }
  }
  if (DRAFT_HAS_FR.test(text)) {
    for (const m of text.matchAll(DRAFT_FRANC_BEFORE)) if (DRAFT_FRANC_PREFIX.has(m[1] ?? "")) marked(m, m[2] ?? "", m[3], "franc");
  }
  if (DRAFT_HAS_AND_HALF.test(text)) {
    for (const m of text.matchAll(DRAFT_AND_HALF_SCALE)) {
      const unit = (m[2] ?? "").toLowerCase();
      const n = (value(m[1] ?? "") + 0.5) * (unit === "grand" ? 1e3 : (MONEY_SCALE_VALUE[unit] ?? 1));
      if (!Number.isFinite(n)) continue;
      const after = DRAFT_CURRENCY_AFTER.exec(text.slice((m.index ?? 0) + m[0].length));
      if (after === null) {
        if (unit === "grand") figures.push(dollarsFigure(n));
        else refused.push(m[0]);
        continue;
      }
      const nation = (after[1] ?? "").toLowerCase().replace(/\s+/g, " ");
      const word = (after[2] ?? "").toLowerCase();
      if (/^(?:dollars?|bucks)$/.test(word)) {
        const code = nation === "" ? "usd" : DRAFT_NATION_DOLLAR[nation];
        if (code === undefined) refused.push(`${m[0]}${after[0]}`);
        else figures.push(code === "usd" ? dollarsFigure(n) : `${fmtNum(n)}${code}`);
        // Fix round: "three and a half thousand dollars Canadian" is also read in the dollar named after it.
        const rest = text.slice((m.index ?? 0) + m[0].length + after[0].length);
        const tail = nation === "" ? DRAFT_NATION_TAIL.exec(rest) : null;
        if (tail !== null && (!tail[0].includes(",") || DRAFT_CLAUSE_END.test(rest.slice(tail[0].length)))) {
          const key = (tail[1] ?? "").toLowerCase().replace(/\s+/g, " ");
          const named = DRAFT_DOLLAR_NAME[key] ?? DRAFT_NATION_DOLLAR[key];
          if (named === undefined) refused.push(`${m[0]}${after[0]}${tail[0]}`);
          else if (named !== "usd") figures.push(`${fmtNum(n)}${named}`);
        }
      } else {
        const unitName = word === "quid" || word === "sterling" ? "pound" : (FOREIGN_UNIT[word] ?? word.replace(/s$/, ""));
        figures.push(`${fmtNum(n)}${unitName}`);
      }
    }
  }
  if (DRAFT_HAS_HUNDRED.test(text)) for (const m of text.matchAll(DRAFT_BARE_HUNDRED)) refused.push(m[0]);
  if (DRAFT_HAS_MIL.test(text)) {
    for (const m of text.matchAll(DRAFT_MIL)) {
      if (m[1] !== undefined || m[3] !== undefined) figures.push(dollarsFigure(value(m[2] ?? "") * 1e6));
      else refused.push(m[0]);
    }
  }
  if (DRAFT_HAS_LAKH.test(text)) for (const m of text.matchAll(DRAFT_LAKH)) refused.push(m[0]);
  if (DRAFT_HAS_VAGUE.test(text)) {
    for (const m of text.matchAll(DRAFT_VAGUE_MANY)) {
      if (/grand$/i.test(m[0]) && GRAND_AFTER_NOUN.test(text.slice((m.index ?? 0) + m[0].length))) continue;
      refused.push(m[0]);
    }
  }
  if (DRAFT_HAS_HALF.test(text)) {
    for (const m of text.matchAll(DRAFT_DURATION_HALF_JOINED)) {
      const head = (m[1] ?? "").toLowerCase();
      const n = /^an?$/.test(head) ? 1 : value(head);
      if (!Number.isFinite(n)) continue;
      const unit = (m[3] ?? "").toLowerCase();
      figures.push(/^y/.test(unit) ? `${fmtNum(n + 0.5)}y` : durationFigure(n + 0.5, null, `${m[2] ?? ""}${unit}`));
    }
  }
  if (DRAFT_HAS_HALF.test(text)) {
    for (const m of text.matchAll(DRAFT_HALF_JOINED_BEFORE_UNIT)) {
      const n = value((m[1] ?? m[2] ?? "").toLowerCase());
      if (!Number.isFinite(n)) continue;
      const unit = (m[4] ?? "").toLowerCase();
      figures.push(/^y/.test(unit) ? `${fmtNum(n + 0.5)}y` : durationFigure(n + 0.5, null, `${m[3] ?? ""}${unit}`));
    }
    // Fix round: a half after a whole that is not read is refused and read as written (`DRAFT_HALF_VAGUE_WHOLE`).
    const unread = (m: RegExpMatchArray): void => {
      refused.push(m[0]);
      figures.push(m[0].replace(/\s+/g, " ").trim());
    };
    for (const m of text.matchAll(DRAFT_HALF_VAGUE_WHOLE)) unread(m);
    for (const m of text.matchAll(DRAFT_HALF_BARE_WHOLE)) {
      const n = value((m[1] ?? m[2] ?? "").toLowerCase());
      if (n === 1 && m[3] === undefined) continue;
      unread(m);
    }
  }
  if (DRAFT_HAS_AND_DECIMAL.test(text)) {
    for (const m of text.matchAll(DRAFT_WHOLE_AND_DECIMAL)) {
      if (m[1] !== undefined && Number(m[4]) >= 1) continue;
      const n = value((m[2] ?? m[3] ?? "").toLowerCase());
      if (!Number.isFinite(n)) continue;
      const unit = (m[6] ?? "").toLowerCase();
      figures.push(/^y/.test(unit) ? `${fmtNum(n)}y` : durationFigure(n, null, `${m[5] ?? ""}${unit}`));
    }
  }
  if (DRAFT_HAS_HALF.test(text)) {
    // Round 3: a whole joined to a half through any run. A readable whole reads
    // whole and a half; a run the readers above do not read whole, or a whole
    // that is not a number, is refused and read as no fact reads it.
    for (const m of text.matchAll(DRAFT_WHOLE_JOIN_HALF)) {
      const run = m[4] ?? "";
      if (run.trim() === "" && m[3] === undefined) continue;
      const unit = (m[6] ?? "").toLowerCase();
      if (m[3] === undefined) {
        const n = value((m[1] ?? m[2] ?? "").toLowerCase());
        if (!Number.isFinite(n)) continue;
        figures.push(/^y/.test(unit) ? `${fmtNum(n + 0.5)}y` : durationFigure(n + 0.5, null, `${m[5] ?? ""}${unit}`));
        // A clean join is read whole above, and stays unrefused.
        if (DRAFT_CLEAN_JOIN.test(run)) continue;
      }
      refused.push(m[0]);
      figures.push(`unread: ${m[0].replace(/\s+/g, " ").trim()}`);
    }
    for (const m of text.matchAll(DRAFT_UNIT_THEN_HALF)) {
      if (!DRAFT_JOINS.test(m[4] ?? "")) continue;
      const head = (m[1] ?? "").toLowerCase();
      const n = /^an?$/.test(head) ? 1 : value(head);
      if (!Number.isFinite(n)) continue;
      const unit = (m[3] ?? "").toLowerCase();
      figures.push(/^y/.test(unit) ? `${fmtNum(n + 0.5)}y` : durationFigure(n + 0.5, null, `${m[2] ?? ""}${unit}`));
    }
  }
  if (DRAFT_HAS_DOLLAR_SIGN.test(text)) {
    // Round 3: "3,500 HK$": read in the dollar the prefix names, US dollars as dollars, any other refused.
    for (const m of matchAllPastGroups(text, DRAFT_AMOUNT_THEN_PREFIX_SCAN)) {
      const prefix = m[3] ?? "";
      const lower = prefix.toLowerCase();
      const code = DRAFT_DOLLAR_CODE[lower];
      if (lower === "us" || lower === "usd") figures.push(dollarsFigure(scaled(m[1] ?? "", m[2])));
      else if (code !== undefined && (prefix.length > 1 || prefix === prefix.toUpperCase())) {
        figures.push(`${fmtNum(scaled(m[1] ?? "", m[2]))}${code}`);
      } else refused.push(m[0]);
    }
  }
}

/**
 * Is a base read part of a wider one? The tail of "1/2 ct" ("2 ct") is not the
 * figure. The upper end of a range ("6 weeks" in "4-6 weeks") IS a figure the
 * base reader has always read, and a fact that states the range grounds it.
 */
function supersededBy(b: { at: number; end: number }, w: WideRead, text: string): boolean {
  if (w.at > b.at || b.end > w.end || (w.at === b.at && w.end === b.end)) return false;
  return !(w.range && b.end === w.end && /(?:to|or|-)\s*$/i.test(text.slice(w.at, b.at)));
}

/*
 * TA-1377 (finding 14): a length or weight, on both sides. At f1743de4 the
 * verifier read none, so "The chain is 20 inches." passed against a cited
 * "The chain is 18 inches long." (section 4: any customer-facing figure must
 * exact-match a KB source).
 *
 * A number, or a range of two ("18-20", "18 to 20", "18 or 20", "18 x 20"),
 * then a unit: in, inch, inches, the " mark after a digit (or its curly,
 * double-prime or two-apostrophe forms), mm, millimetre(s), millimeter(s),
 * cm, centimetre(s), centimeter(s), g, gram(s). Each read is written as a key
 * in a namespace of its own (`unit:18in`, `unit:2mm`, `unit:5g`), which no
 * other read can take, so it grounds only the same unit read and is grounded
 * only by one. Spellings fold within a unit (18" and 18 inches are one key);
 * nothing converts between units, so 45 cm never grounds 17.7 inches.
 *
 * The reads are added after every other read, never in place of one, so a
 * fact grounds every draft figure it grounded at f1743de4 and no more of them,
 * and a draft reads every figure it read there: the change only holds more.
 *
 * Not read, each on purpose:
 *   - a number with a currency mark or a currency code before it ("$2mm" is
 *     the $2,000,000 f1743de4 reads, "usd 2mm" its "usd 2"), and a number that
 *     ends a range whose near end is a number with a money scale or "mm"
 *     ("$1k to 2mm", "$2mm-3mm"), where f1743de4 reads or refuses money,
 *     and mm with a currency mark straight after it ("2mm$"), which
 *     f1743de4 refuses as money;
 *   - "in" before a word other than a measure's ("2 in stock", "size 7 in
 *     yellow gold"), or before a number ("1 in 5"): a count, not a length;
 *   - g, mm or cm inside a word ("5gold", "10gb", "14kg");
 *   - a fraction or a number after a slash, dot or comma ("1 1/2 inches"
 *     reads nothing, never "2 inches").
 */
const MEASURE_PREFIX = "unit:";
const MEASURE_NUMBER = `(?:${DIGITS}|${SPELLED})`;
const MEASURE_RANGE_JOIN = `(?: ?[${DASH}] ?| (?:to|or|by) | ?[x\\u00d7] ?)`;
const MEASURE_WORD_JOIN = `(?: ?[${DASH}] ?| ?)`;
/** "in" is a length only before an end, a mark, or a word that measures. */
const MEASURE_IN_FOLLOWS =
  "(?=\\.|$|[^\\sA-Za-z0-9]| (?:$|[^\\sA-Za-z0-9]|(?:long|wide|thick|tall|high|deep|lengths?|chains?|x|by)\\b))";
const MEASURE_WORD = `(?:(?:inches|inch|millimet(?:er|re)s?|mm|centimet(?:er|re)s?|cm|grams?|g)\\b|in\\b${MEASURE_IN_FOLLOWS})`;
const MEASURE_MARK = `(?:"|\\u201d|\\u2033|'')`;
const MEASURE_BODY =
  `(${MEASURE_NUMBER})(?:(${MEASURE_RANGE_JOIN})(${MEASURE_NUMBER}))?` +
  `(?:${MEASURE_WORD_JOIN}(${MEASURE_WORD})|(?<=\\d)(${MEASURE_MARK}))`;
const MEASURE_READ = new RegExp(`(?<![\\w.,/])${MEASURE_BODY}`, "gi");
const MEASURE_EXACT = new RegExp(`^${MEASURE_BODY}$`, "i");
/**
 * TA-1377 fix round 1 (cost): a text `MEASURE_READ` can match at all. Every
 * match ends in a number then a unit: a digit, then an optional join, then
 * the start of a unit word or a mark; or a spelled number, which ends in one
 * of `MEASURE_SPELLED_END` and a word boundary, then a space or a dash join,
 * then a unit word. This is that shape with the rest left out, so it matches
 * wherever `MEASURE_READ` would (a superset), and a text it misses reads no
 * measure.
 */
const MEASURE_UNIT_START = "(?:in|g|m(?:m|illimet)|c(?:m|entimet))";
/** The last word of every `SPELLED` match: a unit, teen or tens word, hundred, thousand or zero. */
const MEASURE_SPELLED_END = "(?:one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|teen|ty|hundred|thousand|zero)";
const MEASURE_GATE = new RegExp(
  `\\d(?: ?[${DASH}] ?| ?)${MEASURE_UNIT_START}|${MEASURE_SPELLED_END}(?: ?[${DASH}] ?| )${MEASURE_UNIT_START}|\\d(?:["\\u201d\\u2033]|'')`,
  "i",
);
/** The last character of every figure `MEASURE_EXACT` matches: a unit word's or a mark's. */
const MEASURE_LAST = /[shremgn"\u201d\u2033']/i;
/** A currency mark or a code f1743de4 reads money after, just before the number. */
const MEASURE_MONEY_BEFORE =
  /(?:\p{Sc}|\b(?:usd|cad|aud|nzd|hkd|sgd|mxn|eur|gbp|jpy|cny|rmb|inr|chf|sek|aed|rs|s?fr)\.?)\s?$/iu;
/** A currency mark straight after an mm read ("2mm$"). */
const MEASURE_MONEY_AFTER = /^\p{Sc}/u;
/** The near end of a range a money reader reads or refuses: a number, a money scale or mm, a join. */
const MEASURE_MONEY_NEAR = new RegExp(
  `\\d[\\d,.]*\\s?(?:k|m|mm|bn|thousand|million|billion|grand)?(\\s?(?:[${DASH}]|to|or|thru|through)\\s?)$`,
  "i",
);

/** The key of a length or weight read ("18 inches", '18"', "2-3mm"), or null for any other figure. */
function measureKey(figure: string): string | null {
  // Cost: a figure that cannot end a unit word or a mark is never one.
  if (!MEASURE_LAST.test(figure.slice(-1))) return null;
  const m = MEASURE_EXACT.exec(figure);
  if (m === null) return null;
  const value = (n: string): string => (/^\d/.test(n) ? textNum(n.replace(/,/g, "")) : String(spelledValue(n)));
  const word = (m[4] ?? "").toLowerCase();
  const unit = m[5] !== undefined || word.startsWith("in")
    ? "in"
    : word.startsWith("m")
      ? "mm"
      : word.startsWith("c")
        ? "cm"
        : "g";
  const a = value(m[1] ?? "");
  if (m[3] === undefined) return `${MEASURE_PREFIX}${a}${unit}`;
  const join = (m[2] ?? "").trim().toLowerCase();
  const kind = join === "or" ? "or" : join === "x" || join === "×" || join === "by" ? "x" : "-";
  return `${MEASURE_PREFIX}${a}${kind}${value(m[3])}${unit}`;
}

/**
 * Is this normalized figure a length or weight key (TA-1377)? A measure read
 * grounds and is grounded only in its own namespace, and it is never a figure
 * a caller picks to say: `diagnose` keeps its quotable choice on f1743de4's
 * reads, so a stale price beside a length still escalates (fix round 1).
 */
export function isMeasureFigure(figure: string): boolean {
  return figure.startsWith(MEASURE_PREFIX);
}

const isMeasureKey = isMeasureFigure;

/** The length and weight reads in folded text, verbatim and in the order written (TA-1377). */
function measureReads(text: string): string[] {
  // Cost (fix round 1): a text with no number before a unit reads nothing.
  if (!MEASURE_GATE.test(text)) return [];
  const out: string[] = [];
  let lastEnd = -1;
  for (const m of text.matchAll(MEASURE_READ)) {
    const at = m.index ?? 0;
    const before = text.slice(Math.max(0, at - 40), at);
    if (MEASURE_MONEY_BEFORE.test(before)) continue;
    // The far end of a range whose near end a money reader may read. Unless
    // that near end is itself a length just read ("2mm-3mm", no currency).
    const near = MEASURE_MONEY_NEAR.exec(before);
    if (near !== null && lastEnd !== at - (near[1] ?? "").length) continue;
    // "2mm$": f1743de4 refuses it on the draft side as money, so that mm is
    // a money scale, not a width (a probe found it: the unit read took it).
    if (/^m/i.test(m[4] ?? "") && MEASURE_MONEY_AFTER.test(text.slice(at + m[0].length))) continue;
    lastEnd = at + m[0].length;
    out.push(m[0]);
  }
  return out;
}

/**
 * TA-1374 round 3: which text a verifier read is for. A `"fact"` read (the
 * default) is a KB fact's, and is exactly 373becde's: no fact grounds a
 * figure it did not ground there. A `"draft"` read is a reply's, or one of
 * two drafts F-05 compares: every figure the fact read gives, plus what
 * TA-1374's wider readers give, so a draft 373becde held is still held.
 */
export type FigureSide = "fact" | "draft";
export interface FigureReadOptions {
  readonly side?: FigureSide;
}

/**
 * Every figure the verifier reads: `extractFigures`, then the patterns above,
 * verbatim and in that order. Item 85(a).
 *
 * TA-566: read from `foldFigureText`, and a base read that is only the tail of
 * a wider one (`supersededBy`) is replaced by the wider read, which comes last
 * and is written in its canonical form (a form `normalizeVerifierFigure` keeps).
 *
 * TA-1374 round 3: `side: "draft"` adds the wider reads to the fact read,
 * never in place of it. "a year and a half" reads "a year" and "1.5y", so a 1
 * year fact no longer grounds it, and a 1.5 year fact does not either (as at
 * 373becde): only a fact stating both would. Over-blocking is the side B13 takes.
 */
export function extractVerifierFigures(text: string, opts: FigureReadOptions = {}): string[] {
  return extractVerifierFiguresWith(text, opts, memoNormalizer());
}

/**
 * `extras` false is the draft read exactly as 5f4596d8 made it, kept
 * reachable for `figuresDiffer` only (TA-1375 round 2). Every other caller
 * reads with the draft extras.
 */
function extractVerifierFiguresWith(
  text: string,
  opts: FigureReadOptions,
  norm: (f: string) => string,
  extras = true,
  measures = true,
): string[] {
  // TA-1374 CI: what both sides read alike is read once, so a draft read costs
  // one more `scanWide`, not a second whole read (the linear-length budget).
  const shared = sharedVerifierReads(text);
  // TA-1376 round 3: a fact's misread years inside a listed form are
  // quarantined. A draft keeps 9f293ea2's fact read, unquarantined, under its own.
  if (opts.side !== "draft") {
    const fact = readVerifierFigures(shared, false, norm, true);
    // TA-1377: the length and weight reads, after every f1743de4 fact read.
    return measures ? withMeasures(fact, shared.folded, norm) : fact;
  }
  const fact = readVerifierFigures(shared, false, norm);
  const seen = new Set(fact.map(norm));
  const out = [...fact];
  const add = (f: string): void => {
    const n = norm(f);
    if (seen.has(n)) return;
    seen.add(n);
    out.push(f);
  };
  for (const f of readVerifierFigures(shared, true, norm)) add(f);
  // TA-1375 round 2: the draft extras, after every 5f4596d8 draft read.
  if (extras) for (const f of draftExtras(shared.folded).figures) add(f);
  // TA-1377: the length and weight reads, after every f1743de4 draft read.
  return measures ? withMeasures(out, shared.folded, norm) : out;
}

/** `read` with each length and weight read not already in it added at the end, in place (TA-1377). */
function withMeasures(read: string[], folded: string, norm: (f: string) => string): string[] {
  const found = measureReads(folded);
  if (found.length === 0) return read;
  const seen = new Set(read.map(norm));
  for (const f of found) {
    const n = norm(f);
    if (seen.has(n)) continue;
    seen.add(n);
    read.push(f);
  }
  return read;
}

/**
 * `normalizeVerifierFigure`, cached for one read. It is a pure function of
 * its argument (every pattern it runs is non-global), so a repeated figure
 * ("10%, " 4,000 times) is normalized once.
 */
function memoNormalizer(): (f: string) => string {
  const cache = new Map<string, string>();
  return (f) => {
    let n = cache.get(f);
    if (n === undefined) {
      n = normalizeVerifierFigure(f);
      cache.set(f, n);
    }
    return n;
  };
}

interface SharedVerifierReads {
  readonly folded: string;
  readonly base: readonly { at: number; end: number; text: string }[];
  readonly baseSpans: ReadonlySet<string>;
  readonly verifierOnly: readonly string[];
  readonly deadlines: readonly string[];
}

/** The reads that do not depend on the side: the same on both, so read once. */
function sharedVerifierReads(text: string): SharedVerifierReads {
  const folded = foldFigureText(text);
  const base = baseReads(folded);
  // Item 153: a date with an unresolved may is read like any other, verbatim,
  // and `normalizeVerifierFigure` keeps its month unresolved, so the verifier
  // holds it unless a fact written the same way grounds it.
  const verifierOnly: string[] = [];
  for (const pattern of VERIFIER_ONLY_PATTERNS) verifierOnly.push(...(folded.match(pattern) ?? []));
  return {
    folded,
    base,
    baseSpans: new Set(base.map((b) => `${b.at}:${b.end}`)),
    verifierOnly,
    deadlines: extractDeadlines(text), // TA-603 DATES2: a dated promise with no digit is a figure too (deadlines.ts).
  };
}

function readVerifierFigures(shared: SharedVerifierReads, draft: boolean, norm: (f: string) => string, quarantine = false): string[] {
  const { folded, base, baseSpans } = shared;
  const { reads, owner } = scanWide(folded, draft);
  const out = base
    .filter((b) => {
      const w = reads[owner[b.at] ?? -1];
      return w === undefined || !supersededBy(b, w, folded);
    })
    .map((b) => b.text);
  out.push(...shared.verifierOnly);
  for (const w of reads) if (!baseSpans.has(`${w.at}:${w.end}`)) out.push(w.figure);
  out.push(...shared.deadlines);
  const preRange = out.length;
  const read = withRangeEnds(out, reads, norm);
  // TA-1376 round 3: a fact read only (`quarantined`).
  return quarantine ? quarantined(shared, read, preRange, reads, owner, norm) : read;
}

/**
 * TA-572 (decision 89). A range's upper end, once, where nothing read above
 * already states it: a fact "2-3 business days" grounds "3 business days", as
 * "3 to 5 days" has always grounded "5 days". The lower end is not added: a
 * reply that quotes it alone states the fast end as the turnaround (ULTRA's
 * caught figbind-3), and a reply's own range is checked whole by its range read.
 * Money is the exception it always was: "$65-80" reads "$65" and "$80", so
 * "65-80 dollars" reads both too (`WIDE_MONEY_WORDS_RANGE`).
 */
function withRangeEnds(out: string[], reads: readonly WideRead[], norm: (f: string) => string): string[] {
  const ends = reads.flatMap((w) => w.ends);
  if (ends.length === 0) return out;
  const seen = new Set(out.map(norm));
  for (const e of ends) {
    if (seen.has(e)) continue;
    seen.add(e);
    out.push(e);
  }
  return out;
}

/** `extractFigures` with where each read sits, for `supersededBy`. */
function baseReads(text: string): { at: number; end: number; text: string }[] {
  const out: { at: number; end: number; text: string }[] = [];
  for (const pattern of FIGURE_PATTERNS) {
    for (const m of text.matchAll(pattern)) out.push({ at: m.index ?? 0, end: (m.index ?? 0) + m[0].length, text: m[0] });
  }
  return out;
}

/**
 * What the verifier will not read whole, verbatim: a price whose scale sits on
 * one end of a range only ("$3-4k": $3 to $4,000, or $3,000 to $4,000?). The
 * verifier refuses it as `ungrounded_number`, the stance of `unreadableMoney`
 * (section 0: when unsure, do not). Money-only readers elsewhere
 * (`unreadableMoney`, `statedAmounts`) are untouched; their other callers read
 * KB facts and owner notes, which this does not change.
 *
 * TA-1374 round 3: `side: "draft"` adds the shorthand ranges ("3-3.5k").
 * The two readers that refuse at 373becde run first in either scan and
 * refuse whatever reads after them, so a draft refuses all a fact does.
 */
export function unreadableVerifierFigures(text: string, opts: FigureReadOptions = {}): string[] {
  const folded = foldFigureText(text);
  const refused = [...scanWide(folded, opts.side === "draft").refused];
  // TA-1375 round 2: a draft is also refused for an amount in a currency it
  // cannot read and a magnitude with no currency (`draftExtras`).
  if (opts.side === "draft") refused.push(...draftExtras(folded).refused);
  return refused;
}

/**
 * The money the verifier reads in a text, as it is written, in the order it is
 * read. The freshness gate asks here, so the price it ages is the price the
 * grounding check passed ("65 dollars", "$14,500.00", "$5k"), never a narrower
 * read of the same text (figbind-8).
 */
export function extractVerifierMoney(text: string, opts: FigureReadOptions = {}): string[] {
  return extractVerifierFigures(text, opts).filter((f) => normalizeVerifierFigure(f).startsWith("$"));
}

/** A computed figure no number holds: "Infinity", "NaN", or an exponent ("1e+23"). */
const NON_FINITE_FIGURE = /infinity|(?<![a-z])nan(?![a-z])|\de[+-]\d/i;
const FIGURE_DIGIT_RUN = /\d[\d,]*/g;

/**
 * TA-1378 (FINDINGS TA-1377 left open 8, B13). A figure read as an amount no
 * safe integer of cents holds: a computed read that is not finite or is
 * written as an exponent ("$Infinity", "$1e+23", "$9.999999999999999e+25"),
 * or a dollar figure with a digit run past `Number.MAX_SAFE_INTEGER` cents.
 * Two different such amounts read as one figure ("$999...9k" and
 * "$999...8k" both "$1e+23"), so neither side's read can ground the other:
 * the verifier holds the draft. A figure of ordinary size is never one.
 */
export function isInexactAmountFigure(figure: string): boolean {
  const n = normalizeVerifierFigure(figure);
  if (NON_FINITE_FIGURE.test(n)) return true;
  if (!n.startsWith("$")) return false;
  for (const run of n.matchAll(FIGURE_DIGIT_RUN)) {
    if (!Number.isSafeInteger(Number(run[0].replace(/,/g, "")) * 100)) return true;
  }
  return false;
}

/**
 * `normalizeFigure`, plus one canonical form per new kind, so the same claim
 * written two ways matches ("3 p.m." and "3:00pm", "18kt" and "eighteen
 * karat", "Oct. 5" and "5 October", "15:00" and "3pm") and a different claim
 * never does. On any figure `extractFigures` reads it returns exactly what
 * `normalizeFigure` does, but for whole dollars: "$65.00" is "$65" here, the
 * rule the memory check's `canonicalFigure` already applies (TA-468). Every
 * other rule below is anchored on a form those figures never take.
 */
export function normalizeVerifierFigure(figure: string): string {
  // TA-1377: a length or weight read takes its own key. No read made before
  // this round takes the shape `MEASURE_EXACT` anchors on.
  const measure = measureKey(figure);
  if (measure !== null) return measure;
  const pre = figure
    .toLowerCase()
    .replace(
      new RegExp(`\\b(${FRACTION_WORD})[\\s-]+(off|price|down)\\b`),
      (_, f: string, kind: string) => `${FRACTION_VALUE[f.replace(/\s+/g, "")] ?? f}${kind === "down" ? "down" : "off"}`,
    )
    .replace(/\ba\s+couple\s+of\s+years\b/, "2 years")
    .replace(/\ban?\s+year\b/, "1 year")
    // The half of a size only: a shared figure like "two and a half weeks"
    // keeps normalizeFigure's own reading.
    .replace(/^(?:sizes?|sz\.?)\s*[:#]?(.*)$/, (_, rest: string) =>
      `size ${rest.replace(/\s+(?:and\s+)?a\s+half\b/g, ".5").replace(/(\d)\s+1\/2\b/g, "$1.5")}`)
    .replace(/a\.m\./g, "am")
    .replace(/p\.m\./g, "pm")
    .replace(/o(?:'|\u2019)?clock/, "oclock");
  let s = normalizeFigure(
    pre
      .replace(/^(\d{1,2}(?::\d{2})?)([ap])$/, "$1$2m")
      .replace(/^(?:noon|midday)$/, "12pm")
      .replace(/^midnight$/, "12am")
      .replace(new RegExp(`\\s+${DAY_PART}$`), (part) => (part.includes("morning") ? " am" : " pm"))
      .replace(new RegExp(ORDINAL_WORD, "g"), ordinalWordAsDigits)
      .replace(/^usd\s?([\d,.]+)$|^([\d,.]+)\s?(?:usd|\$)$/, (_, a?: string, b?: string) => `$${a ?? b}`),
  ).replace(/:00(?=am|pm)/g, "");
  if (s.endsWith(".00") && parseMoneyCents(s) !== null) s = s.slice(0, -3);

  const h24 = /^(1[3-9]|2[0-3]):(\d{2})$/.exec(s);
  if (h24) return `${Number(h24[1]) - 12}${h24[2] === "00" ? "" : `:${h24[2]}`}pm`;
  const karat = /^(\d+)-?(?:k|kt|karats?)$/.exec(s);
  if (karat) return `${karat[1]}k`;
  const years = /^(\d+(?:\.\d+)?)(?:(?:to|-)(\d+(?:\.\d+)?))?-?(?:years?|yrs?)$/.exec(s);
  if (years) return years[2] === undefined ? `${years[1]}y` : `${years[1]}-${years[2]}y`;
  const timeRange = /^(\d{1,2}(?::\d{2})?)(am|pm)?(?:to|-)(\d{1,2}(?::\d{2})?)(am|pm)$/.exec(s);
  if (timeRange) return `${timeRange[1]}${timeRange[2] ?? ""}-${timeRange[3]}${timeRange[4]}`;
  const size = /^size(\d+(?:\.\d+)?)(?:(?:to|-)(\d+(?:\.\d+)?))?$/.exec(s);
  if (size) return size[2] === undefined ? `size${size[1]}` : `size${size[1]}-${size[2]}`;
  // Item 153: a may not written "May" is "may?5", a form no month-day date
  // ("may5") grounds and `CANONICAL_MONTH_DAY` never takes. Only a fact written
  // the same way grounds it, so the day is checked and the month never assumed.
  const month = (word: string): string => {
    const abbr = word.slice(0, 3);
    return abbr === "may" && mayUnresolved(figure) ? "may?" : abbr;
  };
  // TA-566: one spelling per turnaround, carat weight and percent, whichever
  // way the text wrote it ("30 min" and "30 minutes", "1.50 ct" and "1.5
  // carats", "4 to 6 weeks" and "4-6 weeks").
  const duration = NORMALIZED_DURATION.exec(s);
  if (duration) {
    const unit = duration[4] ?? "";
    return durationFigure(duration[1] ?? "", duration[2] ?? null, `${duration[3] === undefined ? "" : "business-"}${unit}`);
  }
  const carat = /^(\d+(?:\.\d+)?)(?:ct|cts|carats?)$/.exec(s);
  if (carat) return `${textNum(carat[1] ?? "")}ct`;
  const percent = /^(\d+(?:\.\d+)?)%$/.exec(s);
  if (percent) return `${textNum(percent[1] ?? "")}%`;
  const monthDay = /^([a-z]+?)\.?(?:the)?(\d{1,2})(?:st|nd|rd|th)?$/.exec(s);
  if (monthDay && MONTH_ONLY.test(monthDay[1] ?? "")) return `${month(monthDay[1] ?? "")}${monthDay[2]}`;
  const dayMonth = /^(?:the)?(\d{1,2})(?:st|nd|rd|th)?(?:of)?([a-z]+)$/.exec(s);
  if (dayMonth && MONTH_ONLY.test(dayMonth[2] ?? "")) return `${month(dayMonth[2] ?? "")}${dayMonth[1]}`;
  return s;
}

/**
 * A calendar date the text states, month and day both named: "October 12",
 * "5 Oct", "10/5", "2026-10-12", verbatim and in the order written (L11, the
 * owner's call brief). The brief points the owner at a dated fact, and F-16
 * keeps the date patterns in this module, so it asks here instead of keeping a
 * pattern of its own.
 *
 * The subset of item 85(a)'s date patterns that name a month: the month-first
 * and day-first spellings, the slash form WITH A YEAR, and the ISO form. NOT
 * "the 5th", which names no month, NOT "10-6", which may be opening hours, and
 * NOT a bare "10/5", which is "1/2 carat" in the same shape (the verifier
 * grounds those against a fact, where being wrong costs a refused draft; a
 * brief that points a person at a date should not guess, section 0).
 */
const DATE_PATTERNS: readonly RegExp[] = [MONTH_DAY, DAY_MONTH, SLASH_DATE_YEAR, ISO_DATE];

export function extractDates(text: string): string[] {
  const found: { at: number; text: string }[] = [];
  for (const pattern of DATE_PATTERNS) {
    if (pattern === MONTH_DAY || pattern === DAY_MONTH) {
      // Item 153: a may not written "May" is never the month here.
      found.push(...monthNamedDates(text, pattern).filter((d) => !d.unresolved));
      continue;
    }
    for (const m of text.matchAll(pattern)) found.push({ at: m.index ?? 0, text: m[0] });
  }
  return found.sort((a, b) => a.at - b.at).map((f) => f.text);
}

/**
 * Item 153. The dates `extractDates` leaves out because their month is a
 * "may" not written "May" ("may 5", "the 5th of may", "MAY 5"), verbatim and
 * in the order written. The verb's own shape ("3 may differ") is one of them:
 * a pattern cannot tell it from a date, so it is never skipped. For a caller
 * whose safety rests on SEEING a date, not on reading one: a held row that may
 * state a date, or a rule that saves only a sentence with exactly one.
 */
export function extractUncertainDates(text: string): string[] {
  return [MONTH_DAY, DAY_MONTH]
    .flatMap((pattern) => monthNamedDates(text, pattern))
    .filter((d) => d.unresolved)
    .sort((a, b) => a.at - b.at)
    .map((d) => d.text);
}

/**
 * The verifier's normalized figure set for a text. Item 85(a). `opts` reaches
 * the period mentions only: `dropNotWorking` is for the fact-grounding path
 * (decision 183, the verifier reading cited KB facts); `figuresDiffer` and
 * every other reader keep the default, every mention. `side` reaches the
 * figure read only (TA-1374 round 3, `FigureReadOptions`).
 */
export function verifierFigureSet(text: string, opts: PeriodMentionOptions & FigureReadOptions = {}): Set<string> {
  // TA-603 DATES2 fix rounds 1-2: a fact grounds a day only through a promise shape (extractDeadlines, added by
  // extractVerifierFigures); a fact that only names the day ("we are closed Friday") grounds nothing. A period
  // phrase it names (this week, this weekend, this month) grounds by mention.
  const norm = memoNormalizer();
  const figures = extractVerifierFiguresWith(text, { side: opts.side ?? "fact" }, norm);
  return new Set([...figures.map(norm), ...extractPeriodMentions(text, opts)]);
}

/**
 * M1 (the memory save-gate). A magnitude written after a money token. "$5k"
 * is a `MONEY_TOKEN` of "$5" followed by "k", so `parseMoneyCents` reads it as
 * five dollars; a stored budget of $5.00 is worse than none.
 */
const MONEY_MAGNITUDE = /^(?:[a-z]|\s*(?:k|thousand|grand|million|mil|billion)\b)/i;

/**
 * M1 review. The other end of a range whose second end carries no sign
 * ("$3-5k", "$3,000 to 5,000", "between $2,000 and 3,000"): one `MONEY_TOKEN`,
 * so the sole-token rule alone read the first end as the whole amount. The
 * hyphen, an en dash (U+2013) and an em dash (U+2014) all join a range.
 */
const RANGE_JOIN = "(?:-|\u2013|\u2014|to|and|or|through|thru)";
const MONEY_RANGE_AFTER = new RegExp(`^\\s*${RANGE_JOIN}\\s*\\$?\\s?\\d`, "i");
/** The same range written the other way round: "3,000 to $5,000". */
const MONEY_RANGE_BEFORE = new RegExp(`\\d\\s*k?\\s*${RANGE_JOIN}\\s*$`, "i");
/** An amount per period ("$200/month", "$150 per month", "$250 monthly") is not a total. */
const MONEY_PERIOD =
  /^\s*(?:(?:\/|an?\b|per\b|each\b|every\b)\s*(?:mo|mos|months?|weeks?|wk|years?|yr|annum)\b|(?:monthly|weekly|yearly|annually)\b)/i;

/**
 * M1. The one money amount a line states, as integer cents, or null.
 *
 * Stricter than `parseMoneyCents`, which reads the FIRST token, because a
 * memory row stores the figure as a fact: null when the line holds no money
 * token, more than one (a range is two), a token `MONEY_EXACT` refuses, a
 * token with a magnitude after it ("$5k", "$5 K", "$5 thousand"), a token
 * that is one end of a range or an alternative whose other end has no sign
 * ("$3-5k", "3,000 to $5,000", "$3,000 or 4,000"), or an amount per period
 * ("$200/month"). Dollar-less amounts ("5000 dollars", "USD 4000") are not
 * read either: `MONEY_TOKEN` needs the sign, and this adds no second money
 * pattern (F-16).
 */
export function parseSoleMoneyCents(text: string): number | null {
  const read = readSoleMoneyCents(text);
  return read === INEXACT_AMOUNT ? null : read;
}

/** `parseSoleMoneyCents`, telling an amount past a safe integer of cents (`INEXACT_AMOUNT`) from none (TA-1378). */
export function readSoleMoneyCents(text: string): MoneyRead {
  const tokens = [...text.matchAll(new RegExp(MONEY_TOKEN.source, "g"))];
  const only = tokens.length === 1 ? tokens[0] : undefined;
  if (only === undefined) return null;
  const start = only.index ?? 0;
  const before = text.slice(0, start);
  const after = text.slice(start + only[0].length);
  if (MONEY_MAGNITUDE.test(after) || MONEY_RANGE_AFTER.test(after) || MONEY_PERIOD.test(after)) return null;
  if (MONEY_RANGE_BEFORE.test(before)) return null;
  return readMoneyCents(only[0]);
}

/**
 * M1. A size as a customer states it: `SIZE`, plus the one form `SIZE` does
 * not read because no reply writes it, "size is 7" ("size of 7", "size is a
 * 7"). Kept apart from `VERIFIER_ONLY_PATTERNS` so the verifier reads exactly
 * what it read before.
 */
const STATED_SIZE = new RegExp(
  `\\b${SIZE_WORD}\\s*(?:(?:is|of|=)\\s+)?(?:an?\\s+)?${SIZE_NUM}(?:\\s*(?:to|-)\\s*${SIZE_NUM})?`,
  "gi",
);
/** "6 or 7", "6/7", "6, 7": a second size offered as an alternative. */
const SIZE_ALTERNATIVE = new RegExp(`^\\s*(?:or|and|/|,)\\s*(?:an?\\s+)?${NUM}\\b`, "i");
const STATED_SIZE_LINK = /\s*(?:(?:is|of|=)\s+)?(?:an?\s+)?(?=\S)/i;
const SINGLE_SIZE = /^size\d+(?:\.\d+)?$/;
/**
 * M1 review. What can follow a size that `SIZE_NUM` does not read, so the
 * whole number alone would be stored: a fraction other than the half ("6
 * 1/4"), a vulgar fraction ("6" then U+00BC to U+00BE, or U+2150 to U+215E),
 * a quarter or a third in words, or a unit that makes it no US size ("17mm",
 * "52 EU"). `SIZE_NUM` is not widened for it, because the verifier shares it.
 */
const SIZE_UNREAD_TAIL =
  /^\s*(?:\d+\/\d+|[\u00bc-\u00be\u2150-\u215e]|(?:and\s+)?(?:(?:a|one)\s+)?(?:quarter|third|three\s+quarters)\b|(?:mm|millimet(?:er|re)s?|eu|uk|european|british)\b)/i;
/** "her UK ring size is 6", "her European size is 52": a size on another scale. */
const SIZE_FOREIGN_SCALE = /\b(?:uk|eu|european|british)\s+(?:ring\s+)?$/i;

/**
 * M1. The one size a line states, in `normalizeVerifierFigure`'s form
 * ("size7", "size6.5"), or null when it states none, a range, an alternative
 * ("6 or 7"), two different sizes, or a size this reader cannot read whole (a
 * quarter, a vulgar fraction, millimetres, a UK or EU scale).
 */
export function parseSoleSize(text: string): string | null {
  const found = new Set<string>();
  for (const m of text.matchAll(STATED_SIZE)) {
    const start = m.index ?? 0;
    const tail = text.slice(start + m[0].length);
    if (SIZE_ALTERNATIVE.test(tail) || SIZE_UNREAD_TAIL.test(tail)) return null;
    if (SIZE_FOREIGN_SCALE.test(text.slice(0, start))) return null;
    const word = new RegExp(`^${SIZE_WORD}`, "i").exec(m[0])?.[0] ?? "";
    const rest = m[0].slice(word.length).replace(STATED_SIZE_LINK, "");
    found.add(normalizeVerifierFigure(`size ${rest}`));
  }
  const only = found.size === 1 ? [...found][0] : undefined;
  return only !== undefined && SINGLE_SIZE.test(only) ? only : null;
}

/** `normalizeVerifierFigure`'s month-day form: "may14", "jun3". Never an unresolved "may?14" (item 153). */
const CANONICAL_MONTH_DAY = /^(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)(?:3[01]|[12]\d|[1-9])$/;

/**
 * M1. The month-name dates a text states, distinct, in the verifier's
 * canonical form. A slash or dash date is not one of them: "6/12" is June or
 * December depending on who wrote it. A month alone ("in May") is not a date
 * the patterns here read, so it is not returned either.
 */
export function extractMonthDays(text: string): string[] {
  return [...verifierFigureSet(text)].filter((f) => CANONICAL_MONTH_DAY.test(f));
}

export function extractFigures(text: string): string[] {
  const out: string[] = [];
  for (const pattern of FIGURE_PATTERNS) out.push(...(text.match(pattern) ?? []));
  return out;
}

/**
 * Canonical form for comparison. Whitespace and case are noise; the digits and
 * the separators are the claim. Commas are KEPT: dropping them would make
 * "$14,000" normalize to "$14000", which is correct, but stripping them to
 * "$14" is bug 3 and the reason this function is tested against it directly.
 */
export function normalizeFigure(figure: string): string {
  const digits = figure
    .toLowerCase()
    .replace(SPELLED_NUMBER, (phrase) => String(spelledValue(phrase)))
    .replace(/\ba\s+couple\s+of\s+(?=(?:days|weeks|months)\b)/g, "2 ")
    .replace(/\ban?\s+(?=(?:week|month)\b)/g, "1 ")
    // Anchored on the left: unanchored, this restarts at every digit of a long
    // number and goes quadratic (verifier.test.ts, the 20,000-digit percent).
    .replace(/(?<![\d.])(\d+(?:\.\d+)?)\s*(?:dollars?|bucks)\b/g, (_, n: string) => `$${groupThousands(n)}`)
    .replace(/\s*(?:per\s?cent|pct)\b/g, "%");
  return digits.replace(/\s+/g, "");
}

/** "1900" to "1,900", the shop's own "$" form, so spelled dollars ground against it. */
function groupThousands(n: string): string {
  const [whole, frac] = n.split(".");
  const grouped = (whole ?? "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return frac === undefined ? grouped : `${grouped}.${frac}`;
}

export function figureSet(text: string): Set<string> {
  return new Set(extractFigures(text).map(normalizeFigure));
}

/**
 * Do two texts make different numeric claims?
 *
 * Symmetric on purpose. Removing a price is exactly as substantive as changing
 * one, and an asymmetric check would wave through the edit that deletes a
 * figure the customer was about to rely on.
 *
 * TA-569 addendum (GRAD2, decision 63, reversing item 85's narrow reader): it
 * reads with the verifier's WIDE reader (`verifierFigureSet`), so a changed
 * business-day count, "3 wks", a carat word, a currency code or a time is a
 * change, and "$1,500" against "fifteen hundred dollars" or "4-6 weeks"
 * against "4 to 6 weeks" is not (`normalizeVerifierFigure` folds them). A bare
 * number neither reader grammar covers ("75 to size it") is compared too, so a
 * corrected one is never waved through. `figureSet` stays narrow: the KB build
 * and the other readers of it keep their reading.
 *
 * TA-1374 round 3: `side: "draft"` is for two drafts (F-05's draft and the
 * approved text). They differ if they differ on the fact read, as at
 * 373becde, or on the draft read, so "a year" edited to "a year and a half"
 * is a figure edit, and no edit 373becde called one is ever cosmetic. The
 * default, the fact read, is for every other caller, as at 373becde.
 */
export function figuresDiffer(a: string, b: string, opts: FigureReadOptions = {}): boolean {
  // TA-1376 round 3: the fact read carries quarantine keys. Each names the
  // read 9f293ea2 made in its place, so two texts 9f293ea2 read apart are
  // still read apart, and a Part B form edited to its misread is a difference.
  if (differOn(a, b, "fact")) return true;
  if (opts.side !== "draft") return false;
  // TA-1375 round 2: the draft read gained reads (`draftExtras`), and a new
  // read can make two drafts read alike that 5f4596d8 read apart (it carries a
  // number that was bare). So the draft side also compares 5f4596d8's draft
  // read: no edit 5f4596d8 called a figure edit is ever cosmetic. Fix round:
  // both draft reads come from one read of each text (`draftKeys`). TA-1376
  // adds 9f293ea2's draft read the same way (`prior`).
  const ka = draftKeys(a);
  const kb = draftKeys(b);
  // TA-1377: f1743de4's draft read (`head1376`) too, so no edit f1743de4 called a figure edit is ever cosmetic,
  // and the draft keys with the length and weight reads (`measured`), so a unit edit is a figure edit.
  return (
    keysDiffer(ka.base, kb.base) ||
    keysDiffer(ka.prior, kb.prior) ||
    keysDiffer(ka.head1376, kb.head1376) ||
    keysDiffer(ka.head, kb.head) ||
    keysDiffer(ka.measured, kb.measured)
  );
}

/**
 * TA-1377: the keys without the length and weight reads first, which are
 * f1743de4's, so no two texts it read apart read alike; then with them, so a
 * unit edit ("18 inches" to "18 mm") is a figure edit too.
 */
function differOn(a: string, b: string, side: FigureSide): boolean {
  const fa = verifierFigureSet(a, { side });
  const fb = verifierFigureSet(b, { side });
  return keysDiffer(figureKeys(a, withoutMeasures(fa)), figureKeys(b, withoutMeasures(fb))) || keysDiffer(figureKeys(a, fa), figureKeys(b, fb));
}

function withoutMeasures(figures: ReadonlySet<string>): Set<string> {
  return new Set([...figures].filter((f) => !isMeasureKey(f)));
}

function keysDiffer(fa: ReadonlySet<string>, fb: ReadonlySet<string>): boolean {
  if (fa.size !== fb.size) return true;
  for (const f of fa) if (!fb.has(f)) return true;
  return false;
}

/** A text's figure set plus the bare numbers it leaves uncovered: what `figuresDiffer` compares. */
function figureKeys(text: string, figures: ReadonlySet<string>): Set<string> {
  return new Set([...figures, ...bareNumbers(text, figures)]);
}

/**
 * TA-1375 round 2 fix round: a draft's 5f4596d8 keys and its keys with the
 * draft extras, from one draft read. `head` is `base` plus the normalized
 * extras, exactly what `verifierFigureSet(text, { side: "draft" })` returns,
 * so F-05's draft side costs one draft read per text, not four.
 */
function draftKeys(text: string): {
  base: Set<string>;
  prior: Set<string>;
  head1376: Set<string>;
  head: Set<string>;
  measured: Set<string>;
} {
  const norm = memoNormalizer();
  const opts: PeriodMentionOptions & FigureReadOptions = { side: "draft" };
  const read = extractVerifierFiguresWith(text, opts, norm, false, false);
  const base = new Set([...read.map(norm), ...extractPeriodMentions(text, opts)]);
  const folded = foldFigureText(text);
  const extras = draftExtras(folded);
  const prior = new Set(base);
  for (const f of extras.figures.slice(0, extras.before1376)) prior.add(norm(f));
  const head1376 = new Set(prior);
  for (const f of extras.figures.slice(extras.before1376, extras.before1377)) head1376.add(norm(f));
  const head = new Set(head1376);
  for (const f of extras.figures.slice(extras.before1377)) head.add(norm(f));
  // TA-1377: the draft keys plus the length and weight reads.
  const measured = new Set(head);
  for (const f of measureReads(folded)) measured.add(norm(f));
  return {
    base: figureKeys(text, base),
    prior: figureKeys(text, prior),
    head1376: figureKeys(text, head1376),
    head: figureKeys(text, head),
    measured: figureKeys(text, measured),
  };
}

/**
 * ASCII numbers in the text that no wide-reader figure already carries, as
 * "#75" keys. A number inside a read figure ("$1,500", "4 to 6 weeks", "3pm")
 * is that figure's business, so only a number the reader grammar left in prose
 * is returned. Commas fold ("1,500" and "1500" are one number).
 */
function bareNumbers(text: string, figures: ReadonlySet<string>): Set<string> {
  const covered = new Set<string>();
  // TA-1376 round 3: a quarantine key covers what the read it replaced
  // covered, never the numbers of its form, so the bare numbers are 9f293ea2's.
  for (const f of figures) {
    for (const n of quarantinedRead(f).matchAll(/\d[\d,]*(?:\.\d+)?/g)) covered.add(foldNumber(n[0]));
  }
  const bare = new Set<string>();
  // Anchored on the left, like the dollar pattern above: unanchored, this
  // restarts at every digit of a long number and goes quadratic.
  for (const n of text.matchAll(/(?<![\d.,])\d[\d,]*(?:\.\d+)?/g)) {
    const folded = foldNumber(n[0]);
    if (!covered.has(folded)) bare.add(`#${folded}`);
  }
  return bare;
}

function foldNumber(n: string): string {
  // "65.00" and "65" are one number, as `normalizeVerifierFigure` reads "$65.00".
  // Text, not Number(): a long digit run must not collapse to one float.
  const [whole = "", frac = ""] = n.replace(/,/g, "").split(".");
  const w = whole.replace(/^0+(?=\d)/, "");
  const f = frac.replace(/0+$/, "");
  return f === "" ? w : `${w}.${f}`;
}

/**
 * TA-1355 D11. Every number written in digits, in the order written, folded
 * as `figuresDiffer` folds them ("$14,000" and "14000" are "14000", "$4.00"
 * is "4"). For comparing which numbers two texts mention, never for printing
 * or pricing: it says nothing about what a number measures.
 */
export function mentionedNumbers(text: string): string[] {
  // The same left-anchored pattern as `bareNumbers` above: unanchored it goes quadratic.
  return [...text.matchAll(/(?<![\d.,])\d[\d,]*(?:\.\d+)?/g)].map((m) => foldNumber(m[0]));
}

/**
 * Integer cents to a display string. Deterministic code, never a model, and
 * it lives here rather than in a UI helper because F-16's ruling is ONE money
 * module: three independent comma bugs passed green tests before it existed.
 */
export function formatCents(cents: number): string {
  if (!Number.isInteger(cents)) {
    throw new Error(`formatCents expects integer cents, got ${cents}`);
  }
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100);
  const rest = String(abs % 100).padStart(2, "0");
  const grouped = dollars.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}$${grouped}.${rest}`;
}

/**
 * Integer cents that are whole dollars to the form a shop types in a message: "$12,500", no decimals
 * (TA-1317 fix 1). A figure with cents is refused, never rounded or cut: a reply that quotes a price
 * quotes it exactly (section 4), so a caller holding cents uses `formatCents`.
 */
export function formatWholeDollars(cents: number): string {
  if (!Number.isInteger(cents)) {
    throw new Error(`formatWholeDollars expects integer cents, got ${cents}`);
  }
  if (cents % 100 !== 0) {
    throw new Error(`formatWholeDollars expects whole dollars, got ${cents} cents`);
  }
  const sign = cents < 0 ? "-" : "";
  const grouped = (Math.abs(cents) / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}$${grouped}`;
}

/**
 * Integer micro-USD to a display string with four decimals.
 *
 * `formatCents` cannot carry a telemetry figure: one model call costs a
 * fraction of a cent, and two decimals prints almost the whole cost report as
 * 0.00. So this is the same money module at one finer resolution, and it is
 * here rather than in the CLI that wanted it because F-16 is a choke point.
 *
 * TRUNCATES toward zero, deliberately. `(microUsd / 1_000_000).toFixed(4)`,
 * which is what two callers were doing independently, ROUNDS: it prints
 * 999,999 micro-USD as "1.0000". The cost report labels its total a floor
 * (unpriced calls exist and are counted separately), and a floor that rounds
 * up is a figure nobody measured. Section 4 both ways: math is deterministic,
 * and a customer-facing or operator-facing number never exceeds its source.
 */
export function formatMicroUsd(microUsd: number): string {
  if (!Number.isInteger(microUsd)) {
    throw new Error(`formatMicroUsd expects integer micro-USD, got ${microUsd}`);
  }
  const sign = microUsd < 0 ? "-" : "";
  const abs = Math.abs(microUsd);
  const dollars = Math.floor(abs / 1_000_000);
  // Four decimals of a dollar is one ten-thousandth, which is 100 micro-USD.
  const rest = String(Math.floor((abs % 1_000_000) / 100)).padStart(4, "0");
  const grouped = dollars.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}${grouped}.${rest}`;
}

/*
 * TA-1377 placement: this block sits at the end of the file so that no line
 * above it moves, and the line cites into this file (SPEC_COVERAGE.md cites
 * `figuresDiffer` by line) stay where main put them. `quarantined` calls
 * `unitHalfLookup` only when a text is read, never while the module loads,
 * so every constant below is set before it is used.
 */
/*
 * TA-1377: the same forms before a month, week or day unit (FINDINGS TA-1376,
 * Left open 3). At f1743de4 a fact "1/2 a week" read 1 week, "1 month and a
 * half" 1 month, "1-1/2 weeks" 1-0.5 weeks and 0.5 weeks, "half of 1 day" 1
 * day, so each grounded a draft stating the wrong figure. The pattern is
 * `QUARANTINE_FORM`'s, alternative for alternative, with the same left
 * context, separator classes and clause end, and only the unit changed. Many
 * of these forms f1743de4 already reads at their true value ("1 1/2 weeks" is
 * 1.5 weeks, "half a month" 0.5 months), so a read is quarantined only when
 * it is a duration of the form's unit whose value is not the form's
 * (`unitHalfMisread`). In a range form only the range read can be, as for
 * years: its high end is what the fact states.
 *
 * Each alternative is a named group so a form a test at f1743de4 pins can be
 * left as f1743de4 reads it (`UNIT_HALF_PINNED`). Its span is still taken, so
 * no other form matches inside it.
 *
 * The unit is any spelling the reader reads as months, weeks or days, as the
 * year forms take "yr" and "yrs": "mo", "mos", "wk", "wks", and days with a
 * "business", "working" or "work" before them, which the reader reads as
 * business days ("1-1/2 business days" read 1-0.5 and 0.5 business days).
 */
const UNIT_HALF_WORD = `(?:months?|mos?|weeks?|wks?|(?:(?:business|working|work)(?: ?[${DASH}] ?| ))?days?)`;
const UNIT_HALF_UNIT = `(?: ?[${DASH}] ?| ?)${UNIT_HALF_WORD}(?!\\w)`;
const UNIT_HALF_FORM = new RegExp(
  `(?<wh>(?:${QUARANTINE_LEFT_NUMBER}(?<whd>\\d+)|${QUARANTINE_LEFT_WORD}(?<whw>${UNIT_WORD}|${TEEN_WORD}|${TENS_WORD}))` +
    `(?:${QUARANTINE_WHOLE_JOIN}${QUARANTINE_HALF_NUMBER}| ?${VULGAR_HALF})` +
    `(?:${UNIT_HALF_UNIT}|(?<range>${QUARANTINE_RANGE_JOIN}(?<high>\\d+(?:\\.\\d+)?)${UNIT_HALF_UNIT})))` +
    `|(?<ha>${QUARANTINE_LEFT_WORD}(?:half${QUARANTINE_JOIN}a|a${QUARANTINE_JOIN}half|half)${UNIT_HALF_UNIT})` +
    `|(?<ho>${QUARANTINE_LEFT_WORD}half${QUARANTINE_JOIN}(?:of${QUARANTINE_JOIN}(?:an?|one|1)|an)${UNIT_HALF_UNIT})` +
    `|(?<na>${QUARANTINE_LEFT_NUMBER}${QUARANTINE_HALF_NUMBER}${QUARANTINE_JOIN}(?:of${QUARANTINE_JOIN})?(?:an?|one|1)${UNIT_HALF_UNIT})` +
    `|(?<va>${VULGAR_HALF}${QUARANTINE_JOIN}(?:of${QUARANTINE_JOIN})?(?:an?|one|1)${UNIT_HALF_UNIT})` +
    `|(?<uh>(?:${QUARANTINE_LEFT_NUMBER}1|${QUARANTINE_LEFT_WORD}(?:one|an))${QUARANTINE_JOIN}${UNIT_HALF_WORD}` +
    `(?:,? and |,? ?& ?)(?:(?:a|one)${QUARANTINE_JOIN})?half(?!(?:${QUARANTINE_JOIN})?[A-Za-z0-9]))`,
  "gi",
);
/*
 * Forms a test at f1743de4 pins as f1743de4 reads them, by alternative and
 * unit: the whole-and-half form for months ("1 and 1/2 months", "1-1/2
 * months", figures.partB1376.test.ts), "1/2 a month" (the same file), a
 * vulgar half then "a week" (the same file) and "1 week and a half"
 * (figures.drafts1375.test.ts). Each is left as f1743de4 reads it. Whether
 * those pins may change is Kabir's call, as TA-1376 asked of "a year and a
 * half" (Kabir decides 4).
 */
const UNIT_HALF_PINNED: ReadonlySet<string> = new Set(["wh:month", "na:month", "va:week", "uh:week"]);
const UNIT_HALF_KINDS = ["wh", "ha", "ho", "na", "va", "uh"] as const;
/** A month, week, day or business day duration read as `normalizeVerifierFigure` writes it ("1week", "1-0.5businessdays"). */
const UNIT_HALF_READ = /^\d+(?:\.\d+)?(-\d+(?:\.\d+)?)?((?:business)?(?:month|week|day))s?$/;
/** The unit a form names, as a read spells it. Only the unit is a word of these letters in a form. */
const UNIT_HALF_MONTH = /(?<![a-z])mo(?:nth)?s?(?![a-z])/;
const UNIT_HALF_WEEK = /(?<![a-z])w(?:ee)?ks?(?![a-z])/;
const UNIT_HALF_BUSINESS = /(?<![a-z])(?:business|working|work)(?![a-z])/;

interface UnitHalfForm {
  readonly at: number;
  readonly end: number;
  readonly text: string;
  /** "month", "week", "day" or "businessday", as a read spells it. */
  readonly unit: string;
  /** The form's true value as a read ("1.5weeks"), and for a range form its true range ("1.5-3weeks"). */
  readonly trueRead: string;
  readonly range: boolean;
  readonly pinned: boolean;
}

function unitHalfForms(folded: string, norm: (f: string) => string): UnitHalfForm[] {
  const out: UnitHalfForm[] = [];
  for (const m of folded.matchAll(UNIT_HALF_FORM)) {
    const g = m.groups ?? {};
    const kind = UNIT_HALF_KINDS.find((k) => g[k] !== undefined) ?? "wh";
    const lower = m[0].toLowerCase();
    const base = UNIT_HALF_MONTH.test(lower) ? "month" : UNIT_HALF_WEEK.test(lower) ? "week" : "day";
    const unit = UNIT_HALF_BUSINESS.test(lower) ? `business${base}` : base;
    const whole = g.whd !== undefined ? Number(g.whd) : g.whw !== undefined ? spelledValue(g.whw.toLowerCase()) : kind === "uh" ? 1 : 0;
    const value = whole + 0.5;
    const range = g.range !== undefined;
    out.push({
      at: m.index ?? 0,
      end: (m.index ?? 0) + m[0].length,
      text: m[0],
      unit,
      trueRead: norm(`${range ? `${value}-${g.high ?? ""}` : value} ${unit === base ? "" : "business "}${base}s`),
      range,
      pinned: UNIT_HALF_PINNED.has(`${kind}:${base}`),
    });
  }
  return out;
}

/** Is `key`, a read inside `form`, a duration of its unit that is not its true value? */
function unitHalfMisread(key: string, form: UnitHalfForm): boolean {
  if (form.pinned) return false;
  const read = UNIT_HALF_READ.exec(key);
  if (read === null || read[2] !== form.unit) return false;
  if (form.range && read[1] === undefined) return false;
  return key !== form.trueRead;
}

/**
 * The month, week and day half forms in `folded`, as a lookup from a fact
 * read and its span to the read or its quarantine key, or null when the
 * text has no unpinned form. A read is keyed only when its whole span lies
 * inside one form and it is a misread of that form (`unitHalfMisread`).
 */
function unitHalfLookup(folded: string, norm: (f: string) => string): ((figure: string, span: readonly [number, number]) => string) | null {
  const unitForms = unitHalfForms(folded, norm).filter((f) => !f.pinned);
  if (unitForms.length === 0) return null;
  // Each folded character's form, so each test is O(1) and a long text stays linear (forms never overlap).
  const unitFormAt = new Int32Array(folded.length + 1).fill(-1);
  unitForms.forEach((f, i) => unitFormAt.fill(i, f.at, f.end));
  return (figure, span) => {
    const unitFirst = unitFormAt[span[0]] ?? -1;
    const unitForm = unitForms[unitFirst];
    if (unitForm === undefined || unitFormAt[span[1] - 1] !== unitFirst) return figure;
    const key = norm(figure);
    return unitHalfMisread(key, unitForm) ? quarantineKey(key, unitForm.text) : figure;
  };
}

/**
 * TA-1378 (TA-1377 Left open 4; CLAUDE.md section 0.5, the 0% cap): "per" and
 * "cent" parted by any run of spaces, tabs and dashes. A line break is never
 * folded, so a sentence end stays where it was. One quantifier, so a long run
 * is scanned once from each "per".
 */
const PER_CENT_RUN = new RegExp(`\\bper[ \\t\\u00a0${DASH}]+cent\\b`, "gi");

/**
 * TA-1378: the shop's discount readers (`readShopDiscountClaims`) read
 * "per-cent" as "per cent", so "I'll take 20-per-cent off" counts toward the
 * cap as "20 per cent off" does. Shop side only: the customer reader keeps
 * base e5faa94's reading (customerReadBase.test.ts), and `PERCENT_MARK`, which
 * the figure readers share and where a wider fact read would ground more, is
 * left alone. Nothing in the discount readers reads the run itself. Defined
 * here, at the end, so no line cite above it moves.
 */
function foldPerCent(text: string): string {
  return text.replace(PER_CENT_RUN, "per cent");
}
